import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "sonner";
import {
  Calendar as CalendarIcon,
  Clock,
  User,
  MapPin,
  Check,
  X,
  Filter,
  Plus,
  ChevronLeft,
  ChevronRight,
  Flame,
  Users,
  MessageCircle,
  ListOrdered,
  AlertCircle,
  CheckCircle,
  CheckCircle2,
  RotateCcw,
  Edit,
  Eye,
  Dumbbell,
  Building2,
  MessageSquare,
  Loader2,
  ChevronsUpDown,
  Search as SearchIcon,
  FileText,
  XCircle,
  RefreshCw,
  Layers,
  CalendarRange,
} from "lucide-react";
import { API } from "@/App";
import { format, addDays, addMonths, startOfWeek, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isSameMonth } from "date-fns";
import { tr } from "date-fns/locale";
import { getDateLocale, getDayNamesShort } from "@/utils/dateUtils";
import TimePicker from "@/components/TimePicker";
import { DatePickerTR } from "@/components/ui/date-picker-tr";
import { formatDateToLocal } from "@/lib/utils";
import { useLocalizedData } from "@/contexts/LocalizedDataContext";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useTranslation } from "react-i18next";
import CancellationAnalysis from "@/components/CancellationAnalysis";
import { OpenClassesManager } from "@/components/OpenClassesManager";
import { computeRepeatDates, RecurringPreview, WeekdayPicker, newSeriesId } from "@/components/RecurringOptions";
import { RestrictionWarning, RecurringTemplateBar } from "@/components/MemberRestriction";
import { resColorClass, ReservationColorLegend, MonthDayCounts } from "@/components/ReservationColors";
import { RepeatReservationDialog } from "@/components/RepeatReservationDialog";
import { SeriesManageDialog } from "@/components/SeriesManageDialog";
import { BulkPlanDialog } from "@/components/BulkPlanDialog";
import { CANCEL_REASONS } from "@/components/cancelReasons";
import { DayStrip } from "@/components/DayStrip";
import { GroupRemindersDialog } from "@/components/GroupRemindersDialog";

// Predefined cancellation reasons (used in the cancel dialog + analysis report)

// Helper component to check availability for a request
// Using React.useState and React.useEffect since this is defined before the main component imports
const RequestAvailabilityCheck = ({ request, trainers, rooms, apiUrl }) => {
  const { t } = useTranslation(['reservations', 'common', "admin", "packages", "qr"]);
  const { getLocalizedBranch, getLocalizedTrainingType } = useLocalizedData();
  const [trainerAvail, setTrainerAvail] = React.useState(null);
  const [roomAvail, setRoomAvail] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const checkAvailability = async () => {
      setLoading(true);
      try {
        // Check trainer availability - exclude current request to avoid self-blocking
        if (request.trainer_id) {
          const trainerRes = await fetch(
            `${apiUrl}/trainers/${request.trainer_id}/availability?date=${request.date}&time=${request.start_time}&end_time=${request.end_time || ""}&exclude_request_id=${request.request_id}`,
            { credentials: "include" }
          );
          if (trainerRes.ok) {
            setTrainerAvail(await trainerRes.json());
          }
        }
        
        // Check room availability - exclude current request to avoid self-blocking
        if (request.room_id) {
          const roomRes = await fetch(
            `${apiUrl}/rooms/${request.room_id}/availability?date=${request.date}&time=${request.start_time}&end_time=${request.end_time || ""}&exclude_request_id=${request.request_id}`,
            { credentials: "include" }
          );
          if (roomRes.ok) {
            setRoomAvail(await roomRes.json());
          }
        }
      } catch (error) {
        console.error("Error checking availability:", error);
      } finally {
        setLoading(false);
      }
    };
    
    if (request.date && request.start_time) {
      checkAvailability();
    }
  }, [request, apiUrl]);

  if (loading) {
    return <span className="text-grey">{t('qr:checking')}</span>;
  }

  const trainer = trainers?.find(t => t.trainer_id === request.trainer_id);
  const room = rooms?.find(r => r.room_id === request.room_id);

  return (
    <>
      {/* Trainer Availability */}
      {trainerAvail && (
        <div className={`flex items-center gap-1 px-2 py-1 rounded-lg ${trainerAvail.is_available ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
          {trainerAvail.is_available ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
          <span>{trainer?.name || t("common:trainer")}: {trainerAvail.is_available ? t("reservations:trainerAvailable") : trainerAvail.reason || t("reservations:trainerBusy")}</span>
        </div>
      )}
      
      {/* Room Availability */}
      {roomAvail && (
        <div className={`flex items-center gap-1 px-2 py-1 rounded-lg ${roomAvail.available_capacity > 0 && !roomAvail.is_locked ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
          {roomAvail.available_capacity > 0 && !roomAvail.is_locked ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
          <span>{room?.name || t("common:room")}: {roomAvail.is_locked ? t("common:roomLocked") : `${roomAvail.available_capacity}/${roomAvail.total_capacity}`}</span>
        </div>
      )}
      
      {/* Fallback message if no data */}
      {!trainerAvail && !roomAvail && (
        <span className="text-grey">{t("reservations:availabilityFailed")}</span>
      )}
    </>
  );
};

export default function ReservationsModule({ businessId, subscription, userPermissions, currentUser }) {
  const { t } = useTranslation(["common", "reservations", "members"]);
  const { getLocalizedBranch, getLocalizedTrainingType } = useLocalizedData();
  const { currencySymbol } = useCurrency();
  // Check if user can add/edit (has active paid subscription)
  const canEdit = subscription?.is_active && subscription?.plan !== 'free';
  const [reservations, setReservations] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [members, setMembers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [memberCards, setMemberCards] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTrainer, setSelectedTrainer] = useState("");
  const [selectedRoom, setSelectedRoom] = useState("");
  const [selectedMemberFilter, setSelectedMemberFilter] = useState(""); // Üye filtresi
  const [memberFilterOpen, setMemberFilterOpen] = useState(false);
  const [memberFilterSearch, setMemberFilterSearch] = useState("");
  const [viewMode, setViewMode] = useState("week"); // day, week, month - default haftalık
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newResStatus, setNewResStatus] = useState("pending_member_approval"); // "pending_member_approval" or "approved"
  const [statusFilter, setStatusFilter] = useState("all"); // "all", "confirmed", "pending"
  const [showAvailableSlots, setShowAvailableSlots] = useState(false); // Boş saatleri göster
  const [trainerSchedules, setTrainerSchedules] = useState({}); // trainer_id -> schedules
  const [trainerTimeBlocks, setTrainerTimeBlocks] = useState({}); // trainer_id -> time blocks
  const [businessWorkingHours, setBusinessWorkingHours] = useState({}); // Business working hours by day
  const [businessSettings, setBusinessSettings] = useState({}); // Full business settings
  const [showChangeModal, setShowChangeModal] = useState(false);
  const [selectedReservationForChange, setSelectedReservationForChange] = useState(null);
  const [completingReservationId, setCompletingReservationId] = useState(null); // Çift tıklama koruması
  const [processingReservationId, setProcessingReservationId] = useState(null); // İptal/Onayla/Reddet için
  
  // Ders Açıklaması (Lesson Note) modal state
  const [showLessonNoteModal, setShowLessonNoteModal] = useState(false);
  const [lessonNoteTarget, setLessonNoteTarget] = useState(null); // reservation
  const [lessonNoteMode, setLessonNoteMode] = useState("edit"); // "complete" | "edit"
  const [lessonNoteText, setLessonNoteText] = useState("");
  const [savingLessonNote, setSavingLessonNote] = useState(false);
  
  // Change/Edit form - tüm alanlar
  const [changeDate, setChangeDate] = useState("");
  const [changeStartTime, setChangeStartTime] = useState("");
  const [changeEndTime, setChangeEndTime] = useState("");
  const [changeMember, setChangeMember] = useState("");
  const [changeTrainer, setChangeTrainer] = useState("");
  const [changeRoom, setChangeRoom] = useState("");
  const [changeBranch, setChangeBranch] = useState("");
  const [changeType, setChangeType] = useState("one_on_one");
  const [changeAllowShared, setChangeAllowShared] = useState(true);
  const [changePackageId, setChangePackageId] = useState("");
  const [changeMemberPackages, setChangeMemberPackages] = useState([]);
  const [changeSelectedPackageInfo, setChangeSelectedPackageInfo] = useState(null);
  const [changeFilteredRooms, setChangeFilteredRooms] = useState([]);
  const [changeFilteredTrainers, setChangeFilteredTrainers] = useState([]);
  const [changeRoomAvailability, setChangeRoomAvailability] = useState(null);
  const [changeTrainerAvailability, setChangeTrainerAvailability] = useState(null);
  const [changeApprovalStatus, setChangeApprovalStatus] = useState("change_pending_member"); // change_pending_member veya approved
  const [changePaymentType, setChangePaymentType] = useState(""); // package, card, demo
  const [changeCard, setChangeCard] = useState("");
  
  // View Reservation Modal
  const [showViewModal, setShowViewModal] = useState(false);
  // Day Time view: grouped (duet/group) box member picker
  const [dtGroupPicker, setDtGroupPicker] = useState(null);
  const [viewingReservation, setViewingReservation] = useState(null);
  
  // Change Requests ({t("common:changeRequests")})
  const [changeRequests, setChangeRequests] = useState([]);
  
  // Talep (Reservation Request) sistemi
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [reservationRequests, setReservationRequests] = useState([]);
  const [showRequestsTab, setShowRequestsTab] = useState(false);
  const [requestMember, setRequestMember] = useState("");
  const [requestPackageId, setRequestPackageId] = useState("");
  const [requestTrainer, setRequestTrainer] = useState("");
  const [requestRoom, setRequestRoom] = useState("");
  const [requestStartTime, setRequestStartTime] = useState("");
  const [requestEndTime, setRequestEndTime] = useState("");
  const [requestMemberPackages, setRequestMemberPackages] = useState([]);
  const [requestSelectedPackageInfo, setRequestSelectedPackageInfo] = useState(null);
  const [requestFilteredRooms, setRequestFilteredRooms] = useState([]);
  const [requestFilteredTrainers, setRequestFilteredTrainers] = useState([]);
  const [requestRoomAvailability, setRequestRoomAvailability] = useState(null);
  const [requestTrainerAvailability, setRequestTrainerAvailability] = useState(null);
  const [requestBranch, setRequestBranch] = useState("");
  const [requestType, setRequestType] = useState("one_on_one");
  const [requestAllowShared, setRequestAllowShared] = useState(true);
  const [requestNotes, setRequestNotes] = useState("");
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedRequestForReject, setSelectedRequestForReject] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  
  // Değişiklik Talebi Red Modal
  const [showRejectChangeModal, setShowRejectChangeModal] = useState(false);
  const [selectedChangeForReject, setSelectedChangeForReject] = useState(null);
  const [rejectChangeReason, setRejectChangeReason] = useState("");
  
  // Değişiklik Talebi Düzenleme Modal (İşletme tarafından)
  const [showEditChangeModal, setShowEditChangeModal] = useState(false);
  const [editingChangeRequest, setEditingChangeRequest] = useState(null);
  const [editChangeForm, setEditChangeForm] = useState({
    new_date: "",
    new_start_time: "",
    new_end_time: "",
    trainer_id: "",
    room_id: "",
    reason: ""
  });
  const [editChangeRoomAvailability, setEditChangeRoomAvailability] = useState(null);
  const [editChangeTrainerAvailability, setEditChangeTrainerAvailability] = useState(null);
  
  // Edit Request Modal (İşletme tarafından talep düzenleme)
  const [showEditRequestModal, setShowEditRequestModal] = useState(false);
  const [editingRequest, setEditingRequest] = useState(null);
  const [editReqMember, setEditReqMember] = useState("");
  const [editReqTrainer, setEditReqTrainer] = useState("");
  const [editReqRoom, setEditReqRoom] = useState("");
  const [editReqBranch, setEditReqBranch] = useState("");
  const [editReqDate, setEditReqDate] = useState("");
  const [editReqStartTime, setEditReqStartTime] = useState("");
  const [editReqEndTime, setEditReqEndTime] = useState("");
  const [editReqNotes, setEditReqNotes] = useState("");
  const [editReqFilteredTrainers, setEditReqFilteredTrainers] = useState([]);
  const [editReqFilteredRooms, setEditReqFilteredRooms] = useState([]);
  const [editReqRoomAvailability, setEditReqRoomAvailability] = useState(null);
  const [editReqTrainerAvailability, setEditReqTrainerAvailability] = useState(null);
  const [submittingEditRequest, setSubmittingEditRequest] = useState(false);
  
  // Cancel/Burn Modal
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedReservationForCancel, setSelectedReservationForCancel] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelReasonCategory, setCancelReasonCategory] = useState("");
  // İptal Analizi dialog
  const [showCancelAnalysis, setShowCancelAnalysis] = useState(false);
  const [showOpenClasses, setShowOpenClasses] = useState(false);
  const [openClassPrefill, setOpenClassPrefill] = useState(null);
  const [showReminders, setShowReminders] = useState(false);
  
  // Loading states for preventing double-click
  const [submittingRequest, setSubmittingRequest] = useState(false);
  const [submittingChange, setSubmittingChange] = useState(false);
  const [submittingApproval, setSubmittingApproval] = useState(false);
  const [submittingCancel, setSubmittingCancel] = useState(false);
  const [submittingReservation, setSubmittingReservation] = useState(false); // For create reservation
  const [submittingEditChange, setSubmittingEditChange] = useState(false); // For edit change request
  const [burnSession, setBurnSession] = useState(false);
  const [addToOnline, setAddToOnline] = useState(true);
  const [onlineOnly, setOnlineOnly] = useState(false);
  const [changeAddToOnline, setChangeAddToOnline] = useState(false);
  const [cancelOnline, setCancelOnline] = useState(null); // {linked, others}
  const [deleteOnlineOnCancel, setDeleteOnlineOnCancel] = useState(true);
  // Optional commission awards when a lesson is cancelled (trainer & employee, independent)
  const [awardTrainerCommission, setAwardTrainerCommission] = useState(false);
  const [awardEmployeeCommission, setAwardEmployeeCommission] = useState(false);
  
  // Restore Modal
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [selectedReservationForRestore, setSelectedReservationForRestore] = useState(null);
  const [restoreReason, setRestoreReason] = useState("");

  // YENİ: Çakışma kontrolü
  const [reservationConflicts, setReservationConflicts] = useState([]);
  const [checkingConflicts, setCheckingConflicts] = useState(false);
  
  // Action loading state - bir butona basılınca diğerleri disabled olsun
  const [actionLoading, setActionLoading] = useState(null);
  
  // Full page loading overlay
  const [pageLoading, setPageLoading] = useState(false);

  // Create reservation form - yeni sıralama (Üye -> Ödeme Tipi -> Antrenör -> Oda -> Branş -> Saat -> Antrenman Tipi)
  const [newResDate, setNewResDate] = useState(null); // Modal için ayrı tarih state'i
  const [datePopoverOpen, setDatePopoverOpen] = useState(false); // For auto-closing date picker
  const [newResMember, setNewResMember] = useState("");
  const [paymentType, setPaymentType] = useState("package"); // package, card, demo, external_member
  const [selectedPackageId, setSelectedPackageId] = useState("");
  const [selectedCard, setSelectedCard] = useState("");
  const [newResTrainer, setNewResTrainer] = useState("");
  const [newResRoom, setNewResRoom] = useState("");
  const [newResBranch, setNewResBranch] = useState("");
  const [newResStartTime, setNewResStartTime] = useState("");
  const [newResEndTime, setNewResEndTime] = useState("");
  // Latest-only availability fetch: ignores stale responses and invalid (end <= start) ranges
  const availSeqRef = useRef({});
  const availFetch = async (key, url, start, end) => {
    const n = (availSeqRef.current[key] = (availSeqRef.current[key] || 0) + 1);
    if (start && end && end <= start) return null;
    const response = await fetch(url, { credentials: "include" });
    return availSeqRef.current[key] === n ? response : null;
  };
  const [newResType, setNewResType] = useState("one_on_one");
  const fromSlotClickRef = useRef(false); // Track if form was opened from empty slot click
  const prevNewResBranchRef = useRef("");
  const prevChangeBranchRef = useRef("");
  const [newResFilteredTrainers, setNewResFilteredTrainers] = useState([]); // Branşa göre filtrelenmiş antrenörler
  
  // External member (Dış Üye) - for Prim Veren/Kira Veren trainers
  const [isExternalMember, setIsExternalMember] = useState(false); // Checkbox state
  const [externalMembers, setExternalMembers] = useState([]); // Members from trainer's own business
  const [externalMemberPackages, setExternalMemberPackages] = useState([]); // Packages of selected external member
  const [selectedExternalMember, setSelectedExternalMember] = useState("");
  const [selectedExternalPackageId, setSelectedExternalPackageId] = useState("");
  const [selectedExternalPackageInfo, setSelectedExternalPackageInfo] = useState(null);
  const [trainerOwnBusiness, setTrainerOwnBusiness] = useState(null); // Trainer's own business info
  const [canHaveExternalMembers, setCanHaveExternalMembers] = useState(false); // Trainer is Prim/Kira Veren
  
  // Recurring reservation options
  const [repeatEnabled, setRepeatEnabled] = useState(false);
  const [repeatType, setRepeatType] = useState("weekly"); // daily, weekly
  const [repeatCount, setRepeatCount] = useState(4); // Number of repetitions
  const [repeatDays, setRepeatDays] = useState([]);
  const [repeatEndMode, setRepeatEndMode] = useState("count"); // count | date
  const [repeatUntil, setRepeatUntil] = useState("");
  const [repeatStatus, setRepeatStatus] = useState({ bad: 0, overLimit: false });
  const [savedTemplates, setSavedTemplates] = useState({});
  const [repeatSource, setRepeatSource] = useState(null);
  const [seriesSource, setSeriesSource] = useState(null);
  const [showBulkPlan, setShowBulkPlan] = useState(false);
  const statusForMember = (m) => (businessSettings?.display_settings?.allow_online_booking === false || m?.is_app_registered === false ? "approved" : "pending_member_approval");
  const refreshAfterBulk = () => { fetchData(); fetchTabCounts(); fetchPendingReservations(); };
  const canRepeat = (res) => canEdit && ["confirmed", "approved", "completed"].includes(res?.status) && res?.payment_type !== "external_member" && !!res?.member_id;
  
  // Filtered rooms based on selected branch
  const [filteredRooms, setFilteredRooms] = useState([]);
  
  // PAGINATION STATE
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [paginationInfo, setPaginationInfo] = useState({
    page: 1,
    page_size: 10,
    total_count: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false
  });
  
  // Selected member's packages
  const [memberPackages, setMemberPackages] = useState([]);
  
  // Selected member info
  const [selectedMemberInfo, setSelectedMemberInfo] = useState(null);
  
  // Selected package info (for duration and branch)
  const [selectedPackageInfo, setSelectedPackageInfo] = useState(null);
  
  // Availability check
  const [availability, setAvailability] = useState({ checking: false, result: null });
  
  // Complex reservation options
  const [allowShared, setAllowShared] = useState(true);  // "Diğer makinalar açık kalsın mı?"
  const [roomAvailability, setRoomAvailability] = useState(null);
  const [branchMismatchWarning, setBranchMismatchWarning] = useState("");
  
  // Owner/manager (with backdate_reservations permission) can back-date reservations
  const canBackdateReservations = () => {
    if (currentUser?.role === "business" || userPermissions?.is_owner) return true;
    if (!userPermissions?.is_manager) return false;
    // Manager: explicit permission required (missing key defaults to True for backward compatibility)
    return userPermissions?.permissions?.backdate_reservations !== false;
  };
  
  // Helper function to check if a date/time is valid for reservation
  const isTimeSlotValid = (date, time) => {
    if (!date || !time) return true; // Don't validate if not set
    // Owners & managers can create back-dated reservations (retroactive entry)
    if (canBackdateReservations()) return true;
    
    const now = new Date();
    const minAdvanceHours = businessSettings.reservation_settings?.min_advance_booking || 
                           businessSettings.min_advance_booking || 2;
    
    // Create datetime from selected date and time
    const [hours, minutes] = time.split(':').map(Number);
    const selectedDateTime = new Date(date);
    selectedDateTime.setHours(hours, minutes, 0, 0);
    
    // Calculate minimum allowed time
    const minAllowedTime = new Date(now.getTime() + (minAdvanceHours * 60 * 60 * 1000));
    
    return selectedDateTime >= minAllowedTime;
  };
  
  // Helper: Min advance süresini okunabilir formatta döndür
  const getMinAdvanceText = () => {
    const minAdvanceHours = businessSettings.reservation_settings?.min_advance_booking || 
                           businessSettings.min_advance_booking || 2;
    const minAdvanceMinutes = Math.round(minAdvanceHours * 60);
    
    if (minAdvanceMinutes >= 60) {
      const hours = Math.floor(minAdvanceMinutes / 60);
      const mins = minAdvanceMinutes % 60;
      return mins > 0 ? `${hours} saat ${mins} dakika` : `${hours} saat`;
    }
    return `${minAdvanceMinutes} dakika`;
  };
  
  // Get minimum allowed date for date picker
  const getMinDate = () => {
    // Owners & managers can pick past dates
    if (canBackdateReservations()) return undefined;
    return formatDateToLocal(new Date());
  };
  
  // Get minimum allowed time for today
  const getMinTime = (selectedDateStr) => {
    if (!selectedDateStr) return null;
    // Owners & managers can pick past times
    if (canBackdateReservations()) return null;
    
    const today = formatDateToLocal(new Date());
    if (selectedDateStr !== today) return null; // No time restriction for future dates
    
    const now = new Date();
    const minAdvanceHours = businessSettings.reservation_settings?.min_advance_booking || 
                           businessSettings.min_advance_booking || 2;
    
    const minTime = new Date(now.getTime() + (minAdvanceHours * 60 * 60 * 1000));
    return `${String(minTime.getHours()).padStart(2, '0')}:${String(minTime.getMinutes()).padStart(2, '0')}`;
  };
  
  // Check if end time exceeds business closing time
  const isEndTimeWithinBusinessHours = (date, endTime) => {
    if (!date || !endTime || !businessWorkingHours) return true;
    
    // Get day of week (0 = Sunday, 1 = Monday, etc.)
    const dateObj = new Date(date);
    const dayIndex = dateObj.getDay();
    const dayNames = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
    const dayName = dayNames[dayIndex];
    
    const dayHours = businessWorkingHours[dayName];
    if (!dayHours || dayHours.closed) return false; // Business closed
    
    const closingTime = dayHours.close || dayHours.end || "22:00";
    
    // Compare times
    const [endH, endM] = endTime.split(':').map(Number);
    const [closeH, closeM] = closingTime.split(':').map(Number);
    
    const endMinutes = endH * 60 + endM;
    const closeMinutes = closeH * 60 + closeM;
    
    return endMinutes <= closeMinutes;
  };
  
  // Get business closing time for a specific date
  const getBusinessClosingTime = (date) => {
    if (!date || !businessWorkingHours) return "22:00";
    
    const dateObj = new Date(date);
    const dayIndex = dateObj.getDay();
    const dayNames = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
    const dayName = dayNames[dayIndex];
    
    const dayHours = businessWorkingHours[dayName];
    if (!dayHours || dayHours.closed) return null;
    
    return dayHours.close || dayHours.end || "22:00";
  };
  
  const [trainerAvailability, setTrainerAvailability] = useState(null);
  
  // YENİ 4-TAB SİSTEMİ
  // reservations: Onaylı + İptal + Tamamlanan
  // requests: Üyeden gelen YENİ talepler (pending_business_approval)
  // pending: İşletmenin yaptığı, üye onayı bekleyen (pending_member_approval, change_pending_member)
  // changes: Üyeden gelen değişiklik talepleri (change_pending_business)
  const [activeTab, setActiveTab] = useState("reservations");
  const [tabCounts, setTabCounts] = useState({ requests: 0, pending: 0, changes: 0 });
  
  // Waitlist (eski sistem - artık kullanılmıyor ama geriye uyumluluk için bırakıldı)
  const [waitlist, setWaitlist] = useState([]);
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);
  
  // Waitlist form
  const [waitlistMember, setWaitlistMember] = useState("");
  const [waitlistTrainer, setWaitlistTrainer] = useState("");
  const [waitlistRoom, setWaitlistRoom] = useState("");
  const [waitlistBranch, setWaitlistBranch] = useState("");
  const [waitlistTime, setWaitlistTime] = useState("");
  const [waitlistNotes, setWaitlistNotes] = useState("");

  useEffect(() => {
    if (businessId) {
      fetchAllData();
    }
  }, [businessId, selectedDate, viewMode]);
  
  // OPTIMIZED: Single API call for all reservation data
  const fetchAllData = async () => {
    try {
      // Calculate date range
      let startDate, endDate;
      if (viewMode === "day" || viewMode === "dayTime" || viewMode === "week") {
        const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 });
        const weekEnd = addDays(weekStart, 6);
        startDate = weekStart;
        endDate = weekEnd;
      } else {
        startDate = startOfMonth(selectedDate);
        endDate = endOfMonth(selectedDate);
      }
      
      const startStr = format(startDate, "yyyy-MM-dd");
      const endStr = format(endDate, "yyyy-MM-dd");
      
      // Build pagination query params. Calendar views (day/week/month) load the
      // ENTIRE visible date range in one page so the grid never drops records
      // that fall outside the first paginated page.
      const isCalendarView = ["day", "dayTime", "week", "month"].includes(viewMode);
      const effPageSize = isCalendarView ? 1000 : pageSize;
      const effPage = isCalendarView ? 1 : currentPage;
      const paginationParams = `page=${effPage}&page_size=${effPageSize}`;
      
      // Fetch all data in parallel - OPTIMIZED with PAGINATION
      const [allDataRes, trainersRes, roomsRes, membersRes, cardsRes, packagesRes, businessRes, bulkSchedulesRes, bulkBlocksRes, employeesRes] = await Promise.all([
        fetch(`${API}/reservations/all-data/${businessId}?start_date=${startStr}&end_date=${endStr}&${paginationParams}`, { credentials: "include" }),
        fetch(`${API}/businesses/${businessId}/trainers`, { credentials: "include" }),
        fetch(`${API}/businesses/${businessId}/rooms`, { credentials: "include" }),
        fetch(`${API}/businesses/${businessId}/members`, { credentials: "include" }),
        fetch(`${API}/public/member-cards`),
        fetch(`${API}/businesses/${businessId}/packages`, { credentials: "include" }),
        fetch(`${API}/businesses/${businessId}`, { credentials: "include" }),
        fetch(`${API}/businesses/${businessId}/bulk-schedules`, { credentials: "include" }),
        fetch(`${API}/businesses/${businessId}/bulk-time-blocks?start_date=${startStr}&end_date=${endStr}`, { credentials: "include" }),
        fetch(`${API}/businesses/${businessId}/employees`, { credentials: "include" })
      ]);
      
      // Process all reservation data from single endpoint
      if (allDataRes.ok) {
        const allData = await allDataRes.json();
        setReservations(allData.reservations || []);
        setReservationRequests(allData.reservation_requests || []);
        setAllReservations(allData.all_reservations || []);
        setChangeRequests(allData.change_requests || []);
        setTabCounts(allData.tab_counts || {});
        // Beklemede tab = pending_member_approval + change_pending_member
        setPendingReservations(allData.pending_tab || []);
        // Set pagination info
        if (allData.pagination) {
          setPaginationInfo(allData.pagination);
        }
      }
      
      // Process trainers and bulk data
      let trainersData = [];
      if (trainersRes.ok) {
        trainersData = await trainersRes.json();
        setTrainers(trainersData);
      }

      if (employeesRes.ok) {
        const empData = await employeesRes.json();
        setEmployees(Array.isArray(empData) ? empData : (empData.employees || []));
      }
      
      if (bulkSchedulesRes.ok) {
        setTrainerSchedules(await bulkSchedulesRes.json());
      }
      
      if (bulkBlocksRes.ok) {
        setTrainerTimeBlocks(await bulkBlocksRes.json());
      }
      
      if (roomsRes.ok) setRooms(await roomsRes.json());
      if (membersRes.ok) setMembers(await membersRes.json());
      if (cardsRes.ok) {
        const data = await cardsRes.json();
        setMemberCards(data.member_cards || []);
      }
      if (packagesRes.ok) setPackages(await packagesRes.json());
      
      if (businessRes.ok) {
        const businessData = await businessRes.json();
        setBusinessWorkingHours(businessData.working_hours || {});
        setBusinessSettings(businessData);
        const businessBranches = businessData.branches || businessData.training_types || [];
        setBranches(businessBranches.map(b => typeof b === 'string' ? b : (b.name || b)));
      }
    } catch (error) {
      console.error("Error fetching reservation data:", error);
    } finally {
      setLoading(false);
    }
  };
  
  // Keep fetchData for backward compatibility but redirect to fetchAllData
  const fetchData = () => fetchAllData();
  
  // YENİ: Tab sayaçlarını getir - now handled by fetchAllData
  const fetchTabCounts = async () => {
    // Handled by fetchAllData
  };
  
  // YENİ: Üyeden gelen talepler (pending_business_approval) - now handled by fetchAllData
  const fetchReservationRequests = async () => {
    // Handled by fetchAllData
  };
  
  // YENİ: Üyeden gelen değişiklik talepleri - now handled by fetchAllData
  const fetchChangeRequests = async () => {
    // Handled by fetchAllData
  };
  
  // YENİ: İşletmenin yaptığı, üye onayı bekleyen - now handled by fetchAllData
  const [pendingReservations, setPendingReservations] = useState([]);
  const fetchPendingReservations = async () => {
    // Handled by fetchAllData
  };
  
  // YENİ: TÜM rezervasyonları getir - now handled by fetchAllData
  const [allReservations, setAllReservations] = useState([]);
  const fetchAllReservations = async () => {
    // Handled by fetchAllData
  };
  
  const fetchWaitlist = async () => {
    try {
      const response = await fetch(
        `${API}/waitlist/${businessId}?date=${format(selectedDate, "yyyy-MM-dd")}`,
        { credentials: "include" }
      );
      if (response.ok) {
        setWaitlist(await response.json());
      }
    } catch (error) {
      console.error("Error fetching waitlist:", error);
    }
  };
  
  const handleConvertWaitlist = async (waitlistId) => {
    try {
      const response = await fetch(`${API}/waitlist/${waitlistId}/convert`, {
        method: "PUT",
        credentials: "include",
      });
      
      if (response.ok) {
        toast.success(t("reservations:createdFromWaitlist"), { duration: 2000 });
        fetchData();
        fetchWaitlist();
      } else {
        const error = await response.json();
        toast.error(error.detail || t("common:operationFailed"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };
  
  const handleRemoveFromWaitlist = async (waitlistId) => {
    try {
      const response = await fetch(`${API}/waitlist/${waitlistId}`, {
        method: "DELETE",
        credentials: "include",
      });
      
      if (response.ok) {
        toast.success(t("reservations:removedFromWaitlist"), { duration: 2000 });
        fetchWaitlist();
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };
  
  // Add to waitlist
  const handleAddToWaitlist = async () => {
    if (!waitlistMember || !waitlistTime) {
      toast.error(t("reservations:selectMemberAndTime"));
      return;
    }
    
    try {
      const response = await fetch(`${API}/waitlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          business_id: businessId,
          member_id: waitlistMember,
          trainer_id: waitlistTrainer && waitlistTrainer !== "none" ? waitlistTrainer : null,
          room_id: waitlistRoom || null,
          branch: waitlistBranch || null,
          date: format(selectedDate, "yyyy-MM-dd"),
          start_time: waitlistTime,
          training_type: "one_on_one",
          notes: waitlistNotes || null,
        }),
      });
      
      if (response.ok) {
        toast.success("Bekleme listesine eklendi");
        setShowWaitlistModal(false);
        resetWaitlistForm();
        fetchWaitlist();
      } else {
        const error = await response.json();
        toast.error(error.detail || t("reservations:addFailed"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };
  
  const resetWaitlistForm = () => {
    setWaitlistMember("");
    setWaitlistTrainer("");
    setWaitlistRoom("");
    setWaitlistBranch("");
    setWaitlistTime("");
    setWaitlistNotes("");
  };

  // Fetch member's packages and info when member changes
  // Also reset form fields when member changes
  const prevMemberRef = useRef(null);
  
  useEffect(() => {
    // Check if member actually changed (not just initial load)
    const memberChanged = prevMemberRef.current !== null && prevMemberRef.current !== newResMember;
    prevMemberRef.current = newResMember;
    
    if (newResMember) {
      fetchMemberPackages(newResMember);
      // Find member info to get assigned trainer
      const memberInfo = members.find(m => m.member_id === newResMember);
      setSelectedMemberInfo(memberInfo);
      
      // Reset form fields when member changes (except trainer which might be auto-filled)
      if (memberChanged) {
        // paymentType will be set by fetchMemberPackages after loading packages
        setSelectedPackageId("");
        setSelectedPackageInfo(null);
        setNewResBranch("");
        setNewResType("one_on_one");
        // Don't reset date/time/room if opened from empty slot click
        if (!fromSlotClickRef.current) {
          setNewResRoom("");
          setNewResDate("");
          setNewResStartTime("");
          setNewResEndTime("");
        }
        setRoomAvailability(null);
        setTrainerAvailability(null);
        setAllowShared(true);
        setAddToOnline(true);
        setRepeatEnabled(false);
        setRepeatDays([]);
        setRepeatEndMode("count");
        setRepeatUntil("");
        setRepeatType("weekly");
        setRepeatCount(4);
      }
      
      // Auto-set status based on online booking setting and member registration
      // Priority: online booking kapalı > kayıt olmayan üye > normal
      if (businessSettings?.display_settings?.allow_online_booking === false) {
        setNewResStatus("approved");
      } else if (memberInfo?.is_app_registered === false) {
        setNewResStatus("approved");
      } else {
        // Kayıtlı üye ve online booking açık - normal akış
        setNewResStatus("pending_member_approval");
      }
      
      // Auto-fill trainer ONLY if no trainer is selected yet
      // Antrenör sadece bilgi amaçlı - kullanıcı farklı antrenör seçebilir
      if (memberInfo?.trainer_id && !newResTrainer) {
        setNewResTrainer(memberInfo.trainer_id);
      }
    } else {
      setMemberPackages([]);
      setSelectedMemberInfo(null);
    }
  }, [newResMember, members, businessSettings?.display_settings?.allow_online_booking]);
  
  // Refetch data when page changes
  useEffect(() => {
    if (businessId) {
      fetchAllData();
    }
  }, [currentPage, pageSize]);
  
  // Auto-fill branch and calculate end time when package is selected
  useEffect(() => {
    if (selectedPackageId && paymentType === "package") {
      const mp = memberPackages.find(p => p.member_package_id === selectedPackageId);
      if (mp) {
        console.log("Selected member package:", mp);
        
        // For combo packages (2+ branches), DON'T auto-set: let user pick.
        // For single-branch packages, auto-fill (current behavior).
        const pkgBranches = mp.package_branches && mp.package_branches.length > 0
          ? mp.package_branches
          : (mp.package_branch ? [mp.package_branch] : []);
        if (pkgBranches.length === 1) {
          console.log("Setting branch from single-branch package:", pkgBranches[0]);
          setNewResBranch(pkgBranches[0]);
        } else if (pkgBranches.length >= 2) {
          console.log("Combo package detected — user must pick branch", pkgBranches);
          // Only auto-clear if current selection isn't in the combo list
          if (newResBranch && !pkgBranches.includes(newResBranch)) {
            setNewResBranch("");
          }
        }
        
        // Also set trainer from package if assigned
        if (mp.trainer_id && !newResTrainer) {
          setNewResTrainer(mp.trainer_id);
        }
        
        const duration = mp.session_duration || 55;

        // Store package info for duration calculation
        setSelectedPackageInfo({
          ...mp,
          branches: mp.package_branches || [],
          branch: mp.package_branch || "",
          session_duration: duration,
          session_type: mp.session_type || t("common:privateLessonType")
        });
        
        // BAŞLANGIÇ SAATİ VARSA YENİ PAKETİN SÜRESİYLE BİTİŞİ ANINDA HESAPLA
        if (newResStartTime) {
          const [hours, minutes] = newResStartTime.split(':').map(Number);
          const startMinutes = hours * 60 + minutes;
          const endMinutes = startMinutes + duration;
          const endHours = Math.floor(endMinutes / 60);
          const endMins = endMinutes % 60;
          const calculatedEndTime = `${endHours.toString().padStart(2, '0')}:${endMins.toString().padStart(2, '0')}`;
          setNewResEndTime(calculatedEndTime);
        }
        
        // Auto-set training type based on package session_type
        // Admin Settings values: "Özel Ders", "Düet", "Grup Dersi", "Online"
        if (mp.session_type) {
          // Map to frontend values: one_on_one, duet, group
          const typeMapping = { 
            "Özel Ders": "one_on_one",
            "Birebir": "one_on_one",
            "Düet": "duet",
            "Grup Dersi": "group",
            "Grup": "group",
            "Online": "one_on_one",
            // Fallbacks for old values
            "private": "one_on_one",
            "duo": "duet",
            "group": "group"
          };
          const mappedType = typeMapping[mp.session_type] || "one_on_one";
          console.log("Setting training type to:", mappedType, "from session_type:", mp.session_type);
          setNewResType(mappedType);
        }
      }
    } else {
      setSelectedPackageInfo(null);
    }
  }, [selectedPackageId, paymentType, memberPackages, newResTrainer, newResStartTime]);
  
  // Fetch external members when trainer changes and is Prim Veren or Kira Veren
  useEffect(() => {
    const fetchExternalMembers = async () => {
      if (!newResTrainer) {
        setExternalMembers([]);
        setTrainerOwnBusiness(null);
        setCanHaveExternalMembers(false);
        setIsExternalMember(false);
        return;
      }
      
      const selectedTrainerInfo = trainers.find(t => t.trainer_id === newResTrainer);
      if (!selectedTrainerInfo) {
        setCanHaveExternalMembers(false);
        return;
      }
      
      // Check if trainer is Prim Veren or Kira Veren
      const hasExternalCapability = ['commission_giving', 'rent_giving'].includes(selectedTrainerInfo.work_type);
      setCanHaveExternalMembers(hasExternalCapability && !!selectedTrainerInfo.own_business_id);
      
      if (!hasExternalCapability || !selectedTrainerInfo.own_business_id) {
        setExternalMembers([]);
        setTrainerOwnBusiness(null);
        setIsExternalMember(false);
        return;
      }
      
      try {
        // Fetch trainer's own business info
        const businessRes = await fetch(`${API}/businesses/${selectedTrainerInfo.own_business_id}`, {
          credentials: "include"
        });
        if (businessRes.ok) {
          const businessData = await businessRes.json();
          setTrainerOwnBusiness(businessData);
        }
        
        // Fetch members from trainer's own business
        const membersRes = await fetch(`${API}/businesses/${selectedTrainerInfo.own_business_id}/members`, {
          credentials: "include"
        });
        if (membersRes.ok) {
          const externalMembersData = await membersRes.json();
          setExternalMembers(externalMembersData || []);
        }
      } catch (error) {
        console.error("Error fetching external members:", error);
        setExternalMembers([]);
      }
    };
    
    fetchExternalMembers();
  }, [newResTrainer, trainers]);
  
  // Reset member selection when isExternalMember changes
  useEffect(() => {
    if (isExternalMember) {
      setNewResMember("");
      setSelectedPackageId("");
      setSelectedPackageInfo(null);
      setPaymentType("external_member");
      // External member seçilmediğinde henüz status ayarlanmaz
      // Status, external member seçildiğinde is_app_registered'a göre ayarlanacak
    } else {
      setSelectedExternalMember("");
      setSelectedExternalPackageId("");
      setSelectedExternalPackageInfo(null);
      if (paymentType === "external_member") {
        setPaymentType("package");
      }
      // Normal üye seçildiğinde status'u default'a döndür
      setNewResStatus("pending_member_approval");
    }
  }, [isExternalMember]);
  
  // External member seçildiğinde is_app_registered kontrolü yap
  useEffect(() => {
    if (isExternalMember && selectedExternalMember) {
      const selectedMember = externalMembers.find(m => m.member_id === selectedExternalMember);
      if (selectedMember) {
        // Kayıtsız üye ise direkt onaylı, kayıtlı ise onaya gidebilir
        if (selectedMember.is_app_registered === false) {
          setNewResStatus("approved");
        } else {
          setNewResStatus("pending_member_approval");
        }
      }
    }
  }, [selectedExternalMember, externalMembers, isExternalMember]);
  
  // Fetch external member's packages when external member changes
  useEffect(() => {
    const fetchExternalMemberPackages = async () => {
      if (!selectedExternalMember || !trainerOwnBusiness) {
        setExternalMemberPackages([]);
        setSelectedExternalPackageInfo(null);
        return;
      }
      
      try {
        const res = await fetch(`${API}/members/${selectedExternalMember}/packages`, {
          credentials: "include"
        });
        if (res.ok) {
          const packagesData = await res.json();
          // Filter only active packages with remaining sessions
          const activePackages = (packagesData || []).filter(p => 
            p.status === 'active' && (p.remaining_sessions > 0 || p.remaining_sessions === undefined)
          );
          setExternalMemberPackages(activePackages);
        }
      } catch (error) {
        console.error("Error fetching external member packages:", error);
        setExternalMemberPackages([]);
      }
    };
    
    fetchExternalMemberPackages();
  }, [selectedExternalMember, trainerOwnBusiness]);
  
  // Auto-fill branch from external package
  useEffect(() => {
    if (isExternalMember && selectedExternalPackageId) {
      const pkg = externalMemberPackages.find(p => p.member_package_id === selectedExternalPackageId);
      if (pkg) {
        setSelectedExternalPackageInfo(pkg);
        // Set branch from package - check all possible field names
        const pkgBranch = pkg.package_branch || pkg.package_branches?.[0] || pkg.branch || pkg.branches?.[0];
        if (pkgBranch) {
          setNewResBranch(pkgBranch);
        }
        // Set training type from package
        if (pkg.session_type || pkg.training_type) {
          const sessionType = pkg.session_type || pkg.training_type;
          const typeMapping = { 
            "Özel Ders": "one_on_one",
            "Birebir": "one_on_one",
            "one_on_one": "one_on_one",
            "Düet": "duet",
            "duet": "duet",
            "Grup Dersi": "group",
            "group": "group"
          };
          setNewResType(typeMapping[sessionType] || "one_on_one");
        }
      }
    } else if (!isExternalMember) {
      setSelectedExternalPackageInfo(null);
    }
  }, [selectedExternalPackageId, isExternalMember, externalMemberPackages]);
  
  // Filter rooms based on selected package's branch
  // Filter rooms based on selected branch AND training type
  useEffect(() => {
    // Determine the active branch based on payment type
    let activeBranch = null;
    let activeTrainingType = null;
    
    if (isExternalMember && selectedExternalPackageInfo) {
      // For external member: use external package's branch and training type
      const packageBranches = selectedExternalPackageInfo.branches || [selectedExternalPackageInfo.branch || selectedExternalPackageInfo.package_branch];
      activeBranch = packageBranches.filter(Boolean);
      activeTrainingType = selectedExternalPackageInfo.training_type || selectedExternalPackageInfo.session_type;
    } else if (paymentType === "package" && selectedPackageInfo) {
      // For internal package: use package's branch and training type
      const packageBranches = selectedPackageInfo.branches || [selectedPackageInfo.branch];
      activeBranch = packageBranches.filter(Boolean);
      activeTrainingType = selectedPackageInfo.training_type || selectedPackageInfo.session_type;
    } else if ((paymentType === "card" || paymentType === "demo")) {
      // For card/demo: use the manually selected branch and training type
      if (newResBranch) {
        activeBranch = [newResBranch];
      }
      // Map newResType to actual training type name
      const typeMapping = {
        "one_on_one": "Birebir",
        "Özel Ders": "Birebir",
        "duet": "Düet",
        "group": "Grup Dersi",
        "Birebir": "Birebir",
        "Düet": t('reservations:duet'),
        "Grup Dersi": "Grup Dersi"
      };
      activeTrainingType = typeMapping[newResType] || newResType;
    }
    
    // Start with all rooms
    let filtered = [...rooms];
    
    // Helper function for case-insensitive comparison
    const matchesIgnoreCase = (str1, str2) => {
      if (!str1 || !str2) return false;
      return str1.toLowerCase().trim() === str2.toLowerCase().trim();
    };
    
    // Filter by branch if active (case-insensitive)
    if (activeBranch && activeBranch.length > 0) {
      filtered = filtered.filter(room => {
        const roomBranches = room.branches || [];
        // If room has no branch restriction, include it
        if (roomBranches.length === 0) return true;
        // Check if any room branch matches active branch (case-insensitive)
        return roomBranches.some(rb => 
          activeBranch.some(ab => matchesIgnoreCase(rb, ab))
        );
      });
    }
    
    // Filter by training type if active (case-insensitive and flexible matching)
    if (activeTrainingType) {
      filtered = filtered.filter(room => {
        const roomTypes = room.training_types || [];
        // If room has no training type restriction, include it
        if (roomTypes.length === 0) return true;
        
        // Build list of all possible matching values
        const possibleMatches = [activeTrainingType, newResType];
        const additionalMappings = {
          "one_on_one": ["Birebir", "Özel Ders", "private", "1-1"],
          "Birebir": ["one_on_one", "Özel Ders", "private", "1-1"],
          "Özel Ders": ["one_on_one", "Birebir", "private", "1-1"],
          "duet": ["Düet", "duo", "2-1"],
          "Düet": ["duet", "duo", "2-1"],
          "group": ["Grup Dersi", "Grup", "Group"],
          "Grup Dersi": ["group", "Grup", "Group"],
          "Grup": ["group", "Grup Dersi", "Group"]
        };
        if (additionalMappings[activeTrainingType]) {
          possibleMatches.push(...additionalMappings[activeTrainingType]);
        }
        if (additionalMappings[newResType]) {
          possibleMatches.push(...additionalMappings[newResType]);
        }
        
        // Check if room supports any of the possible training types (case-insensitive)
        return roomTypes.some(rt => 
          possibleMatches.some(pm => matchesIgnoreCase(rt, pm))
        );
      });
    }
    
    console.log("Room filter:", { activeBranch, activeTrainingType, newResType, totalRooms: rooms.length, filteredRooms: filtered.length, roomDetails: rooms.map(r => ({name: r.name, branches: r.branches, training_types: r.training_types})) });
    
    setFilteredRooms(filtered);
    
    const branchKey = [...(activeBranch || []), activeTrainingType || ""].filter(Boolean).join("|");
    const branchChanged = prevNewResBranchRef.current !== branchKey;
    prevNewResBranchRef.current = branchKey;
    const roomValid = filtered.some(r => r.room_id === newResRoom);
    // Tek oda varsa otomatik seç; birden fazlaysa branş değişince kullanıcı seçsin
    if (branchKey && filtered.length === 1) {
      if (newResRoom !== filtered[0].room_id) { setNewResRoom(filtered[0].room_id); setRoomAvailability(null); }
    } else if (newResRoom && filtered.length > 0 && (!roomValid || (branchChanged && branchKey && !fromSlotClickRef.current))) {
      setNewResRoom("");
      setRoomAvailability(null);
    }
  }, [selectedPackageInfo, selectedExternalPackageInfo, isExternalMember, newResBranch, newResType, paymentType, rooms]);
  
  // Başlangıç saati VEYA seçilen paket değiştiğinde bitiş saatini anında hesapla
  useEffect(() => {
    if (!newResStartTime) return;

    let duration = null;

    if (isExternalMember && selectedExternalPackageInfo?.session_duration) {
      duration = selectedExternalPackageInfo.session_duration;
    } else if (!isExternalMember && paymentType === "package") {
      // Önce doğrudan seçili paketin süresine bak
      const currentPkg = memberPackages.find(p => p.member_package_id === selectedPackageId);
      duration = currentPkg?.session_duration || selectedPackageInfo?.session_duration || 55;
    } else if (!isExternalMember && paymentType === "demo" && newResBranch) {
      duration = businessSettings?.branch_demo_durations?.[newResBranch] || 30;
    }

    if (duration && duration > 0) {
      const [hours, minutes] = newResStartTime.split(':').map(Number);
      const startMinutes = hours * 60 + minutes;
      const endMinutes = startMinutes + duration;
      const endHours = Math.floor(endMinutes / 60);
      const endMins = endMinutes % 60;
      const calculatedEndTime = `${endHours.toString().padStart(2, '0')}:${endMins.toString().padStart(2, '0')}`;
      
      setNewResEndTime(calculatedEndTime);
    }
  }, [newResStartTime, selectedPackageId, selectedPackageInfo, selectedExternalPackageId, selectedExternalPackageInfo, isExternalMember, paymentType, newResBranch, memberPackages, businessSettings]);
  
  // Filter trainers by branch for new reservation
  useEffect(() => {
    if (newResBranch) {
      const filtered = trainers.filter(t => {
        if (t.status !== "active" && t.is_active === false) return false;
        const trainerBranches = t.branches || [];
        return trainerBranches.includes(newResBranch);
      });
      setNewResFilteredTrainers(filtered);
      
      // If current trainer doesn't know this branch, show warning instead of resetting
      if (newResTrainer) {
        const currentTrainer = trainers.find(t => t.trainer_id === newResTrainer);
        if (currentTrainer && !(currentTrainer.branches || []).includes(newResBranch)) {
          setBranchMismatchWarning(t("reservations:trainerBranchMismatch", { branch: newResBranch }));
        } else {
          setBranchMismatchWarning("");
        }
      } else {
        setBranchMismatchWarning("");
      }
    } else {
      setNewResFilteredTrainers(trainers.filter(t => t.status === "active" || t.is_active !== false));
    }
  }, [newResBranch, trainers]);

  // Check availability when room and time are selected - also re-check when end_time or branch changes
  useEffect(() => {
    const checkRoom = async () => {
      if (!newResDate || !newResRoom || !newResStartTime) {
        setRoomAvailability(null);
        return;
      }
      try {
        const dateStr = format(newResDate, "yyyy-MM-dd");
        let url = `${API}/rooms/${newResRoom}/availability?date=${dateStr}&time=${newResStartTime}`;
        if (newResEndTime) url += `&end_time=${newResEndTime}`;
        if (newResBranch) url += `&branch=${encodeURIComponent(newResBranch)}`;
        if (newResType) url += `&training_type=${encodeURIComponent(newResType)}`;
        
        const response = await availFetch("newRoom", url, newResStartTime, newResEndTime);
        if (!response) return;
        if (response.ok) {
          const data = await response.json();
          setRoomAvailability(data);
        } else {
          // API error - set a default "available" state
          setRoomAvailability({ is_available: true, available_capacity: 1, total_capacity: 1, is_locked: false });
        }
      } catch (error) {
        console.error("Error checking room availability:", error);
        // Network error - set a default "available" state to not block the user
        setRoomAvailability({ is_available: true, available_capacity: 1, total_capacity: 1, is_locked: false, error: true });
      }
    };
    checkRoom();
  }, [newResRoom, newResStartTime, newResEndTime, newResDate, newResBranch, newResType]);
  
  // Check trainer availability - trigger on trainer change, time change, training type change, or room change
  useEffect(() => {
    const checkTrainer = async () => {
      if (!newResTrainer || !newResStartTime || !newResDate) {
        setTrainerAvailability(null);
        return;
      }
      try {
        const dateStr = format(newResDate, "yyyy-MM-dd");
        let url = `${API}/trainers/${newResTrainer}/availability?date=${dateStr}&time=${newResStartTime}`;
        if (newResEndTime) url += `&end_time=${newResEndTime}`;
        if (newResType) url += `&training_type=${encodeURIComponent(newResType)}`;
        if (newResRoom) url += `&room_id=${encodeURIComponent(newResRoom)}`;
        if (newResBranch) url += `&branch=${encodeURIComponent(newResBranch)}`;
        
        const response = await availFetch("newTrainer", url, newResStartTime, newResEndTime);
        if (!response) return;
        if (response.ok) {
          const data = await response.json();
          setTrainerAvailability(data);
        }
      } catch (error) {
        console.error("Error checking trainer availability:", error);
      }
    };
    checkTrainer();
  }, [newResTrainer, newResStartTime, newResEndTime, newResDate, newResType, newResRoom]);
  
  // YENİ: Çakışma kontrolü - tarih, saat, antrenör, oda, üye, antrenman tipi değiştiğinde
  const conflictReqIdRef = useRef(0);
  useEffect(() => {
    if (!newResDate || !newResStartTime || !newResEndTime) {
      setReservationConflicts([]);
      setCheckingConflicts(false);
      return;
    }

    // Bump version so any in-flight request becomes stale
    const myReqId = ++conflictReqIdRef.current;
    setCheckingConflicts(true);
    // Clear existing conflicts immediately so stale warnings don't linger
    setReservationConflicts([]);

    // Debounce rapid input changes (e.g. scrubbing the hour picker)
    const handle = setTimeout(async () => {
      try {
        const dateStr = format(newResDate, "yyyy-MM-dd");
        const response = await fetch(`${API}/reservations/check-conflicts`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            business_id: businessId,
            date: dateStr,
            start_time: newResStartTime,
            end_time: newResEndTime,
            trainer_id: newResTrainer || null,
            room_id: newResRoom || null,
            member_id: newResMember || null,
            training_type: newResType || null
          })
        });
        // Drop response if a newer request was fired meanwhile
        if (myReqId !== conflictReqIdRef.current) return;
        if (response.ok) {
          const data = await response.json();
          setReservationConflicts(data.conflicts || []);
        }
      } catch (error) {
        if (myReqId !== conflictReqIdRef.current) return;
        console.error("Error checking conflicts:", error);
      } finally {
        if (myReqId === conflictReqIdRef.current) {
          setCheckingConflicts(false);
        }
      }
    }, 250);

    return () => {
      clearTimeout(handle);
    };
  }, [newResDate, newResStartTime, newResEndTime, newResTrainer, newResRoom, newResMember, newResType, businessId]);
  
  // Edit Request branş değiştiğinde antrenör ve oda filtrele
  useEffect(() => {
    if (editReqBranch) {
      // Antrenörleri branşa göre filtrele
      const branchFilteredTrainers = trainers.filter(t => {
        if (!t.is_active && t.status !== "active") return false;
        const trainerBranches = t.branches || t.specializations || [];
        return trainerBranches.some(b => 
          b.toLowerCase() === editReqBranch.toLowerCase() ||
          b.toLowerCase().includes(editReqBranch.toLowerCase())
        );
      });
      setEditReqFilteredTrainers(branchFilteredTrainers);
      
      // Odaları branşa göre filtrele  
      const branchFilteredRooms = rooms.filter(r => {
        if (!r.is_active) return false;
        const roomBranches = r.branches || [];
        // Oda branş kısıtı yoksa göster, varsa kontrol et
        if (roomBranches.length === 0) return true;
        return roomBranches.some(b => 
          b.toLowerCase() === editReqBranch.toLowerCase() ||
          b.toLowerCase().includes(editReqBranch.toLowerCase())
        );
      });
      setEditReqFilteredRooms(branchFilteredRooms);
      if (branchFilteredRooms.length === 1) setEditReqRoom(branchFilteredRooms[0].room_id);
      
      // Seçili antrenör artık uygun değilse temizle
      if (editReqTrainer && branchFilteredTrainers.length > 0) {
        const isTrainerValid = branchFilteredTrainers.some(t => t.trainer_id === editReqTrainer);
        if (!isTrainerValid) {
          setEditReqTrainer("");
        }
      }
      
      // Seçili oda artık uygun değilse temizle
      if (editReqRoom && branchFilteredRooms.length > 0) {
        const isRoomValid = branchFilteredRooms.some(r => r.room_id === editReqRoom);
        if (!isRoomValid) {
          setEditReqRoom("");
        }
      }
    } else {
      // Branş seçilmezse tüm aktif antrenör ve odaları göster
      setEditReqFilteredTrainers(trainers.filter(t => t.is_active || t.status === "active"));
      setEditReqFilteredRooms(rooms.filter(r => r.is_active));
    }
  }, [editReqBranch, trainers, rooms]);
  
  // Edit Request başlangıç saati değiştiğinde bitiş saatini otomatik hesapla
  useEffect(() => {
    if (editReqStartTime && editReqBranch) {
      // Branşa göre ders süresini belirle
      const branchDurations = {
        "EMS": 30,
        "Pilates": 60,
        "Yoga": 60,
        "Fitness": 60,
        "Kickbox": 60,
        "CrossFit": 60,
        "Fonksiyonel": 45,
        "Zumba": 60,
        "default": 60
      };
      
      const duration = branchDurations[editReqBranch] || branchDurations["default"];
      
      // Bitiş saatini hesapla
      const [hours, minutes] = editReqStartTime.split(":").map(Number);
      const startMinutes = hours * 60 + minutes;
      const endMinutes = startMinutes + duration;
      const endHours = Math.floor(endMinutes / 60);
      const endMins = endMinutes % 60;
      const calculatedEndTime = `${String(endHours).padStart(2, '0')}:${String(endMins).padStart(2, '0')}`;
      
      setEditReqEndTime(calculatedEndTime);
    }
  }, [editReqStartTime, editReqBranch]);

  // Edit Request Modal - Check room availability
  useEffect(() => {
    const checkEditReqRoomAvailability = async () => {
      if (!editReqRoom || !editReqDate || !editReqStartTime) {
        setEditReqRoomAvailability(null);
        return;
      }
      try {
        let url = `${API}/rooms/${editReqRoom}/availability?date=${editReqDate}&time=${editReqStartTime}`;
        if (editReqEndTime) url += `&end_time=${editReqEndTime}`;
        if (editReqBranch) url += `&branch=${encodeURIComponent(editReqBranch)}`;
        // Exclude the original request from conflict check
        if (editingRequest?.reservation_id) {
          url += `&exclude_reservation_id=${editingRequest.reservation_id}`;
        }
        const response = await availFetch("erRoom", url, editReqStartTime, editReqEndTime);
        if (!response) return;
        if (response.ok) {
          setEditReqRoomAvailability(await response.json());
        }
      } catch (error) {
        console.error("Error checking edit request room availability:", error);
      }
    };
    checkEditReqRoomAvailability();
  }, [editReqRoom, editReqDate, editReqStartTime, editReqEndTime, editReqBranch, editingRequest]);

  // Edit Request Modal - Check trainer availability
  useEffect(() => {
    const checkEditReqTrainerAvailability = async () => {
      if (!editReqTrainer || !editReqDate || !editReqStartTime) {
        setEditReqTrainerAvailability(null);
        return;
      }
      try {
        let url = `${API}/trainers/${editReqTrainer}/availability?date=${editReqDate}&time=${editReqStartTime}`;
        if (editReqEndTime) url += `&end_time=${editReqEndTime}`;
        if (editingRequest?.training_type) url += `&training_type=${encodeURIComponent(editingRequest.training_type)}`;
        if (editReqRoom) url += `&room_id=${encodeURIComponent(editReqRoom)}`;
        if (editReqBranch) url += `&branch=${encodeURIComponent(editReqBranch)}`;
        // Exclude the original request from conflict check
        if (editingRequest?.reservation_id) {
          url += `&exclude_reservation_id=${editingRequest.reservation_id}`;
        }
        const response = await availFetch("erTrainer", url, editReqStartTime, editReqEndTime);
        if (!response) return;
        if (response.ok) {
          setEditReqTrainerAvailability(await response.json());
        }
      } catch (error) {
        console.error("Error checking edit request trainer availability:", error);
      }
    };
    checkEditReqTrainerAvailability();
  }, [editReqTrainer, editReqDate, editReqStartTime, editReqEndTime, editReqRoom, editingRequest]);

  // Edit Change Request Modal - Check room availability
  useEffect(() => {
    const checkEditChangeRoomAvailability = async () => {
      if (!editChangeForm.room_id || !editChangeForm.new_date || !editChangeForm.new_start_time) {
        setEditChangeRoomAvailability(null);
        return;
      }
      try {
        let url = `${API}/rooms/${editChangeForm.room_id}/availability?date=${editChangeForm.new_date}&time=${editChangeForm.new_start_time}`;
        if (editChangeForm.new_end_time) url += `&end_time=${editChangeForm.new_end_time}`;
        if (editingChangeRequest?.branch) url += `&branch=${encodeURIComponent(editingChangeRequest.branch)}`;
        // Exclude the original reservation from conflict check
        if (editingChangeRequest?.reservation_id) {
          url += `&exclude_reservation_id=${editingChangeRequest.reservation_id}`;
        }
        const response = await availFetch("ecRoom", url, editChangeForm.new_start_time, editChangeForm.new_end_time);
        if (!response) return;
        if (response.ok) {
          setEditChangeRoomAvailability(await response.json());
        }
      } catch (error) {
        console.error("Error checking edit change room availability:", error);
      }
    };
    checkEditChangeRoomAvailability();
  }, [editChangeForm.room_id, editChangeForm.new_date, editChangeForm.new_start_time, editChangeForm.new_end_time, editingChangeRequest]);

  // Edit Change Request Modal - Check trainer availability
  useEffect(() => {
    const checkEditChangeTrainerAvailability = async () => {
      if (!editChangeForm.trainer_id || !editChangeForm.new_date || !editChangeForm.new_start_time) {
        setEditChangeTrainerAvailability(null);
        return;
      }
      try {
        let url = `${API}/trainers/${editChangeForm.trainer_id}/availability?date=${editChangeForm.new_date}&time=${editChangeForm.new_start_time}`;
        if (editChangeForm.new_end_time) url += `&end_time=${editChangeForm.new_end_time}`;
        if (editingChangeRequest?.training_type) url += `&training_type=${encodeURIComponent(editingChangeRequest.training_type)}`;
        if (editChangeForm.room_id) url += `&room_id=${encodeURIComponent(editChangeForm.room_id)}`;
        // Exclude the original reservation from conflict check
        if (editingChangeRequest?.reservation_id) {
          url += `&exclude_reservation_id=${editingChangeRequest.reservation_id}`;
        }
        const response = await availFetch("ecTrainer", url, editChangeForm.new_start_time, editChangeForm.new_end_time);
        if (!response) return;
        if (response.ok) {
          setEditChangeTrainerAvailability(await response.json());
        }
      } catch (error) {
        console.error("Error checking edit change trainer availability:", error);
      }
    };
    checkEditChangeTrainerAvailability();
  }, [editChangeForm.trainer_id, editChangeForm.new_date, editChangeForm.new_start_time, editChangeForm.new_end_time, editChangeForm.room_id, editingChangeRequest]);

  const fetchMemberPackages = async (memberId) => {
    try {
      const response = await fetch(`${API}/members/${memberId}/packages`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        // Filter: status not cancelled and has remaining sessions
        // is_active sadece false ise filtrele (undefined/null geçerli)
        const activePackages = data.filter(p => 
          p.status !== 'cancelled' && 
          p.is_active !== false && 
          (p.remaining_sessions || 0) > 0
        );
        setMemberPackages(activePackages);
        
        // Auto-select payment type based on available options
        // Priority: package > card > demo
        const memberInfo = members.find(m => m.member_id === memberId);
        const hasPackages = activePackages.length > 0;
        const hasCard = memberInfo?.member_card;
        
        const cardsList = Array.isArray(memberInfo?.member_cards) && memberInfo.member_cards.length > 0
          ? memberInfo.member_cards
          : (memberInfo?.member_card ? [memberInfo.member_card] : []);

        if (hasPackages) {
          setPaymentType("package");
        } else if (cardsList.length > 0) {
          setPaymentType("card");
          setSelectedCard(cardsList[0]);
        } else {
          setPaymentType("demo");
        }
      }
    } catch (error) {
      console.error("Error fetching member packages:", error);
    }
  };

  const tplMember = selectedMemberInfo && { ...selectedMemberInfo, recurring_template: savedTemplates[selectedMemberInfo.member_id] !== undefined ? savedTemplates[selectedMemberInfo.member_id] : selectedMemberInfo.recurring_template };
  const applyRecurringTemplate = (tpl) => {
    const base = newResDate || new Date();
    const date = [0, 1, 2, 3, 4, 5, 6].map((i) => addDays(base, i)).find((d) => tpl.days.includes(d.getDay()));
    setNewResDate(date);
    setNewResStartTime(tpl.start_time);
    setNewResEndTime(tpl.end_time);
    if (tpl.trainer_id && trainers.some((t) => t.trainer_id === tpl.trainer_id)) setNewResTrainer(tpl.trainer_id);
    if (tpl.room_id && filteredRooms.some((r) => r.room_id === tpl.room_id)) setNewResRoom(tpl.room_id);
    setRepeatEnabled(true);
    setRepeatType("weekdays");
    setRepeatDays(tpl.days.filter((d) => d !== date.getDay()));
  };
  const repeatDates = computeRepeatDates({ start: newResDate, enabled: repeatEnabled, type: repeatType, days: newResDate ? [...repeatDays, newResDate.getDay()] : repeatDays, endMode: repeatEndMode, count: repeatCount, until: repeatUntil });

  const handleCreateReservation = async (e) => {
    // Prevent any default form behavior
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Prevent double submission
    if (submittingReservation) return;
    
    setSubmittingReservation(true);
    setPageLoading(true);
    
    console.log("=== handleCreateReservation STARTED ===");
    console.log("Form data:", { 
      newResMember, 
      newResTrainer, 
      newResRoom, 
      newResStartTime, 
      newResEndTime, 
      paymentType, 
      selectedPackageId, 
      selectedCard, 
      repeatEnabled, 
      repeatCount,
      repeatType,
      newResType,
      newResBranch,
      isExternalMember,
      selectedExternalMember,
      selectedExternalPackageId
    });
    
    // Basic validation - different for external vs internal member
    if (isExternalMember) {
      if (!selectedExternalMember || !newResTrainer || !newResRoom || !newResStartTime || !newResEndTime) {
        console.log("Validation failed - missing required fields for external member");
        toast.error(t('common:fillAllFields'));
        setSubmittingReservation(false);
        setPageLoading(false);
        return;
      }
      if (!selectedExternalPackageId) {
        toast.error(t("reservations:selectPackageForExternal"));
        return;
      }
    } else {
      if (!newResMember || !newResTrainer || !newResRoom || !newResStartTime || !newResEndTime) {
        console.log("Validation failed - missing required fields");
        toast.error(t('common:fillAllFields'));
        setSubmittingReservation(false);
        setPageLoading(false);
        return;
      }
    }
    
    // Validate date is selected
    if (!newResDate) {
      toast.error(t("reservations:selectDateRequired"));
      setSubmittingReservation(false);
      setPageLoading(false);
      return;
    }
    
    // Time validation - check min advance booking
    if (!isTimeSlotValid(newResDate, newResStartTime)) {
      toast.error(`Rezervasyon en az ${getMinAdvanceText()} önceden yapılmalıdır`);
      setSubmittingReservation(false);
      setPageLoading(false);
      return;
    }
    
    // Branş validation - only required for package/card/demo payment (not external)
    if (!isExternalMember && paymentType === "package" && !newResBranch) {
      toast.error(t("reservations:branchRequiredForPackage"));
      setSubmittingReservation(false);
      setPageLoading(false);
      return;
    }
    
    // End time validation
    if (newResEndTime <= newResStartTime) {
      toast.error(t("reservations:endTimeMustBeAfterStart"));
      setSubmittingReservation(false);
      setPageLoading(false);
      return;
    }
    
    // Check business hours
    if (!isEndTimeWithinBusinessHours(newResDate, newResEndTime)) {
      const closingTime = getBusinessClosingTime(newResDate);
      toast.error(t("common:endTimeExceedsClosingShort"));
      setSubmittingReservation(false);
      setPageLoading(false);
      return;
    }
    
    // Check payment type selection (only for internal member)
    if (!isExternalMember) {
      if (paymentType === "package" && !selectedPackageId) {
        toast.error(t('common:mustSelectPackage'));
        setSubmittingReservation(false);
        setPageLoading(false);
        return;
      }
      if (paymentType === "card" && !selectedCard) {
        toast.error(t("reservations:selectCardRequired"));
        setSubmittingReservation(false);
        setPageLoading(false);
        return;
      }
      if (paymentType === "card" && !newResBranch) {
        toast.error(t("reservations:branchRequiredForCard"));
        setSubmittingReservation(false);
        setPageLoading(false);
        return;
      }
      if (paymentType === "demo" && !newResBranch) {
        toast.error(t("reservations:branchRequiredForDemo"));
        setSubmittingReservation(false);
        setPageLoading(false);
        return;
      }
    }
    
    // Check trainer availability
    if (trainerAvailability && !trainerAvailability.is_available) {
      toast.error(t('reservations:trainerNotAvailable'));
      setSubmittingReservation(false);
      setPageLoading(false);
      return;
    }
    
    // Check room availability - mk_available kullan
    if (roomAvailability) {
      if (roomAvailability.is_locked) {
        toast.error(t("reservations:roomLockedPrivate"));
        setSubmittingReservation(false);
        setPageLoading(false);
        return;
      }
      // MK (Mevcut Kapasite) kontrolü
      const mk = roomAvailability.mk_available ?? roomAvailability.available_capacity ?? 0;
      if (mk <= 0) {
        toast.error(t("reservations:roomNotAvailable"));
        setSubmittingReservation(false);
        setPageLoading(false);
        return;
      }
    }

    try {
      // Branşı al: paketten veya direkt seçimden (kart/demo için)
      let branchFromPackage = newResBranch;
      if (isExternalMember) {
        // External member - get branch from external package
        branchFromPackage = selectedExternalPackageInfo?.branches?.[0] || 
                           selectedExternalPackageInfo?.branch || 
                           selectedExternalPackageInfo?.package_branch || 
                           newResBranch;
      } else if (paymentType === "package") {
        branchFromPackage = selectedPackageInfo?.branches?.[0] || 
                           selectedPackageInfo?.branch || 
                           newResBranch;
      }
      
      // Calculate dates for recurring reservations
      const dates = repeatDates;
      if (repeatEnabled && repeatEndMode === "date" && !repeatUntil) { toast.error("Lütfen tekrar için son tarihi seçin"); return; }
      if (!dates.length) { toast.error("Seçilen günlerde tarih oluşmadı"); return; }
      if (repeatStatus.overLimit) { toast.error("Seçilen ders sayısı paketteki kalan haktan fazla"); return; }
      
      console.log("Creating reservations for dates:", dates);
      
      let successCount = 0;
      let failedDates = [];
      let lastErrorDetail = null;
      const seriesId = repeatEnabled && dates.length > 1 ? newSeriesId() : null;
      
      // Create reservations for each date
      for (const dateStr of dates) {
        // Build request body based on internal vs external member
        // Online booking kapalıysa direkt approved olarak gönder
        const effectiveStatus = businessSettings?.display_settings?.allow_online_booking === false 
          ? "approved" 
          : newResStatus;
        
        const requestBody = {
          business_id: businessId,
          trainer_id: newResTrainer,
          room_id: newResRoom,
          branch: branchFromPackage,
          date: dateStr,
          start_time: newResStartTime,
          end_time: newResEndTime,
          training_type: newResType,
          allow_shared: allowShared,
          status: effectiveStatus,
          is_recurring: repeatEnabled,
          add_to_online: addToOnline,
          series_id: seriesId,
        };
        
        if (isExternalMember) {
          // External member (Dış Üye) - member from trainer's own business
          requestBody.payment_type = "external_member";
          requestBody.member_id = selectedExternalMember; // External member ID
          requestBody.external_member_id = selectedExternalMember;
          requestBody.external_business_id = trainerOwnBusiness?.business_id;
          requestBody.external_package_id = selectedExternalPackageId;
          requestBody.member_package_id = selectedExternalPackageId;
        } else {
          // Internal member (İşletme Üyesi)
          requestBody.member_id = newResMember;
          requestBody.payment_type = paymentType;
          requestBody.member_package_id = paymentType === "package" ? selectedPackageId : null;
          requestBody.package_id = paymentType === "package" ? selectedPackageId : null;
          requestBody.card_type = paymentType === "card" ? selectedCard : null;
          requestBody.is_demo = paymentType === "demo";
        }
        
        const response = await fetch(`${API}/reservations/create`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(requestBody),
        });

        if (response.ok) {
          successCount++;
        } else {
          failedDates.push(dateStr);
          try {
            const errJson = await response.json();
            if (errJson?.detail) lastErrorDetail = errJson.detail;
          } catch (e) { /* ignore parse error */ }
        }
      }

      if (successCount > 0) {
        if (failedDates.length > 0) {
          toast.warning(`${successCount} rezervasyon oluşturuldu. ${failedDates.length} tarihte oluşturulamadı.${lastErrorDetail ? ` Sebep: ${lastErrorDetail}` : ""}`);
        } else if (successCount > 1) {
          toast.success(`${successCount} tekrarlayan rezervasyon oluşturuldu`);
        } else {
          toast.success(newResStatus === "pending" ? t("reservations:reservationCreatedPending") : t("reservations:reservationCreated"));
        }
        setShowCreateModal(false);
        resetForm();
        fetchData();
        fetchTabCounts();
        fetchPendingReservations();
      } else {
        // Show the descriptive backend reason (e.g. expired package / no sessions left)
        toast.error(lastErrorDetail || t("common:reservationFailed"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setSubmittingReservation(false);
      setPageLoading(false);
    }
  };
  
  const resetForm = () => {
    fromSlotClickRef.current = false;
    setNewResDate(null);
    setNewResMember("");
    setNewResTrainer("");
    setNewResRoom("");
    setNewResBranch("");
    setNewResStartTime("");
    setNewResEndTime("");
    setNewResType("one_on_one");
    setPaymentType("package");
    setSelectedPackageId("");
    setSelectedCard("");
    setMemberPackages([]);
    setSelectedMemberInfo(null);
    setSelectedPackageInfo(null);
    setAllowShared(true);
    setRoomAvailability(null);
    setTrainerAvailability(null);
    setNewResStatus("pending_member_approval");
    setAddToOnline(true);
    setRepeatEnabled(false);
    setRepeatDays([]);
    setRepeatEndMode("count");
    setRepeatUntil("");
    setRepeatType("weekly");
    setRepeatCount(4);
    // External member resets
    setIsExternalMember(false);
    setSelectedExternalMember("");
    setSelectedExternalPackageId("");
    setSelectedExternalPackageInfo(null);
  };

  const handleConfirm = async (resId) => {
    try {
      const response = await fetch(`${API}/reservations/${resId}/confirm`, {
        method: "PUT",
        credentials: "include",
      });
      if (response.ok) {
        toast.success(t('reservations:reservationApproved'));
        fetchData();
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };

  useEffect(() => {
    setCancelOnline(null);
    setDeleteOnlineOnCancel(true);
    const r = selectedReservationForCancel;
    if (!r?.open_class_id) return;
    fetch(`${API}/reservations/${r.reservation_id}/online-status`, { credentials: "include" })
      .then((x) => (x.ok ? x.json() : null)).then(setCancelOnline).catch(() => {});
  }, [selectedReservationForCancel]);

  // Online işareti: tek kişiyse kaldırılınca online ders de silinir; başka kişi bağlıysa kaldırılamaz
  const handleChangeOnlineToggle = async (checked) => {
    const r = selectedReservationForChange;
    if (checked || !r?.open_class_id) { setChangeAddToOnline(!!checked); return; }
    const st = await fetch(`${API}/reservations/${r.reservation_id}/online-status`, { credentials: "include" }).then((x) => x.json()).catch(() => null);
    if (st?.linked && st.others > 0) {
      toast.error(`Bu online ders ${st.others} kişiye daha bağlı olduğu için online işareti kaldırılamıyor.`);
      return;
    }
    setChangeAddToOnline(false);
    if (st?.linked) toast.info("Kaydedince bu rezervasyonun online dersi de kaldırılacak.");
  };

  // Kapsamlı İptal fonksiyonu
  const handleCancelWithModal = async () => {
    if (!selectedReservationForCancel || !cancelReasonCategory) {
      toast.error("Lütfen bir iptal nedeni seçin");
      return;
    }
    if (cancelReasonCategory === "other" && !cancelReason.trim()) {
      toast.error(t('members:cancellationReasonRequired'));
      return;
    }
    
    if (submittingCancel) return;
    setSubmittingCancel(true);
    
    try {
      const response = await fetch(`${API}/reservations/${selectedReservationForCancel.reservation_id}/cancel`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ 
          reason: cancelReasonCategory === "other"
            ? cancelReason.trim()
            : (CANCEL_REASONS.find(r => r.value === cancelReasonCategory)?.label || ""),
          reason_category: cancelReasonCategory,
          burn_session: burnSession,
          // When the staff does NOT want to burn the session, force a clean
          // normal cancellation so the automatic late-cancel penalty never
          // deducts a session from the package (business initiative).
          force: !burnSession,
          // Independent commission awards (only honored if the party earns commission)
          award_trainer_commission: awardTrainerCommission,
          award_employee_commission: awardEmployeeCommission,
          delete_online: !!(cancelOnline?.linked && cancelOnline.others === 0 && deleteOnlineOnCancel)
        }),
      });
      
      if (response.ok) {
        toast.success(burnSession ? t("reservations:lessonCancelledBurned") : t("reservations:lessonCancelledNotBurned"));
        setShowCancelModal(false);
        setSelectedReservationForCancel(null);
        setCancelReason("");
        setCancelReasonCategory("");
        setBurnSession(false);
        setAwardTrainerCommission(false);
        setAwardEmployeeCommission(false);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || t("common:cancellationFailed"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setSubmittingCancel(false);
    }
  };
  
  // Geri Alma fonksiyonu
  const handleRestoreWithModal = async () => {
    if (!selectedReservationForRestore || !restoreReason.trim()) {
      toast.error("Geri alma nedeni zorunludur");
      return;
    }
    
    try {
      const response = await fetch(`${API}/reservations/${selectedReservationForRestore.reservation_id}/restore`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ reason: restoreReason }),
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.session_restored ? "Rezervasyon geri alındı ve seans iade edildi" : "Rezervasyon geri alındı");
        setShowRestoreModal(false);
        setSelectedReservationForRestore(null);
        setRestoreReason("");
        fetchData();
        fetchTabCounts();
        fetchPendingReservations();
        fetchAllReservations();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Geri alma başarısız");
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };

  const handleCancel = async (resId, reason) => {
    try {
      const response = await fetch(`${API}/reservations/${resId}/cancel`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ reason }),
      });
      if (response.ok) {
        toast.success(t('reservations:reservationCancelled'));
        fetchData();
        fetchTabCounts();
        fetchPendingReservations();
        fetchAllReservations();
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };

  const handleBurn = async (resId, reason) => {
    try {
      const response = await fetch(`${API}/reservations/${resId}/burn`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ reason: reason || "Gelmedi" }),
      });
      if (response.ok) {
        toast.success(t("common:toast_dersYakildi"));
        fetchData();
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };
  
  // Fetch member packages for edit modal
  const fetchChangeMemberPackages = async (memberId) => {
    try {
      const response = await fetch(`${API}/members/${memberId}/packages`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        // Filter: status not cancelled and has remaining sessions
        const activePackages = data.filter(p => 
          p.status !== 'cancelled' && 
          p.is_active !== false && 
          p.remaining_sessions > 0
        );
        setChangeMemberPackages(activePackages);
      }
    } catch (error) {
      console.error("Error fetching member packages:", error);
    }
  };

  // Düzenle modalında paket değiştiğinde otomatik güncelleme
  useEffect(() => {
    if (changePackageId && changeMemberPackages.length > 0) {
      const selectedPkg = changeMemberPackages.find(p => p.member_package_id === changePackageId);
      if (selectedPkg) {
        setChangeSelectedPackageInfo(selectedPkg);
        
        // Branş otomatik ayarla — combo paketse (2+ branş) kullanıcı seçecek
        const pkgBranches = selectedPkg.package_branches && selectedPkg.package_branches.length > 0
          ? selectedPkg.package_branches
          : (selectedPkg.package_branch ? [selectedPkg.package_branch] : []);
        let branch = "";
        if (pkgBranches.length === 1) {
          branch = pkgBranches[0];
          setChangeBranch(branch);
        } else if (pkgBranches.length >= 2) {
          // Combo: keep existing branch if it's in the list; else clear
          if (changeBranch && pkgBranches.includes(changeBranch)) {
            branch = changeBranch;
          } else {
            setChangeBranch("");
          }
        }
        
        // Antrenman tipi otomatik ayarla
        const sessionType = selectedPkg.session_type || t("common:privateLessonType");
        const typeMapping = { 
          "Özel Ders": "one_on_one",
          "Düet": "duet",
          "Grup Dersi": "group",
          "Online": "one_on_one"
        };
        setChangeType(typeMapping[sessionType] || "one_on_one");
        
        // Odaları branşa göre filtrele
        if (branch) {
          const filtered = rooms.filter(room => {
            if (room.branches && room.branches.length > 0) {
              return room.branches.includes(branch);
            }
            return true;
          });
          setChangeFilteredRooms(filtered.length > 0 ? filtered : rooms);
        } else {
          setChangeFilteredRooms(rooms);
        }
        
        // Antrenörleri branşa göre filtrele - STRICT: Sadece branşı bilen antrenörler
        if (branch) {
          const filteredT = trainers.filter(t => {
            if (t.is_active === false) return false;
            // Antrenörün bu branşı bilmesi ZORUNLU
            const trainerBranches = t.branches || [];
            return trainerBranches.includes(branch);
          });
          setChangeFilteredTrainers(filteredT);
          
          // Eğer mevcut seçili antrenör bu branşı bilmiyorsa, seçimi sıfırla
          if (changeTrainer) {
            const currentTrainer = trainers.find(t => t.trainer_id === changeTrainer);
            if (currentTrainer && !(currentTrainer.branches || []).includes(branch)) {
              setChangeTrainer("");
            }
          }
        } else {
          setChangeFilteredTrainers(trainers.filter(t => t.is_active !== false));
        }
        
        // Bitiş saatini otomatik hesapla
        if (changeStartTime && selectedPkg.session_duration) {
          const duration = selectedPkg.session_duration || 55;
          const [hours, mins] = changeStartTime.split(":").map(Number);
          const startMinutes = hours * 60 + mins;
          const endMinutes = startMinutes + duration;
          const endHours = Math.floor(endMinutes / 60);
          const endMins = endMinutes % 60;
          setChangeEndTime(`${String(endHours).padStart(2, "0")}:${String(endMins).padStart(2, "0")}`);
        }
      }
    } else {
      setChangeSelectedPackageInfo(null);
      setChangeFilteredTrainers(trainers.filter(t => t.is_active !== false));
    }
  }, [changePackageId, changeMemberPackages, rooms, trainers]);

  // Düzenle modalında başlangıç saati değiştiğinde bitiş saatini güncelle
  useEffect(() => {
    if (changeStartTime && changeSelectedPackageInfo?.session_duration) {
      const duration = changeSelectedPackageInfo.session_duration;
      const [hours, mins] = changeStartTime.split(":").map(Number);
      const startMinutes = hours * 60 + mins;
      const endMinutes = startMinutes + duration;
      const endHours = Math.floor(endMinutes / 60);
      const endMins = endMinutes % 60;
      setChangeEndTime(`${String(endHours).padStart(2, "0")}:${String(endMins).padStart(2, "0")}`);
    }
  }, [changeStartTime, changeSelectedPackageInfo]);

  // Düzenle modalında oda müsaitliğini kontrol et
  useEffect(() => {
    if (changeRoom && changeStartTime && changeDate && selectedReservationForChange) {
      checkChangeRoomAvailability();
    } else {
      setChangeRoomAvailability(null);
    }
  }, [changeRoom, changeStartTime, changeEndTime, changeDate, selectedReservationForChange]);

  // Düzenle modalında branş VE antrenman tipi değiştiğinde odaları filtrele
  useEffect(() => {
    let filtered = rooms;
    
    // Filter by branch
    if (changeBranch) {
      filtered = filtered.filter(room => {
        if (room.branches && room.branches.length > 0) {
          return room.branches.includes(changeBranch);
        }
        return true;
      });
    }
    
    // Filter by training type (changeType)
    if (changeType) {
      // Map changeType to actual training type name
      const trainingTypeMap = {
        "one_on_one": "Birebir",
        "duet": "Düet",
        "group": "Grup Dersi",
        "Özel Ders": "Birebir"
      };
      const trainingTypeName = trainingTypeMap[changeType] || changeType;
      
      const byBranch = filtered;
      filtered = filtered.filter(room => {
        if (room.training_types && room.training_types.length > 0) {
          return room.training_types.includes(trainingTypeName) || room.training_types.includes(changeType) || (changeType === "one_on_one" && room.training_types.includes("Özel Ders"));
        }
        return true;
      });
      if (filtered.length === 0) filtered = byBranch;
    }
    
    setChangeFilteredRooms(filtered);
    
    // Açılışta mevcut oda korunur; kullanıcı branşı değiştirince tek oda varsa otomatik, birden fazlaysa seçim kullanıcıda
    const userChanged = !!prevChangeBranchRef.current && prevChangeBranchRef.current !== changeBranch;
    prevChangeBranchRef.current = changeBranch;
    if (changeBranch && filtered.length === 1) {
      if (changeRoom !== filtered[0].room_id) { setChangeRoom(filtered[0].room_id); setChangeRoomAvailability(null); }
    } else if (changeRoom && filtered.length > 0 && (!filtered.find(r => r.room_id === changeRoom) || (userChanged && changeBranch))) {
      setChangeRoom("");
      setChangeRoomAvailability(null);
    }
  }, [changeBranch, changeType, rooms]);

  // Düzenle modalında antrenör müsaitliğini kontrol et
  useEffect(() => {
    if (changeTrainer && changeStartTime && changeDate && selectedReservationForChange) {
      checkChangeTrainerAvailability();
    } else {
      setChangeTrainerAvailability(null);
    }
  }, [changeTrainer, changeStartTime, changeEndTime, changeDate, selectedReservationForChange]);

  const checkChangeRoomAvailability = async () => {
    try {
      let url = `${API}/rooms/${changeRoom}/availability?date=${changeDate}&time=${changeStartTime}`;
      if (changeEndTime) {
        url += `&end_time=${changeEndTime}`;
      }
      // Mevcut rezervasyonu hariç tut
      if (selectedReservationForChange?.reservation_id) {
        url += `&exclude_reservation_id=${selectedReservationForChange.reservation_id}`;
      }
      const response = await availFetch("chRoom", url, changeStartTime, changeEndTime);
        if (!response) return;
      if (response.ok) {
        const data = await response.json();
        setChangeRoomAvailability(data);
      }
    } catch (error) {
      console.error("Error checking room availability:", error);
    }
  };

  const checkChangeTrainerAvailability = async () => {
    try {
      let url = `${API}/trainers/${changeTrainer}/availability?date=${changeDate}&time=${changeStartTime}`;
      if (changeEndTime) {
        url += `&end_time=${changeEndTime}`;
      }
      // Mevcut rezervasyonu hariç tut
      if (selectedReservationForChange?.reservation_id) {
        url += `&exclude_reservation_id=${selectedReservationForChange.reservation_id}`;
      }
      // Add training_type for multi-participant lesson support (duet, group)
      if (changeType) {
        url += `&training_type=${encodeURIComponent(changeType)}`;
      }
      // Add room_id to check room-specific conflicts
      if (changeRoom) {
        url += `&room_id=${encodeURIComponent(changeRoom)}`;
        if (changeBranch) url += `&branch=${encodeURIComponent(changeBranch)}`;
      }
      const response = await availFetch("chTrainer", url, changeStartTime, changeEndTime);
        if (!response) return;
      if (response.ok) {
        const data = await response.json();
        setChangeTrainerAvailability(data);
      }
    } catch (error) {
      console.error("Error checking trainer availability:", error);
    }
  };

  // Talep oluşturma için üye paketlerini yükle
  const fetchRequestMemberPackages = async (memberId) => {
    try {
      const response = await fetch(`${API}/members/${memberId}/packages`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        const activePackages = data.filter(p => 
          p.status !== 'cancelled' && 
          p.is_active !== false && 
          p.remaining_sessions > 0
        );
        setRequestMemberPackages(activePackages);
      }
    } catch (error) {
      console.error("Error fetching member packages:", error);
    }
  };

  // Talep oluşturma - paket değişince otomatik güncelleme
  useEffect(() => {
    if (requestPackageId && requestMemberPackages.length > 0) {
      const selectedPkg = requestMemberPackages.find(p => p.member_package_id === requestPackageId);
      if (selectedPkg) {
        setRequestSelectedPackageInfo(selectedPkg);
        
        // Branş otomatik ayarla
        const branch = selectedPkg.package_branches?.[0] || selectedPkg.package_branch || "";
        setRequestBranch(branch);
        
        // Antrenman tipi otomatik ayarla
        const sessionType = selectedPkg.session_type || t("common:privateLessonType");
        const typeMapping = { 
          "Özel Ders": "one_on_one",
          "Düet": "duet",
          "Grup Dersi": "group",
          "Online": "one_on_one"
        };
        setRequestType(typeMapping[sessionType] || "one_on_one");
        
        // Odaları branşa göre filtrele
        if (branch) {
          const filtered = rooms.filter(room => {
            if (room.branches && room.branches.length > 0) {
              return room.branches.includes(branch);
            }
            return true;
          });
          setRequestFilteredRooms(filtered.length > 0 ? filtered : rooms);
        } else {
          setRequestFilteredRooms(rooms);
        }
        
        // Antrenörleri branşa göre filtrele - STRICT: Sadece branşı bilen antrenörler
        if (branch) {
          const filteredT = trainers.filter(t => {
            if (t.is_active === false) return false;
            // Antrenörün bu branşı bilmesi ZORUNLU
            const trainerBranches = t.branches || [];
            return trainerBranches.includes(branch);
          });
          setRequestFilteredTrainers(filteredT);
          
          // Eğer mevcut seçili antrenör bu branşı bilmiyorsa, seçimi sıfırla
          if (requestTrainer) {
            const currentTrainer = trainers.find(t => t.trainer_id === requestTrainer);
            if (currentTrainer && !(currentTrainer.branches || []).includes(branch)) {
              setRequestTrainer("");
            }
          }
        } else {
          setRequestFilteredTrainers(trainers.filter(t => t.is_active !== false));
        }
        
        // Bitiş saatini otomatik hesapla
        if (requestStartTime && selectedPkg.session_duration) {
          const duration = selectedPkg.session_duration || 55;
          const [hours, mins] = requestStartTime.split(":").map(Number);
          const startMinutes = hours * 60 + mins;
          const endMinutes = startMinutes + duration;
          const endHours = Math.floor(endMinutes / 60);
          const endMins = endMinutes % 60;
          setRequestEndTime(`${String(endHours).padStart(2, "0")}:${String(endMins).padStart(2, "0")}`);
        }
      }
    } else {
      setRequestSelectedPackageInfo(null);
      setRequestFilteredRooms(rooms);
      setRequestFilteredTrainers(trainers.filter(t => t.is_active !== false));
    }
  }, [requestPackageId, requestMemberPackages, rooms, trainers, requestStartTime]);

  // Talep oluşturma - başlangıç saati değişince bitiş saatini güncelle
  useEffect(() => {
    if (requestStartTime && requestSelectedPackageInfo?.session_duration) {
      const duration = requestSelectedPackageInfo.session_duration;
      const [hours, mins] = requestStartTime.split(":").map(Number);
      const startMinutes = hours * 60 + mins;
      const endMinutes = startMinutes + duration;
      const endHours = Math.floor(endMinutes / 60);
      const endMins = endMinutes % 60;
      setRequestEndTime(`${String(endHours).padStart(2, "0")}:${String(endMins).padStart(2, "0")}`);
    }
  }, [requestStartTime, requestSelectedPackageInfo]);

  // Talep oluşturma - oda müsaitliğini kontrol et
  useEffect(() => {
    if (requestRoom && requestStartTime && selectedDate) {
      checkRequestRoomAvailability();
    } else {
      setRequestRoomAvailability(null);
    }
  }, [requestRoom, requestStartTime, requestEndTime, selectedDate]);

  // Talep oluşturma - antrenör müsaitliğini kontrol et
  useEffect(() => {
    if (requestTrainer && requestStartTime && selectedDate) {
      checkRequestTrainerAvailability();
    } else {
      setRequestTrainerAvailability(null);
    }
  }, [requestTrainer, requestStartTime, requestEndTime, selectedDate]);

  const checkRequestRoomAvailability = async () => {
    try {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      let url = `${API}/rooms/${requestRoom}/availability?date=${dateStr}&time=${requestStartTime}`;
      if (requestEndTime) {
        url += `&end_time=${requestEndTime}`;
      }
      const response = await availFetch("rqRoom", url, requestStartTime, requestEndTime);
        if (!response) return;
      if (response.ok) {
        const data = await response.json();
        setRequestRoomAvailability(data);
      }
    } catch (error) {
      console.error("Error checking room availability:", error);
    }
  };

  const checkRequestTrainerAvailability = async () => {
    try {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      let url = `${API}/trainers/${requestTrainer}/availability?date=${dateStr}&time=${requestStartTime}`;
      if (requestEndTime) {
        url += `&end_time=${requestEndTime}`;
      }
      const response = await availFetch("rqTrainer", url, requestStartTime, requestEndTime);
        if (!response) return;
      if (response.ok) {
        const data = await response.json();
        setRequestTrainerAvailability(data);
      }
    } catch (error) {
      console.error("Error checking trainer availability:", error);
    }
  };

  // Talep oluşturma işlemi
  const handleCreateRequest = async () => {
    if (!requestMember || !requestPackageId || !requestTrainer || !requestRoom || !requestStartTime) {
      toast.error(t('common:fillAllFields'));
      return;
    }
    
    // Prevent double-click
    if (submittingRequest) return;
    
    // Kapanış saati kontrolü
    if (!isEndTimeWithinBusinessHours(selectedDate, requestEndTime)) {
      const closingTime = getBusinessClosingTime(selectedDate);
      toast.error(t("common:endTimeExceedsClosingShort"));
      return;
    }
    
    // Min advance booking kontrolü
    if (!isTimeSlotValid(selectedDate, requestStartTime)) {
      toast.error(`Rezervasyon en az ${getMinAdvanceText()} önceden yapılmalıdır`);
      return;
    }
    
    // Müsaitlik kontrolü
    if (requestTrainerAvailability && !requestTrainerAvailability.is_available) {
      toast.error(t('reservations:trainerNotAvailable'));
      return;
    }
    
    if (requestRoomAvailability && requestRoomAvailability.is_locked) {
      toast.error("Bu oda bu saatte kilitli");
      return;
    }
    
    setSubmittingRequest(true);
    try {
      const response = await fetch(`${API}/reservation-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          business_id: businessId,
          member_id: requestMember,
          member_package_id: requestPackageId,
          trainer_id: requestTrainer,
          room_id: requestRoom,
          branch: requestBranch,
          date: format(selectedDate, "yyyy-MM-dd"),
          start_time: requestStartTime,
          end_time: requestEndTime,
          training_type: requestType,
          allow_shared: requestAllowShared,
          notes: requestNotes,
        }),
      });
      
      if (response.ok) {
        toast.success(t("common:toast_rezervasyonTalebiOlusturuldu"));
        setShowRequestModal(false);
        resetRequestForm();
        fetchReservationRequests();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Talep oluşturulamadı");
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setSubmittingRequest(false);
    }
  };

  const resetRequestForm = () => {
    setRequestMember("");
    setRequestPackageId("");
    setRequestTrainer("");
    setRequestRoom("");
    setRequestStartTime("");
    setRequestEndTime("");
    setRequestMemberPackages([]);
    setRequestSelectedPackageInfo(null);
    setRequestFilteredRooms([]);
    setRequestFilteredTrainers([]);
    setRequestRoomAvailability(null);
    setRequestTrainerAvailability(null);
    setRequestBranch("");
    setRequestType("one_on_one");
    setRequestAllowShared(true);
    setRequestNotes("");
  };

  // Talep onaylama
  // YENİ SİSTEM: İşletme üyeden gelen talebi onaylar
  const handleApproveRequest = async (reservationId) => {
    if (processingReservationId === reservationId) return;
    setProcessingReservationId(reservationId);
    
    try {
      const response = await fetch(`${API}/reservations/${reservationId}/business-approve`, {
        method: "PUT",
        credentials: "include",
      });
      
      if (response.ok) {
        toast.success(t('common:requestApproved'));
        fetchReservationRequests();
        fetchTabCounts();
        fetchData();
      } else {
        const error = await response.json();
        const errorMsg = typeof error.detail === 'string' 
          ? error.detail 
          : Array.isArray(error.detail) 
            ? error.detail.map(e => e.msg || e).join(', ')
            : error.detail?.msg || "Onaylama başarısız";
        toast.error(errorMsg);
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setProcessingReservationId(null);
    }
  };

  // YENİ SİSTEM: İşletme üyeden gelen talebi reddeder
  const handleRejectRequest = async () => {
    if (!selectedRequestForReject || !rejectReason.trim()) {
      toast.error("Red nedeni zorunludur");
      return;
    }
    
    if (processingReservationId === selectedRequestForReject.reservation_id) return;
    setProcessingReservationId(selectedRequestForReject.reservation_id);
    
    try {
      const response = await fetch(`${API}/reservations/${selectedRequestForReject.reservation_id}/business-reject`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ reason: rejectReason }),
      });
      
      if (response.ok) {
        toast.success(t('common:requestRejected'));
        setShowRejectModal(false);
        setSelectedRequestForReject(null);
        setRejectReason("");
        fetchReservationRequests();
        fetchTabCounts();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Reddetme başarısız");
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setProcessingReservationId(null);
    }
  };

  // YENİ SİSTEM: İşletme üyeden gelen değişiklik talebini onaylar
  const handleApproveChangeRequest = async (reservationId) => {
    if (processingReservationId === reservationId) return;
    setProcessingReservationId(reservationId);
    
    try {
      const response = await fetch(`${API}/reservations/${reservationId}/business-approve`, {
        method: "PUT",
        credentials: "include",
      });
      
      if (response.ok) {
        toast.success(t("admin:toast_degisiklikOnaylandi"));
        fetchChangeRequests();
        fetchTabCounts();
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Onaylama başarısız");
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setProcessingReservationId(null);
    }
  };

  // YENİ SİSTEM: İşletme üyeden gelen değişiklik talebini reddeder
  const handleRejectChangeRequest = async () => {
    if (!selectedChangeForReject || !rejectChangeReason.trim()) {
      toast.error(t("admin:toast_lutfenRedNedeniGirin"));
      return;
    }
    
    if (processingReservationId === selectedChangeForReject.change_id) return;
    setProcessingReservationId(selectedChangeForReject.change_id);
    
    try {
      const response = await fetch(`${API}/reservation-changes/${selectedChangeForReject.change_id}/reject`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ reason: rejectChangeReason }),
      });
      
      if (response.ok) {
        toast.success(t("admin:toast_degisiklikReddedildi"));
        setShowRejectChangeModal(false);
        setSelectedChangeForReject(null);
        setRejectChangeReason("");
        fetchChangeRequests();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Reddetme başarısız");
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setProcessingReservationId(null);
    }
  };

  // İşletme değişiklik talebini düzenler - üyeye gönderilir
  const handleEditChangeSubmit = async () => {
    if (!editingChangeRequest || submittingEditChange) return;
    
    // change_requests artık reservations koleksiyonundan geliyor
    // Bu yüzden reservation_id kullanıyoruz
    const requestId = editingChangeRequest.reservation_id || editingChangeRequest.change_id;
    
    if (!requestId) {
      toast.error(t("common:toast_degisiklikTalebiBulunamadi"));
      return;
    }
    
    setSubmittingEditChange(true);
    setPageLoading(true);
    try {
      const response = await fetch(`${API}/reservations/${requestId}/business-change`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          date: editChangeForm.new_date,
          start_time: editChangeForm.new_start_time,
          end_time: editChangeForm.new_end_time,
          trainer_id: editChangeForm.trainer_id,
          room_id: editChangeForm.room_id,
          notes: editChangeForm.reason
        }),
      });
      
      if (response.ok) {
        toast.success(t("common:toast_degisiklikUyeyeGonderildi"));
        setShowEditChangeModal(false);
        setEditingChangeRequest(null);
        setEditChangeForm({ new_date: "", new_start_time: "", new_end_time: "", trainer_id: "", room_id: "", reason: "" });
        fetchAllData(); // Tüm veriyi yenile
      } else {
        const error = await response.json();
        toast.error(error.detail || t('settings:updateFailed'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setSubmittingEditChange(false);
      setPageLoading(false);
    }
  };

  const getStatusBadge = (status, reservation = null) => {
    const statusMap = {
      pending: { label: t('common:pending'), variant: "outline", className: "border-orange-500 text-orange-600 bg-orange-50" },
      pending_member_approval: { label: t('admin:pendingMemberApproval'), variant: "outline", className: "border-amber-500 text-amber-600 bg-amber-50" },
      pending_business_approval: { label: t('admin:pendingBusinessApproval'), variant: "outline", className: "border-amber-500 text-amber-600 bg-amber-50" },
      change_pending_member: { label: t('admin:changePendingMember'), variant: "outline", className: "border-purple-500 text-purple-600 bg-purple-50" },
      change_pending_business: { label: t('admin:changePendingBusiness'), variant: "outline", className: "border-purple-500 text-purple-600 bg-purple-50" },
      approved: { label: t('admin:approved'), variant: "default", className: "bg-green-600" },
      confirmed: { label: t('admin:approved'), variant: "default", className: "bg-green-600" },
      completed: { label: t('common:completed'), variant: "default", className: "bg-blue-600" },
      cancelled: { label: t('common:cancel'), variant: "destructive", className: "" },
      cancelled_late: { label: t('reservations:lateCancellation'), variant: "destructive", className: "" },
      burned: { label: t('common:burned'), variant: "destructive", className: "bg-red-800" },
      modified_by_business: { label: t('common:sentToMember'), variant: "outline", className: "border-purple-500 text-purple-600 bg-purple-50" },
      modified_by_member: { label: t('common:memberChange'), variant: "outline", className: "border-blue-500 text-blue-600 bg-blue-50" },
      approved_by_member: { label: t('admin:memberApproved'), variant: "outline", className: "border-green-500 text-green-600 bg-green-50" },
    };
    
    // Special handling for late cancellation (is_late_cancel field)
    if (status === "cancelled" && reservation?.is_late_cancel) {
      return <Badge variant="destructive">{t("common:gecİptal")}</Badge>;
    }
    
    const s = statusMap[status] || { label: status || t('common:unknownStatus'), variant: "outline", className: "border-gray-500 text-gray-600 bg-gray-50" };
    return <Badge variant={s.variant} className={s.className}>{s.label}</Badge>;
  };

  // Complete a reservation manually
  // Manuel tamamlama için izin kontrolü
  // İşletme sahibi veya (yönetici + manual_reservation_complete izni) her zaman tamamlayabilir
  // Diğerleri sadece ders saatine "min_advance_booking" süresi kaldığında tamamlayabilir
  const canManualComplete = (reservation) => {
    // İşletme sahibi her zaman tamamlayabilir
    if (currentUser?.role === "business") {
      return { allowed: true };
    }
    
    // Yönetici + izin varsa her zaman tamamlayabilir
    const isManager = userPermissions?.is_manager || userPermissions?.is_owner;
    const hasManualCompletePermission = userPermissions?.permissions?.manual_reservation_complete;
    
    if (isManager && hasManualCompletePermission) {
      return { allowed: true };
    }
    
    // Diğerleri için zaman kontrolü yap - tamamlama için ayrı ayar kullan
    const minAdvanceHours = businessSettings?.reservation_settings?.min_advance_complete ?? 
                           businessSettings?.min_advance_complete ?? 0.5;
    
    // Min süreyi dakikaya çevir (görüntüleme için)
    const minAdvanceMinutes = Math.round(minAdvanceHours * 60);
    const minAdvanceText = minAdvanceMinutes >= 60 
      ? `${Math.floor(minAdvanceMinutes / 60)} saat${minAdvanceMinutes % 60 > 0 ? ` ${minAdvanceMinutes % 60} dakika` : ''}`
      : `${minAdvanceMinutes} dakika`;
    
    // Rezervasyon tarih ve saatini birleştir
    const [year, month, day] = reservation.date.split("-").map(Number);
    const [hours, minutes] = (reservation.start_time || reservation.time).split(":").map(Number);
    const reservationDateTime = new Date(year, month - 1, day, hours, minutes);
    
    // Min. süre kadar önce = tamamlanabilir zaman
    const completeableTime = new Date(reservationDateTime.getTime() - (minAdvanceHours * 60 * 60 * 1000));
    const now = new Date();
    
    if (now >= completeableTime) {
      return { allowed: true };
    }
    
    // Henüz tamamlanamaz
    const diffMs = completeableTime.getTime() - now.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    let timeText = "";
    if (diffHours > 24) {
      const diffDays = Math.floor(diffHours / 24);
      timeText = `${diffDays} gün ${diffHours % 24} saat`;
    } else if (diffHours > 0) {
      timeText = `${diffHours} saat ${diffMinutes} dakika`;
    } else {
      timeText = `${diffMinutes} dakika`;
    }
    
    return { 
      allowed: false, 
      reason: `Ders saatine ${minAdvanceText} kala tamamlanabilir. Kalan süre: ${timeText}`,
      completeableTime 
    };
  };

  // Lesson note edit permission - sadece owner veya manager + lesson_note_edit
  const canEditLessonNote = () => {
    if (currentUser?.role === "business" || userPermissions?.is_owner) return true;
    if (!userPermissions?.is_manager) return false;
    // Manager: check permission key (default True for backward compatibility)
    return userPermissions?.permissions?.lesson_note_edit !== false;
  };
  
  const openLessonNoteModal = (reservation, mode = "edit") => {
    setLessonNoteTarget(reservation);
    setLessonNoteMode(mode);
    setLessonNoteText(reservation?.lesson_note || "");
    setShowLessonNoteModal(true);
  };
  
  const handleCompleteReservation = async (reservationId, reservation) => {
    // Çift tıklama koruması
    if (completingReservationId === reservationId) {
      return;
    }
    
    // Önce izin kontrolü
    const canComplete = canManualComplete(reservation);
    if (!canComplete.allowed) {
      toast.error(canComplete.reason);
      return;
    }
    
    // İzinli kullanıcı ders notu girebiliyorsa, önce not modal'ını aç
    if (canEditLessonNote()) {
      openLessonNoteModal(reservation, "complete");
      return;
    }
    
    // İzin yoksa eski davranış: direkt tamamla
    await performCompleteReservation(reservationId, "");
  };
  
  const performCompleteReservation = async (reservationId, lessonNote) => {
    setCompletingReservationId(reservationId);
    try {
      const response = await fetch(`${API}/reservations/${reservationId}/complete-manual`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ lesson_note: lessonNote || "" }),
      });
      
      if (response.ok) {
        toast.success(t("packages:toast_dersTamamlandiVePakettenDusuldu"));
        fetchData();
        return true;
      } else {
        const error = await response.json();
        toast.error(error.detail || t('common:errorOccurred'));
        return false;
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
      return false;
    } finally {
      setCompletingReservationId(null);
    }
  };
  
  const submitLessonNoteModal = async () => {
    if (!lessonNoteTarget) return;
    if (savingLessonNote) return;
    setSavingLessonNote(true);
    try {
      const note = (lessonNoteText || "").trim();
      if (lessonNoteMode === "complete") {
        // Önce tamamla (notu ile birlikte)
        const ok = await performCompleteReservation(lessonNoteTarget.reservation_id, note);
        if (ok) setShowLessonNoteModal(false);
      } else {
        // Sadece not güncelle (mevcut ders için)
        const response = await fetch(`${API}/reservations/${lessonNoteTarget.reservation_id}/lesson-note`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ lesson_note: note }),
        });
        if (response.ok) {
          toast.success(t("reservations:lessonNoteSaved", "Ders açıklaması kaydedildi"));
          fetchData();
          setShowLessonNoteModal(false);
        } else {
          const err = await response.json().catch(() => ({}));
          toast.error(err.detail || t('common:errorOccurred'));
        }
      }
    } finally {
      setSavingLessonNote(false);
    }
  };

  const getTrainingTypeLabel = (type) => {
    // Map legacy keys to DB name, then pass through localization
    const legacyMap = {
      one_on_one: "Özel Ders",
      duet: "Düet",
      group: "Grup Dersi",
    };
    const canonicalName = legacyMap[type] || type;
    return getLocalizedTrainingType(canonicalName);
  };

  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 8; hour <= 22; hour++) {
      for (let minute = 0; minute < 60; minute += 5) {
        if (hour === 22 && minute > 0) break; // 22:00'dan sonra ekleme
        slots.push(`${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`);
      }
    }
    return slots;
  };

  // 08:00'dan 22:00'a kadar 5 dakika aralıklarla saat slotları
  const timeSlots = [];
  for (let hour = 8; hour <= 22; hour++) {
    for (let minute = 0; minute < 60; minute += 5) {
      if (hour === 22 && minute > 0) break; // 22:00'dan sonra ekleme
      timeSlots.push(`${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`);
    }
  }

  const matchesFilters = (r) => {
    if (onlineOnly && !r.open_class_id) return false;
    if (selectedTrainer && selectedTrainer !== "all" && r.trainer_id !== selectedTrainer) return false;
    if (selectedRoom && selectedRoom !== "all" && r.room_id !== selectedRoom) return false;
    if (selectedMemberFilter && selectedMemberFilter !== "all" && r.member_id !== selectedMemberFilter) return false;
    
    // Status filter with special handling
    if (statusFilter && statusFilter !== "all") {
      if (statusFilter === "cancelled_late") {
        // Show only late cancelled (is_late_cancel: true)
        if (r.status !== "cancelled" || !r.is_late_cancel) return false;
      } else if (statusFilter === "cancelled") {
        // Show only normal cancelled (not late, not burned)
        if (r.status !== "cancelled" || r.is_late_cancel) return false;
      } else if (statusFilter === "approved") {
        // "Onaylandı" = approved OR confirmed (both mean the same)
        if (r.status !== "approved" && r.status !== "confirmed") return false;
      } else {
        // Normal status filter
        if (r.status !== statusFilter) return false;
      }
    }
    return true;
  };
  const filteredReservations = reservations.filter(matchesFilters);
  
  // Filtered requests by date and filters
  // Filtered requests - show ALL pending regardless of date
  // Business owner needs to see all pending requests to manage them
  const getFilteredRequests = () => {
    return reservationRequests.filter((r) => {
      // Don't filter by date - show all pending requests
      // Diğer filtreler
      if (selectedTrainer && selectedTrainer !== "all" && r.trainer_id !== selectedTrainer) return false;
      if (selectedRoom && selectedRoom !== "all" && r.room_id !== selectedRoom) return false;
      if (selectedMemberFilter && selectedMemberFilter !== "all" && r.member_id !== selectedMemberFilter) return false;
      return true;
    });
  };
  
  // Filtered waitlist by date and filters
  const getFilteredWaitlist = () => {
    return waitlist.filter((r) => {
      // Tarih filtresi
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      if (viewMode === "day" && r.date !== dateStr) return false;
      if (viewMode === "week") {
        const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 });
        const weekEnd = addDays(weekStart, 6);
        const reqDate = new Date(r.date);
        if (reqDate < weekStart || reqDate > weekEnd) return false;
      }
      if (viewMode === "month") {
        const monthStart = startOfMonth(selectedDate);
        const monthEnd = endOfMonth(selectedDate);
        const reqDate = new Date(r.date);
        if (reqDate < monthStart || reqDate > monthEnd) return false;
      }
      // Diğer filtreler
      if (selectedTrainer && selectedTrainer !== "all" && r.trainer_id !== selectedTrainer) return false;
      if (selectedRoom && selectedRoom !== "all" && r.room_id !== selectedRoom) return false;
      if (selectedMemberFilter && selectedMemberFilter !== "all" && r.member_id !== selectedMemberFilter) return false;
      return true;
    });
  };
  
  // Filtered change requests - show ALL pending regardless of date
  // Business owner needs to see all pending requests to manage them
  const getFilteredChangeRequests = () => {
    return changeRequests.filter((r) => {
      // Don't filter by date - show all pending change requests
      // Diğer filtreler
      if (selectedTrainer && selectedTrainer !== "all" && r.new_trainer_id !== selectedTrainer && r.original_trainer_id !== selectedTrainer) return false;
      if (selectedMemberFilter && selectedMemberFilter !== "all" && r.member_id !== selectedMemberFilter) return false;
      return true;
    });
  };
  
  // Count pending reservations for badge
  const pendingCount = reservations.filter(r => r.status === "pending").length;
  
  // Day-view reservation list (all statuses, filtered by selected date + filters, sorted sabahtan akşama)
  const getDayViewList = () => {
    const dateStr = format(selectedDate, "yyyy-MM-dd");
    return allReservations.filter(r => {
      if (r.date !== dateStr) return false;
      return matchesFilters(r);
    }).sort((a, b) => {
      const ta = a.start_time || a.time || "";
      const tb = b.start_time || b.time || "";
      return ta.localeCompare(tb);
    });
  };

  // Get reservations for a specific date
  const getReservationsForDate = (date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return allReservations.filter(r => r.date === dateStr && matchesFilters(r));
  };
  
  // Get week days
  const getWeekDays = () => {
    const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 }); // Monday
    return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  };
  
  // Get month days
  const getMonthDays = () => {
    const monthStart = startOfMonth(selectedDate);
    const monthEnd = endOfMonth(selectedDate);
    return eachDayOfInterval({ start: monthStart, end: monthEnd });
  };

  // Calculate available time slots for the day
  const getAvailableSlots = () => {
    const dateStr = format(selectedDate, "yyyy-MM-dd");
    const dayOfWeek = selectedDate.getDay(); // 0=Sunday, 1=Monday, etc.
    // IMPORTANT: Use allReservations instead of reservations to include pending statuses
    // This ensures that slots with pending_member_approval, pending_business_approval etc. are blocked
    const dayReservations = allReservations.filter(r => r.date === dateStr && !["cancelled", "cancelled_late", "burned"].includes(r.status));
    
    // Day names for business working hours (0=Pazar, 1=Pazartesi, etc.)
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayName = dayNames[dayOfWeek];
    
    // Helper to convert time to minutes
    const timeToMins = (t) => {
      if (!t) return 0;
      const [h, m] = t.split(':').map(Number);
      return h * 60 + m;
    };
    
    // Helper to check if slot overlaps with a time range
    const overlaps = (slotStart, slotEnd, rangeStart, rangeEnd) => {
      const slotStartMins = timeToMins(slotStart);
      const slotEndMins = timeToMins(slotEnd);
      const rangeStartMins = timeToMins(rangeStart);
      const rangeEndMins = timeToMins(rangeEnd);
      return !(slotEndMins <= rangeStartMins || slotStartMins >= rangeEndMins);
    };
    
    // Get business working hours for this day
    const businessDay = businessWorkingHours[dayName];
    const businessOpen = businessDay?.open || "08:00";
    const businessClose = businessDay?.close || "22:00";
    const businessClosed = businessDay?.closed === true;
    
    // If business is closed on this day, no slots
    if (businessClosed) {
      return [];
    }
    
    // For each trainer, find available slots
    const availableSlots = [];
    
    trainers.filter(t => t.status === "active").forEach(trainer => {
      // Get trainer's schedules (all days)
      const allSchedules = trainerSchedules[trainer.trainer_id] || [];
      const hasAnySchedule = allSchedules.length > 0; // Does trainer have any schedule defined?
      
      // Get trainer's schedule for THIS day
      const daySchedule = allSchedules.find(s => s.day_of_week === dayOfWeek && s.is_active !== false);
      
      let workStart, workEnd, breaks;
      
      if (hasAnySchedule) {
        // Trainer has schedule defined - only work on days they defined
        if (!daySchedule) {
          // Trainer has schedules but NOT for this day = doesn't work today
          return; // Skip this trainer
        }
        workStart = timeToMins(daySchedule.start_time);
        workEnd = timeToMins(daySchedule.end_time);
        breaks = daySchedule.breaks || [];
      } else {
        // Trainer has NO schedule at all - use business hours
        workStart = timeToMins(businessOpen);
        workEnd = timeToMins(businessClose);
        breaks = [];
      }
      
      // Get trainer's time blocks (appointments, etc.)
      const timeBlocks = trainerTimeBlocks[trainer.trainer_id] || [];
      const dayBlocks = timeBlocks.filter(b => b.date === dateStr);
      
      for (let mins = workStart; mins < workEnd; mins += 60) {
        const slotStart = `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;
        const slotEnd = `${String(Math.floor((mins + 60) / 60)).padStart(2, '0')}:${String((mins + 60) % 60).padStart(2, '0')}`;
        
        // Skip if outside working hours
        if (mins + 60 > workEnd) continue;
        
        // Check if slot is during a break
        const isDuringBreak = breaks.some(brk => 
          overlaps(slotStart, slotEnd, brk.start_time, brk.end_time)
        );
        if (isDuringBreak) continue;
        
        // Check if slot is blocked (time block)
        const isBlocked = dayBlocks.some(block => {
          if (block.all_day) return true;
          return overlaps(slotStart, slotEnd, block.start_time, block.end_time);
        });
        if (isBlocked) continue;
        
        // Check if trainer already has a reservation
        const trainerBusy = dayReservations.some(res => {
          if (res.trainer_id !== trainer.trainer_id) return false;
          const resStart = res.start_time || res.time;
          const resEnd = res.end_time || slotEnd;
          return overlaps(slotStart, slotEnd, resStart, resEnd);
        });
        if (trainerBusy) continue;
        
        // Now check each room
        rooms.filter(r => r.is_active !== false).forEach(room => {
          // Check if room is occupied
          const roomBusy = dayReservations.some(res => {
            if (res.room_id !== room.room_id) return false;
            const resStart = res.start_time || res.time;
            const resEnd = res.end_time || slotEnd;
            return overlaps(slotStart, slotEnd, resStart, resEnd);
          });
          
          if (!roomBusy) {
            availableSlots.push({
              trainer_id: trainer.trainer_id,
              trainer_name: `${trainer.name} ${trainer.surname || ''}`.trim(),
              room_id: room.room_id,
              room_name: room.name,
              start: slotStart,
              end: slotEnd
            });
          }
        });
      }
    });
    
    // Sort by time, then by trainer name
    availableSlots.sort((a, b) => {
      const timeCompare = timeToMins(a.start) - timeToMins(b.start);
      if (timeCompare !== 0) return timeCompare;
      return a.trainer_name.localeCompare(b.trainer_name);
    });
    
    // Filter based on selected trainer/room
    return availableSlots.filter(slot => {
      if (selectedTrainer && selectedTrainer !== "all" && slot.trainer_id !== selectedTrainer) return false;
      if (selectedRoom && selectedRoom !== "all" && slot.room_id !== selectedRoom) return false;
      return true;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Full Page Loading Overlay */}
      {pageLoading && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center">
          <div className="bg-white rounded-2xl p-8 shadow-2xl flex flex-col items-center gap-4">
            <Loader2 className="w-12 h-12 text-navy animate-spin" />
            <p className="text-navy font-medium">İşlem yapılıyor...</p>
          </div>
        </div>
      )}
      
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="font-heading font-bold text-xl text-navy">{t('common:reservations')}</h2>
          <p className="text-grey text-sm">
            {format(selectedDate, "dd/MM/yyyy, EEEE", { locale: getDateLocale() })}
          </p>
        </div>

        <div className="flex items-center gap-3 flex-wrap overflow-x-auto pb-2 sm:pb-0">
          {/* Tab Toggle - YENİ 5 TAB SİSTEMİ */}
          <div className="flex items-center bg-white rounded-xl border p-1 flex-shrink-0">
            <Button
              variant={activeTab === "all" ? "default" : "ghost"}
              size="sm"
              className={`text-xs sm:text-sm ${activeTab === "all" ? "bg-slate-700" : ""}`}
              onClick={() => setActiveTab("all")}
            >
              <Dumbbell className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">{t('common:all')}</span>
            </Button>
            <Button
              variant={activeTab === "reservations" ? "default" : "ghost"}
              size="sm"
              className={`text-xs sm:text-sm ${activeTab === "reservations" ? "bg-navy" : ""}`}
              onClick={() => setActiveTab("reservations")}
            >
              <CalendarIcon className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">{t('common:reservations')}</span>
            </Button>
            <Button
              variant={activeTab === "requests" ? "default" : "ghost"}
              size="sm"
              className={`text-xs sm:text-sm ${activeTab === "requests" ? "bg-orange-500" : ""}`}
              onClick={() => setActiveTab("requests")}
            >
              <Clock className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">{t("common:requests")}</span>
              {tabCounts.requests > 0 && (
                <span className="ml-1 bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5 min-w-[20px] text-center">
                  {tabCounts.requests}
                </span>
              )}
            </Button>
            <Button
              variant={activeTab === "pending" ? "default" : "ghost"}
              size="sm"
              className={`text-xs sm:text-sm ${activeTab === "pending" ? "bg-amber-500" : ""}`}
              onClick={() => setActiveTab("pending")}
            >
              <ListOrdered className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">{t('common:pending')}</span>
              {tabCounts.pending > 0 && (
                <span className="ml-1 bg-amber-600 text-white text-xs rounded-full px-1.5 py-0.5 min-w-[20px] text-center">
                  {tabCounts.pending}
                </span>
              )}
            </Button>
            <Button
              variant={activeTab === "changes" ? "default" : "ghost"}
              size="sm"
              className={`text-xs sm:text-sm ${activeTab === "changes" ? "bg-purple-500" : ""}`}
              onClick={() => setActiveTab("changes")}
            >
              <RotateCcw className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">{t("common:changes")}</span>
              {tabCounts.changes > 0 && (
                <span className="ml-1 bg-purple-600 text-white text-xs rounded-full px-1.5 py-0.5 min-w-[20px] text-center">
                  {tabCounts.changes}
                </span>
              )}
            </Button>
          </div>

          {/* View mode buttons - tüm sekmeler için göster */}
          <div className="flex items-center bg-white rounded-xl border p-1 flex-shrink-0">
            <Button
              variant={viewMode === "day" ? "default" : "ghost"}
              size="sm"
              className={viewMode === "day" ? "bg-navy" : ""}
              onClick={() => setViewMode("day")}
            >
              {t('common:day')}
            </Button>
            <Button
              variant={viewMode === "dayTime" ? "default" : "ghost"}
              size="sm"
              className={viewMode === "dayTime" ? "bg-navy" : ""}
              onClick={() => setViewMode("dayTime")}
              data-testid="view-mode-daytime-btn"
            >
              {t('common:dayTime')}
            </Button>
            <Button
              variant={viewMode === "week" ? "default" : "ghost"}
              size="sm"
              className={viewMode === "week" ? "bg-navy" : ""}
              onClick={() => setViewMode("week")}
            >
              {t('common:week')}
            </Button>
            <Button
              variant={viewMode === "month" ? "default" : "ghost"}
              size="sm"
              className={viewMode === "month" ? "bg-navy" : ""}
              onClick={() => setViewMode("month")}
            >
              {t('common:month')}
            </Button>
          </div>

          {canEdit && (
            <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
              <Button
                className="bg-navy hover:bg-navy/90 rounded-xl"
                onClick={() => {
                  resetForm();
                  setNewResDate(selectedDate);
                  setShowCreateModal(true);
                }}
                data-testid="create-reservation-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('common:newReservation')}
              </Button>
              {businessSettings?.public_booking?.enabled && (
                <Button
                  variant="outline"
                  className="rounded-xl border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  onClick={() => { setOpenClassPrefill(null); setShowOpenClasses(true); }}
                  data-testid="open-classes-btn"
                >
                  <Users className="w-4 h-4 mr-2" />
                  Online Ders Programı
                </Button>
              )}
              {businessSettings?.public_booking?.enabled && (
                <Button
                  variant="outline"
                  className="rounded-xl border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  onClick={() => setShowReminders(true)}
                  data-testid="group-reminders-btn"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Hatırlatma
                </Button>
              )}
              <Button variant="outline" className="rounded-xl text-purple-700 border-purple-200 hover:bg-purple-50" onClick={() => setShowBulkPlan(true)} data-testid="open-bulk-plan-btn">
                <CalendarRange className="w-4 h-4 mr-2" />
                Toplu Plan
              </Button>
              <Button
                variant="outline"
                className="rounded-xl"
                onClick={() => setShowCancelAnalysis(true)}
                data-testid="open-cancel-analysis-btn"
              >
                <XCircle className="w-4 h-4 mr-2" />
                İptal Analizi
              </Button>
            </div>
          )}
          {!canEdit && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800">
              Rezervasyon eklemek için aktif abonelik gerekli
            </div>
          )}
        </div>
      </div>

      {/* Filters - sadece Rezervasyonlar ve Tümü tab'larında görünür */}
      {(activeTab === "reservations" || activeTab === "all") && (
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                className="rounded-xl"
                onClick={() => {
                  if (viewMode === "day" || viewMode === "dayTime") setSelectedDate(addDays(selectedDate, -1));
                  else if (viewMode === "week") setSelectedDate(addDays(selectedDate, -7));
                  else if (viewMode === "month") setSelectedDate(addMonths(selectedDate, -1));
                }}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="outline" className="rounded-xl">
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    {(viewMode === "day" || viewMode === "dayTime") && format(selectedDate, "dd/MM", { locale: getDateLocale() })}
                    {viewMode === "week" && `${format(startOfWeek(selectedDate, { weekStartsOn: 1 }), "dd/MM", { locale: getDateLocale() })} - ${format(addDays(startOfWeek(selectedDate, { weekStartsOn: 1 }), 6), "dd/MM", { locale: getDateLocale() })}`}
                    {viewMode === "month" && format(selectedDate, "MMMM yyyy", { locale: getDateLocale() })}
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => date && setSelectedDate(date)}
                    locale={tr}
                  />
                </DialogContent>
              </Dialog>
              <Button
                variant="outline"
                size="icon"
                className="rounded-xl"
                onClick={() => {
                  if (viewMode === "day" || viewMode === "dayTime") setSelectedDate(addDays(selectedDate, 1));
                  else if (viewMode === "week") setSelectedDate(addDays(selectedDate, 7));
                  else if (viewMode === "month") setSelectedDate(addMonths(selectedDate, 1));
                }}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>

            <Select value={selectedTrainer} onValueChange={setSelectedTrainer}>
              <SelectTrigger className="w-40 rounded-xl">
                <SelectValue placeholder={t('common:trainer')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('trainers:allTrainers')}</SelectItem>
                {trainers.map((t) => (
                  <SelectItem key={t.trainer_id} value={t.trainer_id}>
                    {t.name} {t.surname}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedRoom} onValueChange={setSelectedRoom}>
              <SelectTrigger className="w-40 rounded-xl">
                <SelectValue placeholder={t('common:room')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("common:tumOdalar")}</SelectItem>
                {rooms.map((r) => (
                  <SelectItem key={r.room_id} value={r.room_id}>
                    {r.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Üye Filtresi - Searchable */}
            <Popover open={memberFilterOpen} onOpenChange={setMemberFilterOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  className="w-44 justify-between rounded-xl font-normal"
                  data-testid="member-filter-btn"
                >
                  <span className="truncate">
                    {selectedMemberFilter && selectedMemberFilter !== "all"
                      ? (() => {
                          const m = members.find(m => m.member_id === selectedMemberFilter);
                          return m ? `${m.name} ${m.surname}` : t("common:member");
                        })()
                      : t("common:member")}
                  </span>
                  <ChevronsUpDown className="ml-1 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-56 p-0" align="start">
                <div className="p-2 border-b">
                  <div className="flex items-center gap-2 px-2 py-1 bg-slate-50 rounded-lg">
                    <SearchIcon className="h-4 w-4 text-grey" />
                    <input
                      className="flex-1 bg-transparent text-sm outline-none placeholder:text-grey"
                      placeholder={t('members:searchMember')}
                      value={memberFilterSearch}
                      onChange={(e) => setMemberFilterSearch(e.target.value)}
                      data-testid="member-filter-search"
                    />
                  </div>
                </div>
                <div className="max-h-60 overflow-y-auto p-1">
                  <button
                    className={`w-full text-left px-3 py-2 text-sm rounded-md hover:bg-slate-100 ${!selectedMemberFilter || selectedMemberFilter === "all" ? "bg-slate-100 font-medium" : ""}`}
                    onClick={() => { setSelectedMemberFilter("all"); setMemberFilterOpen(false); setMemberFilterSearch(""); }}
                  >
                    {t('common:allMembers')}
                  </button>
                  {members
                    .slice()
                    .sort((a, b) => `${a.name} ${a.surname}`.localeCompare(`${b.name} ${b.surname}`, 'tr'))
                    .filter(m => {
                      if (!memberFilterSearch) return true;
                      const fullName = `${m.name} ${m.surname}`.toLowerCase();
                      return fullName.includes(memberFilterSearch.toLowerCase());
                    })
                    .map((m) => (
                      <button
                        key={m.member_id}
                        className={`w-full text-left px-3 py-2 text-sm rounded-md hover:bg-slate-100 ${selectedMemberFilter === m.member_id ? "bg-slate-100 font-medium" : ""}`}
                        onClick={() => { setSelectedMemberFilter(m.member_id); setMemberFilterOpen(false); setMemberFilterSearch(""); }}
                      >
                        {m.name} {m.surname}
                      </button>
                    ))}
                </div>
              </PopoverContent>
            </Popover>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-56 rounded-xl">
                <SelectValue placeholder={t('common:status')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("common:allStatuses")}</SelectItem>
                <SelectItem value="approved">{t('admin:approved')}</SelectItem>
                <SelectItem value="completed">{t('common:completed')}</SelectItem>
                <SelectItem value="cancelled">{t('common:cancel')}</SelectItem>
                <SelectItem value="cancelled_late">{t("common:gecİptal")}</SelectItem>
                <SelectItem value="burned">{t("common:yakildi")}</SelectItem>
                <SelectItem value="pending_member_approval">{t('admin:newReservationPendingMember')}</SelectItem>
                <SelectItem value="pending_business_approval">{t('admin:newReservationPendingBusiness')}</SelectItem>
                <SelectItem value="change_pending_member">{t("admin:changePendingMember")}</SelectItem>
                <SelectItem value="change_pending_business">{t("admin:changePendingBusiness")}</SelectItem>
              </SelectContent>
            </Select>

            {/* Boş Saatler Toggle - sadece günlük görünüm ve rezervasyonlar/tümü tabında */}
            {viewMode === "day" && (activeTab === "reservations" || activeTab === "all") && (
              <Button
                variant={showAvailableSlots ? "default" : "outline"}
                size="sm"
                onClick={() => setShowAvailableSlots(!showAvailableSlots)}
                className={`rounded-xl ${showAvailableSlots ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
                data-testid="show-available-slots-btn"
              >
                <Clock className="w-4 h-4 mr-1" />
                {t('common:availableSlots')}
              </Button>
            )}

            <Button
              variant={onlineOnly ? "default" : "outline"}
              size="sm"
              className={`rounded-xl ${onlineOnly ? "bg-sky-500 hover:bg-sky-600 text-white" : "text-sky-700 border-sky-200"}`}
              onClick={() => setOnlineOnly((v) => !v)}
              data-testid="online-only-filter-btn"
            >
              Sadece Online
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedDate(new Date())}
            >
              {t('common:today')}
            </Button>
          </div>
        </CardContent>
      </Card>
      )}

      {(activeTab === "reservations" || activeTab === "all") && (viewMode === "day" || viewMode === "dayTime") && (
        <DayStrip selectedDate={selectedDate} onSelect={setSelectedDate} countFor={(d) => getReservationsForDate(d).length} />
      )}

      {/* "Tümü" ve t('common:reservations') artık aynı takvim görünümünü kullanıyor */}
      {/* Takvim görünümü aşağıda day/week/month olarak render ediliyor */}

      {/* REQUESTS VIEW - Gelen Talepler - Rezervasyonlar gibi liste formatında */}
      {activeTab === "requests" && (
        <Card className="rounded-2xl shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-orange-500" />
              {t("reservations:tabRequests")}
              {getFilteredRequests().length > 0 && (
                <Badge variant="secondary" className="bg-orange-100 text-orange-700">{t("common:requestsCount", { count: getFilteredRequests().length })}</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {getFilteredRequests().length > 0 ? (
              <div className="space-y-3">
                {getFilteredRequests().map((req) => {
                  const member = members.find(m => m.member_id === req.member_id);
                  const trainer = trainers.find(t => t.trainer_id === req.trainer_id);
                  const room = rooms.find(r => r.room_id === req.room_id);
                  
                  return (
                    <div 
                      key={req.reservation_id || req.request_id}
                      className="flex items-center justify-between p-4 bg-orange-50 border border-orange-200 rounded-xl"
                      data-testid={`request-${req.reservation_id || req.request_id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                          <MessageSquare className="w-5 h-5 text-orange-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-navy">{req.member_name || `${member?.name || ""} ${member?.surname || ""}`.trim() || "Üye"}</p>
                          <div className="text-sm text-grey">
                            <span>{req.date ? format(new Date(req.date), "dd.MM.yyyy", { locale: getDateLocale() }) : "-"}</span>
                            <span className="mx-2">•</span>
                            <span>{req.start_time} - {req.end_time}</span>
                            {(trainer?.name || req.trainer_name) && (
                              <>
                                <span className="mx-2">•</span>
                                <span>{trainer?.name || req.trainer_name}</span>
                              </>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-1 mt-1">
                            <Badge 
                              variant="outline" 
                              className={
                                req.status === "modified_by_business" 
                                  ? "bg-purple-50 text-purple-700 border-purple-300"
                                  : req.status === "modified_by_member"
                                  ? "bg-blue-50 text-blue-700 border-blue-300"
                                  : "bg-orange-50 text-orange-700 border-orange-300"
                              }
                            >
                              {req.status === "modified_by_business" ? t("reservations:sentToMember") : 
                               req.status === "modified_by_member" ? t("reservations:changeFromMember") : t("reservations:newRequest")}
                            </Badge>
                            {req.branch && (
                              <Badge variant="outline">{getLocalizedBranch(req.branch)}</Badge>
                            )}
                            {req.is_demo && (
                              <Badge variant="outline" className="text-green-600 border-green-300 bg-green-50">
                                Demo
                              </Badge>
                            )}
                          </div>
                          {req.notes && (
                            <p className="text-xs text-grey italic mt-1">&ldquo;{req.notes}&rdquo;</p>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex gap-2 flex-wrap">
                        {req.status === "modified_by_business" ? (
                          <div className="flex items-center gap-2 text-purple-600">
                            <Clock className="w-4 h-4" />
                            <span className="text-sm font-medium">{t("reservations:waitingMemberResponse")}</span>
                          </div>
                        ) : (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              className="rounded-xl text-slate-600 border-slate-200 hover:bg-slate-50"
                              onClick={() => {
                                setViewingReservation(req);
                                setShowViewModal(true);
                              }}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              {t("common:view")}
                            </Button>
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700 rounded-xl text-white"
                              onClick={() => handleApproveRequest(req.reservation_id)}
                              disabled={processingReservationId === req.reservation_id}
                            >
                              {processingReservationId === req.reservation_id ? (
                                <>
                                  <div className="w-4 h-4 mr-1 animate-spin rounded-full border-2 border-white border-t-transparent" />
                                  {t("common:processing")}
                                </>
                              ) : (
                                <>
                                  <Check className="w-4 h-4 mr-1" />
                                  {t("reservations:approve")}
                                </>
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="rounded-xl text-blue-600 border-blue-200 hover:bg-blue-50"
                              onClick={() => {
                                setEditingRequest(req);
                                setEditRequestForm({
                                  date: req.date || "",
                                  start_time: req.start_time || "",
                                  end_time: req.end_time || "",
                                  trainer_id: req.trainer_id || "",
                                  room_id: req.room_id || "",
                                  reason: ""
                                });
                                setShowEditRequestModal(true);
                              }}
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              {t("common:change")}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="rounded-xl text-red-600 border-red-200 hover:bg-red-50"
                              onClick={() => {
                                setSelectedRequestForReject(req);
                                setRejectReason("");
                                setShowRejectModal(true);
                              }}
                            >
                              <X className="w-4 h-4 mr-1" />
                              {t("reservations:reject")}
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-grey">
                <MessageSquare className="w-12 h-12 text-grey-300 mx-auto mb-2" />
                <p>{t("common:noPendingRequests")}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* BEKLEMEDE VIEW - İşletmenin oluşturduğu, üye onayı bekleyen */}
      {activeTab === "pending" && (
        <Card className="border-0 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <ListOrdered className="w-5 h-5" />
              {t('admin:pendingMemberApproval')}
              {pendingReservations.length > 0 && (
                <Badge variant="secondary" className="bg-amber-100 text-amber-700">{pendingReservations.length} {t('common:pending').toLowerCase()}</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {pendingReservations.length > 0 ? (
              <div className="space-y-3">
                {pendingReservations.map((res) => (
                  <div 
                    key={res.reservation_id}
                    className="flex items-center justify-between p-4 bg-amber-50 border border-amber-200 rounded-xl"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                        <Clock className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-navy">{res.member_name}</p>
                        <div className="text-sm text-grey">
                          <span>{res.date ? format(new Date(res.date), "dd.MM.yyyy", { locale: getDateLocale() }) : "-"}</span>
                          <span className="mx-2">•</span>
                          <span>{res.start_time} - {res.end_time}</span>
                          {res.trainer_name && (
                            <>
                              <span className="mx-2">•</span>
                              <span>{res.trainer_name}</span>
                            </>
                          )}
                        </div>
                        <div className="mt-1">
                          <Badge 
                            variant="outline" 
                            className={res.status === "pending_member_approval" 
                              ? "bg-amber-50 text-amber-700 border-amber-300" 
                              : "bg-blue-50 text-blue-700 border-blue-300"
                            }
                          >
                            {res.status === "pending_member_approval" 
                              ? "Yeni rezervasyon - üye onayı bekliyor" 
                              : t("admin:changePendingMemberLower")
                            }
                          </Badge>
                        </div>
                        {res.notes && (
                          <p className="text-xs text-grey italic mt-1">&ldquo;{res.notes}&rdquo;</p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex gap-2 flex-wrap">
                      {/* Görüntüle */}
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-xl text-slate-600 border-slate-200 hover:bg-slate-50"
                        onClick={() => {
                          setViewingReservation(res);
                          setShowViewModal(true);
                        }}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        {t("common:view")}
                      </Button>
                      {/* Onayla - işletme direkt onaylayabilir */}
                      <Button
                        size="sm"
                        className="rounded-xl bg-green-600 hover:bg-green-700 text-white"
                        disabled={actionLoading !== null}
                        onClick={async () => {
                          setActionLoading(`approve-${res.reservation_id}`);
                          try {
                            const response = await fetch(`${API}/reservations/${res.reservation_id}/business-direct-approve`, {
                              method: "PUT",
                              credentials: "include",
                            });
                            if (response.ok) {
                              toast.success(t('reservations:reservationApproved'));
                              fetchData();
                              fetchPendingReservations();
                              fetchTabCounts();
                            } else {
                              const error = await response.json();
                              toast.error(error.detail || t("reservations:approvalFailed"));
                            }
                          } catch (error) {
                            toast.error(t('common:errorOccurred'));
                          } finally {
                            setActionLoading(null);
                          }
                        }}
                      >
                        {actionLoading === `approve-${res.reservation_id}` ? (
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        ) : (
                          <Check className="w-4 h-4 mr-1" />
                        )}
                        {actionLoading === `approve-${res.reservation_id}` ? t("memberDashboard:approving") : t('admin:confirm')}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-xl text-blue-600 border-blue-200 hover:bg-blue-50"
                        disabled={actionLoading !== null}
                        onClick={async () => {
                          setActionLoading(`change-${res.reservation_id}`);
                          // Değişiklik modalını aç
                          setSelectedReservationForChange(res);
                          setChangeDate(res.date || "");
                          setChangeStartTime(res.start_time || "");
                          setChangeEndTime(res.end_time || "");
                          setChangeTrainer(res.trainer_id || "");
                          setChangeRoom(res.room_id || "");
                          setChangeMember(res.member_id || "");
                          setChangePackageId(res.member_package_id || "");
                          setChangeApprovalStatus("change_pending_member");
                          // Ödeme tipini set et
                          setChangePaymentType(res.payment_type || (res.member_package_id ? "package" : res.is_demo ? "demo" : "package"));
                          
                          // Üye paketlerini yükle
                          if (res.member_id) {
                            fetchChangeMemberPackages(res.member_id);
                          }
                          
                          setShowChangeModal(true);
                          setActionLoading(null);
                        }}
                      >
                        {actionLoading === `change-${res.reservation_id}` ? (
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        ) : (
                          <Edit className="w-4 h-4 mr-1" />
                        )}
                        {actionLoading === `change-${res.reservation_id}` ? t('common:loading') : t("common:change")}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-xl text-red-600 border-red-200 hover:bg-red-50"
                        disabled={actionLoading !== null}
                        onClick={() => {
                          setSelectedReservationForCancel(res);
                          setCancelReason("");
                          setCancelReasonCategory("");
                          setBurnSession(false);
                          setShowCancelModal(true);
                        }}
                      >
                        <X className="w-4 h-4 mr-1" />
                        {t("common:cancel")}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-grey">
                <ListOrdered className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>{t("reservations:noPendingApprovalReservations")}</p>
                <p className="text-sm mt-1">{t("reservations:newReservationHint")}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* CHANGES VIEW - Üyeden gelen değişiklik talepleri */}
      {activeTab === "changes" && (
        <Card className="border-0 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <RotateCcw className="w-5 h-5" />
              {t("common:changeRequests")}
              {changeRequests.length > 0 && (
                <Badge variant="secondary" className="bg-purple-100 text-purple-700">{t("common:requestsCount", { count: changeRequests.length })}</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {changeRequests.length > 0 ? (
              <div className="space-y-4">
                {changeRequests.map((req) => {
                  const member = members.find(m => m.member_id === req.member_id);
                  return (
                    <div 
                      key={req.reservation_id}
                      className="flex items-center justify-between p-4 bg-purple-50 border border-purple-200 rounded-xl"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                          <RotateCcw className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-navy">{req.member_name || `${member?.name || ""} ${member?.surname || ""}`.trim() || "Üye"}</p>
                          <div className="text-sm text-grey">
                            <span>{req.date ? format(new Date(req.date), "dd.MM.yyyy", { locale: getDateLocale() }) : "-"}</span>
                            <span className="mx-2">•</span>
                            <span>{req.start_time} - {req.end_time}</span>
                            {req.trainer_name && (
                              <>
                                <span className="mx-2">•</span>
                                <span>{req.trainer_name}</span>
                              </>
                            )}
                          </div>
                          <div className="mt-1">
                            <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-300">
                              {t("admin:changePendingBusiness")}
                            </Badge>
                          </div>
                          {req.change_reason && (
                            <p className="text-xs text-grey italic mt-1">&ldquo;{req.change_reason}&rdquo;</p>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex gap-2 flex-wrap">
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-xl text-slate-600 border-slate-200 hover:bg-slate-50"
                            onClick={() => {
                              setViewingReservation(req);
                              setShowViewModal(true);
                            }}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            {t("common:view")}
                          </Button>
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 rounded-xl text-white"
                            onClick={() => handleApproveChangeRequest(req.reservation_id)}
                            disabled={processingReservationId === req.reservation_id}
                          >
                            {processingReservationId === req.reservation_id ? (
                              <>
                                <div className="w-4 h-4 mr-1 animate-spin rounded-full border-2 border-white border-t-transparent" />
                                {t("common:processing")}
                              </>
                            ) : (
                              <>
                                <Check className="w-4 h-4 mr-1" />
                                {t("reservations:approve")}
                              </>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-xl text-blue-600 border-blue-200 hover:bg-blue-50"
                            onClick={() => {
                              // Değişiklik talebini düzenleme modalını aç
                              setEditingChangeRequest(req);
                              setEditChangeForm({
                                new_date: req.date || "",
                                new_start_time: req.start_time || "",
                                new_end_time: req.end_time || "",
                                trainer_id: req.trainer_id || "",
                                room_id: req.room_id || "",
                                reason: ""
                              });
                              setShowEditChangeModal(true);
                            }}
                          >
                            <Edit className="w-4 h-4 mr-1" />
                            {t("common:change")}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-xl text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => {
                              setSelectedChangeForReject(req);
                              setRejectChangeReason("");
                              setShowRejectChangeModal(true);
                            }}
                          >
                            <X className="w-4 h-4 mr-1" />
                            {t("common:cancel")}
                          </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <RotateCcw className="w-12 h-12 text-grey mx-auto mb-3" />
                <p className="text-grey">{t("reservations:noPendingChangeRequests")}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* WEEKLY VIEW */}
      {(activeTab === "reservations" || activeTab === "all") && viewMode === "week" && (
        <Card className="border-0 shadow-sm overflow-hidden">
          <CardContent className="p-0 overflow-x-auto">
            <div className="min-w-[700px]">
              <div className="grid grid-cols-7 border-b">
                {getWeekDays().map((day, idx) => (
                  <div 
                    key={idx} 
                    className={`p-3 text-center border-r last:border-r-0 cursor-pointer hover:bg-slate-50 transition-colors ${isSameDay(day, new Date()) ? 'bg-navy/5' : ''}`}
                    onClick={() => { setSelectedDate(day); setViewMode("day"); }}
                    data-testid={`week-day-header-${format(day, "yyyy-MM-dd")}`}
                  >
                    <p className="text-xs text-grey">{format(day, "EEE", { locale: getDateLocale() })}</p>
                    <p className={`font-bold ${isSameDay(day, new Date()) ? 'text-navy' : 'text-grey-700'}`}>
                      {format(day, "d")}
                    </p>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7 min-h-[400px]">
                {getWeekDays().map((day, idx) => {
                  const dayReservations = getReservationsForDate(day).sort((a, b) => (a.start_time || a.time || "").localeCompare(b.start_time || b.time || ""));
                  return (
                    <div 
                      key={idx} 
                      className={`p-2 border-r last:border-r-0 ${isSameDay(day, new Date()) ? 'bg-navy/5' : ''}`}
                    >
                      {dayReservations.length > 0 ? (
                        <div className="space-y-1">
                          {dayReservations.slice(0, 5).map((res) => {
                            const trainer = trainers.find(t => t.trainer_id === res.trainer_id);
                            const room = rooms.find(r => r.room_id === res.room_id);
                            return (
                              <div 
                                key={res.reservation_id}
                                className={`p-1.5 text-xs rounded cursor-pointer ${resColorClass(res.status)}`}
                                onClick={() => {
                                  setSelectedDate(day);
                                  setViewMode("day");
                                }}
                                title={`${res.start_time || res.time}-${res.end_time || ''} | ${res.member_name || 'Üye'} | ${trainer?.name || ''} | ${room?.name || ''}`}
                              >
                                <p className="font-bold truncate flex items-center gap-1">{res.start_time || res.time}{res.end_time ? `-${res.end_time}` : ''}{res.open_class_id && <span className="inline-flex items-center rounded px-1 text-[9px] font-bold leading-4 bg-sky-500 text-white align-middle" data-testid="online-badge">Online</span>}</p>
                                <p className={`truncate font-medium ${['cancelled', 'cancelled_late', 'burned', 'rejected'].includes(res.status) ? 'line-through' : ''}`}>{res.member_name || 'Üye'}</p>
                                <p className="truncate text-[10px] opacity-80">{trainer?.name || '-'} • {room?.name || '-'}</p>
                            </div>
                          );
                        })}
                        {dayReservations.length > 5 && (
                          <p className="text-xs text-grey text-center">+{dayReservations.length - 5} daha</p>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-grey text-center mt-4">-</p>
                    )}
                  </div>
                );
              })}
              </div>
            </div>
            <ReservationColorLegend />
          </CardContent>
        </Card>
      )}

      {/* MONTHLY VIEW */}
      {(activeTab === "reservations" || activeTab === "all") && viewMode === "month" && (
        <Card className="border-0 shadow-sm overflow-hidden">
          <CardContent className="p-0">
            <div className="w-full">
              <div className="grid grid-cols-7 border-b">
                {getDayNamesShort().map((day) => (
                  <div key={day} className="p-1 sm:p-2 text-center text-[10px] sm:text-xs font-medium text-grey border-r last:border-r-0">
                    {day}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7">
                {(() => {
                  const monthStart = startOfMonth(selectedDate);
                  const startDay = monthStart.getDay() === 0 ? 6 : monthStart.getDay() - 1;
                  const days = getMonthDays();
                  const paddedDays = [...Array(startDay).fill(null), ...days];
                  
                  return paddedDays.map((day, idx) => {
                    if (!day) {
                      return <div key={idx} className="p-1 sm:p-2 border-r border-b min-h-[50px] sm:min-h-[80px] bg-slate-50" />;
                  }
                  
                  const dayReservations = getReservationsForDate(day);
                  const isToday = isSameDay(day, new Date());
                  
                  return (
                    <div 
                      key={idx}
                      className={`p-1 sm:p-2 border-r border-b min-h-[50px] sm:min-h-[80px] cursor-pointer hover:bg-slate-50 ${
                        isToday ? 'bg-navy/5' : ''
                      } ${!isSameMonth(day, selectedDate) ? 'text-grey-300' : ''}`}
                      onClick={() => {
                        setSelectedDate(day);
                        setViewMode("day");
                      }}
                    >
                      <p className={`text-xs sm:text-sm ${isToday ? 'font-bold text-navy' : ''}`}>{format(day, "d")}</p>
                      {dayReservations.length > 0 && <MonthDayCounts reservations={dayReservations} />}
                    </div>
                  );
                });
              })()}
              </div>
            </div>
            <ReservationColorLegend />
          </CardContent>
        </Card>
      )}

      {/* DAY TIME VIEW - Dikey Zaman Çizelgesi (saatler solda, antrenör > oda sütunları, süre kadar uzayan kutular) */}
      {(activeTab === "reservations" || activeTab === "all") && viewMode === "dayTime" && (() => {
        const dtDayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        const dtDayName = dtDayNames[selectedDate.getDay()];
        const dtBusinessDay = businessWorkingHours?.[dtDayName];
        const dtBusinessClosed = dtBusinessDay?.closed === true || !dtBusinessDay;
        const dtToMins = (s) => { if (!s) return null; const [hh, mm] = String(s).split(':').map(Number); return hh * 60 + mm; };
        const dtOpenMins = dtToMins(dtBusinessDay?.open) ?? 0;
        const dtCloseMins = dtToMins(dtBusinessDay?.close) ?? 24 * 60;

        const DT_START = 6 * 60;   // 06:00
        const DT_END = 24 * 60;    // 00:00
        const DT_SLOT_MIN = 30;
        const DT_SLOT_H = 30;      // 30dk = 30px
        const DT_COL_W = 180;      // oda sütun genişliği
        const dtSlots = [];
        for (let m = DT_START; m < DT_END; m += DT_SLOT_MIN) dtSlots.push(m);
        const dtFmt = (m) => `${String(Math.floor(m / 60) % 24).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
        const dtPxPerMin = DT_SLOT_H / DT_SLOT_MIN;
        const dtTotalH = dtSlots.length * DT_SLOT_H;

        const dtActiveTrainers = (selectedTrainer && selectedTrainer !== "all")
          ? trainers.filter(tr => tr.trainer_id === selectedTrainer)
          : trainers.filter(tr => tr.status === "active" || tr.is_active !== false);
        const dtActiveRooms = (selectedRoom && selectedRoom !== "all")
          ? rooms.filter(rm => rm.room_id === selectedRoom)
          : rooms.filter(rm => rm.is_active !== false);
        const dtDayList = getDayViewList();

        // Grupla: aynı antrenör + aynı oda + aynı başlangıç saati → düet/grup tek kutuda
        const dtGroups = {};
        dtDayList.forEach(res => {
          const startM = dtToMins(res.start_time || res.time);
          if (startM === null || startM >= DT_END) return;
          const endM = Math.min(dtToMins(res.end_time) ?? (startM + 60), DT_END);
          const roomKey = dtActiveRooms.some(rm => rm.room_id === res.room_id) ? res.room_id : "__none__";
          const tId = res.trainer_id;
          if (!tId) return;
          if (!dtGroups[tId]) dtGroups[tId] = {};
          const key = `${roomKey}|${startM}`;
          if (!dtGroups[tId][key]) dtGroups[tId][key] = { startM, endM, roomKey, items: [] };
          dtGroups[tId][key].items.push(res);
          if (endM > dtGroups[tId][key].endM) dtGroups[tId][key].endM = endM;
        });

        const dtIsCancelledLike = (st) => ["cancelled", "cancelled_late", "burned"].includes(st);
        const dtStatusColors = resColorClass;
        const dtOpenView = (res) => { setViewingReservation(res); setShowViewModal(true); };

        return (
          <>
          <Card className="border-0 shadow-sm overflow-hidden" data-testid="daytime-view">
            <CardHeader className="pb-2 border-b bg-slate-50/50">
              <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                <Clock className="w-4 h-4 text-navy" />
                {t('common:dayTime')} — {format(selectedDate, "d MMMM yyyy, EEEE", { locale: getDateLocale() })}
                {dtBusinessClosed && (
                  <Badge variant="secondary" data-testid="daytime-closed-badge">{t('common:businessClosed')}</Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <div className="min-w-[720px]">
                {/* Başlık satırı: Antrenör adı + altında oda sütunları */}
                <div className="flex border-b bg-slate-50 sticky top-0 z-20">
                  <div className="w-20 flex-shrink-0 border-r flex flex-col" style={{ height: 56 }}>
                    <div className="flex-1 flex items-center justify-center text-[10px] font-bold text-navy px-0.5 text-center leading-tight" data-testid="daytime-axis-trainer-label">{t('common:trainerNameHeader')}</div>
                    <div className="flex-1 flex items-center justify-center text-[10px] text-grey border-t px-0.5 text-center leading-tight" data-testid="daytime-axis-room-label">{t('common:roomNameHeader')}</div>
                  </div>
                  {dtActiveTrainers.map(tr => {
                    const trainerGroups = dtGroups[tr.trainer_id] || {};
                    const hasNoRoom = Object.values(trainerGroups).some(g => g.roomKey === "__none__");
                    const cols = [...dtActiveRooms.map(rm => ({ key: rm.room_id, label: rm.name })), ...(hasNoRoom ? [{ key: "__none__", label: "—" }] : [])];
                    return (
                      <div key={tr.trainer_id} className="border-r last:border-r-0" style={{ width: cols.length * DT_COL_W, flexShrink: 0 }} data-testid={`daytime-trainer-col-${tr.trainer_id}`}>
                        <p className="text-xs sm:text-sm font-bold text-navy truncate text-center pt-2 px-1">{tr.name} {tr.surname || ''}</p>
                        <div className="flex border-t mt-1.5">
                          {cols.map(c => (
                            <div key={c.key} className="text-[10px] sm:text-xs text-grey text-center py-1 border-r last:border-r-0 truncate px-1" style={{ width: DT_COL_W }} data-testid={`daytime-room-col-${tr.trainer_id}-${c.key}`}>
                              {c.label}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {/* Gövde: saat gutter + antrenör/oda gridleri */}
                <div className="flex">
                  {/* Saat gutter */}
                  <div className="w-20 flex-shrink-0 border-r relative bg-white" style={{ height: dtTotalH }}>
                    {dtSlots.map((m, idx) => (
                      <div key={m} className={`absolute right-1 text-[11px] font-mono ${m % 60 === 0 ? 'font-bold text-navy' : 'text-grey'}`} style={{ top: idx * DT_SLOT_H - 7 }}>
                        {dtFmt(m)}
                      </div>
                    ))}
                  </div>
                  {/* Antrenör kolonları */}
                  {dtActiveTrainers.map(tr => {
                    const trainerGroups = dtGroups[tr.trainer_id] || {};
                    const hasNoRoom = Object.values(trainerGroups).some(g => g.roomKey === "__none__");
                    const cols = [...dtActiveRooms.map(rm => ({ key: rm.room_id, label: rm.name })), ...(hasNoRoom ? [{ key: "__none__", label: "—" }] : [])];
                    return (
                      <div key={tr.trainer_id} className="relative border-r last:border-r-0" style={{ width: cols.length * DT_COL_W, height: dtTotalH, flexShrink: 0 }}>
                        {/* Arka plan satırları (çalışma saati dışı gri) */}
                        {dtSlots.map((m) => {
                          const inHours = !dtBusinessClosed && m >= dtOpenMins && m < dtCloseMins;
                          return (
                            <div key={m} className={`flex border-b ${m % 60 === 0 ? 'border-slate-200' : 'border-slate-100'} ${inHours ? 'bg-white' : 'bg-slate-100/70'}`} style={{ height: DT_SLOT_H }} data-testid={m % 60 === 0 ? `daytime-row-${dtFmt(m)}` : undefined}>
                              {cols.map(c => (
                                <div key={c.key} className="border-r last:border-r-0 h-full" style={{ width: DT_COL_W }} />
                              ))}
                            </div>
                          );
                        })}
                        {/* Rezervasyon kutuları (süre kadar uzun) */}
                        {Object.values(trainerGroups).map((g) => {
                          const colIdx = cols.findIndex(c => c.key === g.roomKey);
                          if (colIdx < 0) return null;
                          const top = Math.max(0, g.startM - DT_START) * dtPxPerMin;
                          const height = Math.max((g.endM - Math.max(g.startM, DT_START)) * dtPxPerMin, 18);
                          const displayRes = g.items.find(it => !dtIsCancelledLike(it.status)) || g.items[0];
                          const multi = g.items.length > 1;
                          const allCancelled = g.items.every(it => dtIsCancelledLike(it.status));
                          return (
                            <div
                              key={`${g.roomKey}-${g.startM}`}
                              className={`absolute z-10 rounded-lg border cursor-pointer hover:shadow-md transition-shadow overflow-hidden ${dtStatusColors(displayRes.status)} ${allCancelled ? 'opacity-60' : ''}`}
                              style={{ top, height, left: colIdx * DT_COL_W + 2, width: DT_COL_W - 6 }}
                              onClick={() => multi ? setDtGroupPicker(g.items) : dtOpenView(g.items[0])}
                              data-testid={`daytime-group-box-${tr.trainer_id}-${g.roomKey}-${dtFmt(g.startM).replace(':', '')}`}
                            >
                              <div className="px-1.5 py-0.5 text-[10px] sm:text-xs leading-tight h-full overflow-hidden">
                                <p className="font-semibold flex items-center gap-1 flex-wrap">
                                  <span>{dtFmt(g.startM)}-{dtFmt(g.endM)}</span>
                                  <span className="opacity-80">• {getTrainingTypeLabel(displayRes.training_type)}</span>
                                  {multi && <span className="opacity-80">({g.items.length} {t('common:member')})</span>}
                                  {g.items.some(it => it.open_class_id) && <span className="inline-flex items-center rounded px-1 text-[9px] font-bold leading-4 bg-sky-500 text-white align-middle" data-testid="online-badge">Online</span>}
                                </p>
                                {g.items.map(it => (
                                  <p key={it.reservation_id} className={`truncate ${dtIsCancelledLike(it.status) ? 'line-through' : ''}`} data-testid={`daytime-reservation-${it.reservation_id}`}>
                                    {it.member_name || t('common:member')}
                                  </p>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Düet/Grup kutusu: hangi üyenin detayı? */}
          <Dialog open={!!dtGroupPicker} onOpenChange={(o) => !o && setDtGroupPicker(null)}>
            <DialogContent className="max-w-md" data-testid="daytime-group-dialog">
              <DialogHeader>
                <DialogTitle>{t('common:whichReservationDetail')}</DialogTitle>
              </DialogHeader>
              <div className="space-y-2">
                {(dtGroupPicker || []).map(res => (
                  <button
                    key={res.reservation_id}
                    className="w-full text-left p-3 rounded-xl border hover:bg-slate-50 flex items-center justify-between gap-2"
                    onClick={() => { setDtGroupPicker(null); dtOpenView(res); }}
                    data-testid={`daytime-group-option-${res.reservation_id}`}
                  >
                    <div className="min-w-0">
                      <p className={`font-bold text-navy truncate ${dtIsCancelledLike(res.status) ? 'line-through text-grey' : ''}`}>{res.member_name || t('common:member')}</p>
                      <p className="text-xs text-grey">{res.start_time || res.time}{res.end_time ? `-${res.end_time}` : ''} • {getTrainingTypeLabel(res.training_type)}</p>
                    </div>
                    {getStatusBadge(res.status, res)}
                  </button>
                ))}
              </div>
            </DialogContent>
          </Dialog>
          </>
        );
      })()}


      {/* DAILY VIEW - Reservations List */}
      {(activeTab === "reservations" || activeTab === "all") && viewMode === "day" && (
        <div className="space-y-3">
          {/* Available Slots Panel */}
          {showAvailableSlots && (
            <Card className="border-0 shadow-sm bg-emerald-50">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2 text-emerald-700">
                  <Clock className="w-4 h-4" />
                  Boş Saatler - {format(selectedDate, "d MMMM yyyy", { locale: getDateLocale() })}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {getAvailableSlots().length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2 max-h-[300px] overflow-y-auto">
                    {getAvailableSlots().slice(0, 50).map((slot, idx) => (
                      <div 
                        key={idx}
                        className="p-2 bg-white rounded-lg border border-emerald-200 hover:border-emerald-400 cursor-pointer transition-colors"
                        onClick={() => {
                          // Open create modal with pre-filled data
                          fromSlotClickRef.current = true;
                          setNewResDate(selectedDate);
                          setNewResTrainer(slot.trainer_id);
                          setNewResRoom(slot.room_id);
                          setNewResStartTime(slot.start);
                          setNewResEndTime(slot.end);
                          // Clear availability checks - we already know this slot is available
                          setTrainerAvailability({ is_available: true, reason: "Boş saatlerden seçildi" });
                          setRoomAvailability({ is_available: true, available_capacity: 10, is_locked: false });
                          setShowCreateModal(true);
                        }}
                        data-testid={`available-slot-${idx}`}
                      >
                        <p className="font-bold text-emerald-700 text-sm">{slot.start} - {slot.end}</p>
                        <p className="text-xs text-grey truncate" title={slot.trainer_name}>{slot.trainer_name}</p>
                        <p className="text-xs text-emerald-600 truncate" title={slot.room_name}>{slot.room_name}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-grey py-4 text-center">
                    Seçilen filtrelere göre müsait saat bulunamadı.
                  </p>
                )}
                {getAvailableSlots().length > 50 && (
                  <p className="text-xs text-grey text-center mt-2">
                    İlk 50 sonuç gösteriliyor. Filtreleri daraltın.
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Daily reservations list */}
          {(() => {
            const filteredData = getDayViewList();
            
            return filteredData.length > 0 ? (
              filteredData.map((res) => {
            const trainer = trainers.find((t) => t.trainer_id === res.trainer_id);
            const room = rooms.find((r) => r.room_id === res.room_id);
            const member = members.find((m) => m.member_id === res.member_id);
            const isCancelledLike = ["cancelled", "cancelled_late", "burned"].includes(res.status);

            return (
              <Card
                key={res.reservation_id}
                className={`border-0 shadow-sm hover:shadow-md transition-shadow overflow-hidden ${isCancelledLike ? "bg-slate-50 opacity-60 hover:opacity-100" : ""}`}
                data-testid={`reservation-${res.reservation_id}`}
              >
                <CardContent className="p-3 sm:p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex items-start sm:items-center gap-3 sm:gap-4 flex-1 min-w-0">
                      {/* Time Block */}
                      <div className="w-16 sm:w-20 text-center bg-navy/5 rounded-lg p-2 flex-shrink-0">
                        <p className="font-heading font-bold text-base sm:text-lg text-navy">
                          {res.start_time || res.time}
                        </p>
                        {res.end_time && (
                          <p className="text-[10px] sm:text-xs text-grey">{res.end_time}</p>
                        )}
                      </div>
                      <div className="hidden sm:block h-14 w-px bg-grey-200" />
                      {/* Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-1 sm:gap-2 mb-1">
                          <p className={`font-bold text-sm sm:text-lg truncate max-w-[150px] sm:max-w-none ${isCancelledLike ? "line-through text-grey" : "text-navy"}`}>
                            {res.member_name || `${member?.name || ""} ${member?.surname || ""}`.trim() || t('common:member')}
                          </p>
                          {getStatusBadge(res.status, res)}
                          {res.open_class_id && <span className="inline-flex items-center rounded px-1 text-[9px] font-bold leading-4 bg-sky-500 text-white align-middle" data-testid="online-badge">Online</span>}
                          {res.package_request && (
                            <Badge className="bg-orange-100 text-orange-700 border border-orange-300 text-[10px] sm:text-xs" data-testid={`package-request-badge-${res.reservation_id}`}>
                              Paket yok
                            </Badge>
                          )}
                          {/* Ödeme Tipi Badge */}
                          {res.is_demo || res.payment_type === "demo" ? (
                            <Badge variant="outline" className="text-green-600 border-green-300 bg-green-50 text-[10px] sm:text-xs">
                              Demo
                            </Badge>
                          ) : res.payment_type === "card" ? (
                            <Badge variant="outline" className="text-purple-600 border-purple-300 bg-purple-50 text-[10px] sm:text-xs">
                              {t("memberDashboard:card")}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-blue-600 border-blue-300 bg-blue-50 text-[10px] sm:text-xs font-mono">
                              {res.package_no ? res.package_no : t("reservations:package")}
                            </Badge>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-1 sm:gap-2 mb-1">
                          <Badge variant="outline" className="text-[10px] sm:text-xs">
                            {getLocalizedBranch(res.branch) || "-"}
                          </Badge>
                          <Badge variant="secondary" className="text-[10px] sm:text-xs">
                            {getTrainingTypeLabel(res.training_type)}
                          </Badge>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-grey">
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {trainer?.name || t('common:trainer')}
                          </span>
                          <span className="flex items-center gap-1 font-medium text-navy">
                            <MapPin className="w-3 h-3" />
                            {room?.name || t('common:room')}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-1 sm:gap-2 flex-wrap justify-end">
                      {/* Görüntüle - her durumda */}
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-xl text-slate-600 border-slate-200 hover:bg-slate-50 p-2 sm:px-3"
                        onClick={() => {
                          setViewingReservation(res);
                          setShowViewModal(true);
                        }}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      {res.status === "pending" && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-xl text-green-600 border-green-200 hover:bg-green-50 p-2 sm:px-3"
                            onClick={() => handleConfirm(res.reservation_id)}
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-xl text-red-600 border-red-200 hover:bg-red-50 p-2 sm:px-3"
                            onClick={() => {
                              setSelectedReservationForCancel(res);
                              setCancelReason("");
                              setCancelReasonCategory("");
                              setBurnSession(false);
                              setShowCancelModal(true);
                            }}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      {canRepeat(res) && (
                        <Button size="sm" variant="outline" className="rounded-xl text-purple-700 border-purple-200 hover:bg-purple-50 p-2 sm:px-3" onClick={() => setRepeatSource(res)} title="Tekrarla" data-testid={`repeat-res-btn-${res.reservation_id}`}>
                          <RefreshCw className="w-4 h-4 sm:mr-1" />
                          <span className="hidden sm:inline">Tekrarla</span>
                        </Button>
                      )}
                      {(res.status === "confirmed" || res.status === "approved") && (
                        <>
                          {/* Manuel Tamamla - Sadece admin ve yönetici */}
                          {(currentUser?.role === "business" || userPermissions?.is_manager || userPermissions?.is_owner) && (
                            <Button
                              size="sm"
                              className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white p-2 sm:px-3"
                              onClick={() => handleCompleteReservation(res.reservation_id, res)}
                              disabled={completingReservationId === res.reservation_id}
                              title={!canManualComplete(res).allowed ? canManualComplete(res).reason : t("common:completeLesson")}
                            >
                              {completingReservationId === res.reservation_id ? (
                                <div className="w-4 h-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                              ) : (
                                <>
                                  <Check className="w-4 h-4 sm:mr-1" />
                                  <span className="hidden sm:inline">{t("common:complete")}</span>
                                </>
                              )}
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-xl text-blue-600 border-blue-200 hover:bg-blue-50 p-2 sm:px-3"
                            onClick={() => {
                            setSelectedReservationForChange(res);
                            setChangeDate(res.date);
                            setChangeStartTime(res.start_time || res.time);
                            setChangeEndTime(res.end_time || "");
                            setChangeMember(res.member_id);
                            
                            const mInfo = members.find(m => m.member_id === res.member_id);
                            const mCards = Array.isArray(mInfo?.member_cards) && mInfo.member_cards.length > 0
                              ? mInfo.member_cards
                              : (mInfo?.member_card ? [mInfo.member_card] : []);
                              
                            // Rezervasyonda seçilmiş kart varsa onu al, yoksa üyenin ilk kartını al:
                            const initialCard = res.card_type || mCards[0] || "";
                            setChangeCard(initialCard);
                              setChangeTrainer(res.trainer_id);
                              setChangeRoom(res.room_id);
                              setChangeBranch(res.branch || "");
                              setChangeType(res.training_type || "one_on_one");
                              setChangeAllowShared(res.allow_shared !== false);
                              setChangeAddToOnline(!!res.open_class_id);
                              setChangePackageId(res.member_package_id || "");
                              setChangeSelectedPackageInfo(null);
                              setChangeFilteredRooms(rooms);
                              setChangeFilteredTrainers(trainers.filter(t => t.is_active !== false));
                              setChangeApprovalStatus("change_pending_member");
                              setChangePaymentType(res.payment_type || (res.member_package_id ? "package" : res.is_demo ? "demo" : "package"));
                              fetchChangeMemberPackages(res.member_id);
                              setShowChangeModal(true);
                            }}
                          >
                            <Edit className="w-4 h-4 sm:mr-1" />
                            <span className="hidden sm:inline">{t("common:change")}</span>
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-xl text-red-600 border-red-200 hover:bg-red-50 p-2 sm:px-3"
                            onClick={() => {
                              setSelectedReservationForCancel(res);
                              setCancelReason("");
                              setCancelReasonCategory("");
                              setBurnSession(false);
                              setShowCancelModal(true);
                            }}
                          >
                            <X className="w-4 h-4 sm:mr-1" />
                            <span className="hidden sm:inline">{t("common:cancel")}</span>
                          </Button>
                        </>
                      )}
                      {(res.status === "cancelled" || res.status === "burned" || res.status === "cancelled_late") && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-xl text-green-600 border-green-200 hover:bg-green-50"
                          onClick={() => {
                            setSelectedReservationForRestore(res);
                            setRestoreReason("");
                            setShowRestoreModal(true);
                          }}
                        >
                          Geri Al
                        </Button>
                      )}
                      {/* Ders Açıklaması (Not) - Tamamlanmış dersler için yönetici/işletme sahibi */}
                      {res.status === "completed" && canEditLessonNote() && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-xl text-amber-700 border-amber-200 hover:bg-amber-50 p-2 sm:px-3"
                          onClick={() => openLessonNoteModal(res, "edit")}
                          title={res.lesson_note ? t("reservations:editLessonNote", "Ders açıklamasını düzenle") : t("reservations:addLessonNote", "Ders açıklaması ekle")}
                          data-testid={`lesson-note-btn-${res.reservation_id}`}
                        >
                          <FileText className="w-4 h-4 sm:mr-1" />
                          <span className="hidden sm:inline">{res.lesson_note ? t("reservations:editNote", "Notu Düzenle") : t("reservations:addNote", "Not Ekle")}</span>
                        </Button>
                      )}
                    </div>
                  </div>
                  {/* Ders Açıklaması görüntüsü */}
                  {res.lesson_note && (
                    <div
                      className="mt-3 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-900 flex gap-2"
                      data-testid={`lesson-note-display-${res.reservation_id}`}
                    >
                      <FileText className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600" />
                      <div className="flex-1 whitespace-pre-wrap break-words">{res.lesson_note}</div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })
        ) : (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-12 text-center">
              <CalendarIcon className="w-12 h-12 text-grey mx-auto mb-3" />
              <p className="text-grey">{t("reservations:noReservationsForDate")}</p>
            </CardContent>
          </Card>
        );
          })()}
          
          {/* DAY COUNT (day view shows the whole selected day, no week pagination) */}
          {getDayViewList().length > 0 && (
            <div className="flex items-center justify-end mt-4 p-3 bg-white rounded-xl shadow-sm border">
              <div className="text-sm text-grey" data-testid="day-reservation-count">
                {t("common:totalRecords", { count: getDayViewList().length })}
              </div>
            </div>
          )}
      </div>
      )}

      {/* Create Reservation Modal - DÜZENLE FORMUNA UYUMLU */}
      <Dialog open={showCreateModal} onOpenChange={(open) => {
        if (!open) {
          resetForm();
        }
        setShowCreateModal(open);
      }}>
        <DialogContent className="md:w-[95vw] md:max-w-4xl md:max-h-[90vh] overflow-y-auto p-4 md:p-6">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-navy" />
              {t('common:newReservation')}
            </DialogTitle>
          </DialogHeader>
          {businessSettings?.public_booking?.enabled && (
            <Button
              type="button"
              variant="outline"
              className="w-full sm:w-auto rounded-xl border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              onClick={() => {
                setOpenClassPrefill({
                  date: newResDate ? format(newResDate, "yyyy-MM-dd") : format(selectedDate, "yyyy-MM-dd"),
                  trainer_id: newResTrainer || undefined,
                  room_id: newResRoom || undefined,
                  start_time: newResStartTime || undefined,
                  end_time: newResEndTime || undefined,
                });
                setShowCreateModal(false);
                resetForm();
                setShowOpenClasses(true);
              }}
              data-testid="create-open-class-from-slot-btn"
            >
              <Users className="w-4 h-4 mr-2" />
              Bu saatte Online Ders Oluştur (link ile)
            </Button>
          )}
          
          <form onSubmit={(e) => {
            e.preventDefault();
            console.log("Form submitted!");
            handleCreateReservation(e);
          }} className="mt-4 space-y-6">
            
            {/* Section 1: Trainer and Member Info */}
            <div className="border-b pb-4">
              <h3 className="font-semibold text-navy mb-3 flex items-center gap-2">
                <span className="w-6 h-6 bg-navy text-white rounded-full flex items-center justify-center text-sm">1</span>
                {t('common:trainerAndMemberInfo')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Antrenör Seçimi - Branşa göre filtrelenir */}
                <div>
                  <Label className="font-medium text-sm">{t("common:trainerRequired")}</Label>
                  <Select value={newResTrainer} onValueChange={(value) => {
                    const previousTrainer = newResTrainer;
                    setNewResTrainer(value);
                    // Sadece üye seçilmemişse veya antrenör tamamen kaldırılıyorsa reset yap
                    // Üye seçiliyse antrenör değişse bile üyeyi koru
                    if (!newResMember) {
                      setSelectedExternalMember("");
                      setIsExternalMember(false);
                    }
                  }}>
                    <SelectTrigger className="rounded-xl mt-1">
                      <SelectValue placeholder={t("common:selectTrainer")} />
                    </SelectTrigger>
                    <SelectContent>
                      {(newResFilteredTrainers.length > 0 ? newResFilteredTrainers : trainers.filter(t => t.status === "active")).map((t) => (
                        <SelectItem key={t.trainer_id} value={t.trainer_id}>
                          {t.name} {t.surname}
                          {['commission_giving', 'rent_giving'].includes(t.work_type) && " (Dış Üye Alabilir)"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {newResBranch && newResFilteredTrainers.length === 0 && (
                    <p className="text-xs text-amber-600 mt-1">{t("reservations:noTrainerForBranch", { branch: newResBranch })}</p>
                  )}
                </div>
                
                {/* Dış Üye Checkbox - Sadece Prim/Kira Veren antrenör seçildiyse */}
                {newResTrainer && canHaveExternalMembers && (
                  <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                    <input
                      type="checkbox"
                      id="isExternalMember"
                      checked={isExternalMember}
                      onChange={(e) => setIsExternalMember(e.target.checked)}
                      className="w-5 h-5 text-amber-600 rounded"
                    />
                    <label htmlFor="isExternalMember" className="flex-1 cursor-pointer">
                      <span className="font-medium text-amber-700">{t("reservations:externalMember")}</span>
                      <p className="text-xs text-amber-600">
                        {t("common:fromTrainerOwnBusiness")} ({trainerOwnBusiness?.name || "..."}) {t("common:selectMember")}
                      </p>
                    </label>
                  </div>
                )}
                
                {/* Dış Üye Seçimi */}
                {isExternalMember && (
                  <>
                    <div>
                      <Label className="font-medium text-sm flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-amber-600" />
                        {t("common:externalMemberLabel")} * ({trainerOwnBusiness?.name})
                      </Label>
                      <Select value={selectedExternalMember} onValueChange={setSelectedExternalMember}>
                        <SelectTrigger className="rounded-xl mt-1 border-amber-300">
                          <SelectValue placeholder={externalMembers.length > 0 ? t("common:selectExternalMember") : t("common:memberNotFound")} />
                        </SelectTrigger>
                        <SelectContent>
                          {externalMembers
                            .filter(m => m.status !== "inactive" && m.status !== "deleted")
                            .sort((a, b) => `${a.name} ${a.surname}`.localeCompare(`${b.name} ${b.surname}`, 'tr'))
                            .map((m) => (
                            <SelectItem key={m.member_id} value={m.member_id}>
                              {m.name} {m.surname}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {selectedExternalMember && (
                      <div>
                        <Label className="font-medium text-sm">{t("reservations:externalMemberPackage")} *</Label>
                        <Select value={selectedExternalPackageId} onValueChange={setSelectedExternalPackageId}>
                          <SelectTrigger className="rounded-xl mt-1 border-amber-300">
                            <SelectValue placeholder={externalMemberPackages.length > 0 ? t("packages:selectPackage") : t("packages:noPackage")} />
                          </SelectTrigger>
                          <SelectContent>
                            {externalMemberPackages
                              .filter(mp => mp.is_active !== false && mp.remaining_sessions > 0)
                              .map((mp) => {
                                let pkgBranch = mp.package_branch || mp.package_branches?.[0] || mp.branch;
                                if (!pkgBranch) {
                                  const name = (mp.package_name || "").toLowerCase();
                                  if (name.includes("ems")) pkgBranch = "EMS";
                                  else if (name.includes("pilates")) pkgBranch = "Pilates";
                                  else if (name.includes("yoga")) pkgBranch = "Yoga";
                                  else if (name.includes("fitness")) pkgBranch = "Fitness";
                                }
                                return (
                                  <SelectItem key={mp.member_package_id} value={mp.member_package_id}>
                                    {mp.package_name} ({pkgBranch || t("common:noBranch")}) - {t("packages:remainingAmount")}: {mp.remaining_sessions} ders
                                  </SelectItem>
                                );
                              })}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    
                    {selectedExternalPackageId && selectedExternalPackageInfo && (
                      <>
                        <div className="col-span-full grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label className="font-medium text-grey-600 text-sm">{t('packages:branchFromPackage')}</Label>
                            <div className="p-3 bg-amber-100 rounded-xl text-sm mt-1">
                              {selectedExternalPackageInfo?.package_branch || selectedExternalPackageInfo?.package_branches?.[0] || selectedExternalPackageInfo?.branches?.[0] || selectedExternalPackageInfo?.branch || t("common:notSpecified")}
                            </div>
                          </div>
                          <div>
                            <Label className="font-medium text-grey-600 text-sm">{t('packages:trainingTypeFromPkg')}</Label>
                            <div className="p-3 bg-amber-100 rounded-xl text-sm mt-1">
                              {selectedExternalPackageInfo?.session_type === "one_on_one" ? t("reservations:privateLesson") : 
                               selectedExternalPackageInfo?.session_type === "duet" ? t("common:duet") : 
                               selectedExternalPackageInfo?.session_type || t("common:privateLessonType")}
                            </div>
                          </div>
                        </div>
                        <div className="col-span-full p-3 bg-amber-50 rounded-xl text-sm">
                          <p className="text-amber-700">
                            {t("reservations:trainerWillPayCommission", { rate: 
                              trainers.find(t2 => t2.trainer_id === newResTrainer)?.giving_commission_type === "percentage"
                                ? `%${trainers.find(t2 => t2.trainer_id === newResTrainer)?.giving_commission_rate || 0}`
                                : `${currencySymbol}${trainers.find(t2 => t2.trainer_id === newResTrainer)?.giving_commission_rate || 0}`
                            })}
                          </p>
                        </div>
                      </>
                    )}
                  </>
                )}
                
                {/* İç Üye Seçimi (İşletmenin kendi üyesi) */}
                {!isExternalMember && newResTrainer && (
                  <>
                    <div>
                      <Label className="font-medium text-sm">{t('common:memberRequired')}</Label>
                      <Select value={newResMember} onValueChange={setNewResMember}>
                        <SelectTrigger className="rounded-xl mt-1">
                          <SelectValue placeholder={t("common:ph_uyeSecin")} />
                        </SelectTrigger>
                        <SelectContent>
                          {members
                            .filter(m => m.status !== "inactive" && m.status !== "deleted")
                            .sort((a, b) => `${a.name} ${a.surname}`.localeCompare(`${b.name} ${b.surname}`, 'tr'))
                            .map((m) => (
                            <SelectItem key={m.member_id} value={m.member_id}>
                              {m.name} {m.surname} - {m.member_no}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <RestrictionWarning member={selectedMemberInfo} />
                    </div>

                    {/* Ödeme Tipi - Sadece İç Üye için */}
                    {newResMember && (
                      <div>
                        <Label className="font-medium text-sm">{t('common:paymentTypeRequired')}</Label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          <Button
                            type="button"
                            variant={paymentType === "package" ? "default" : "outline"}
                            className={`flex-1 min-w-[80px] rounded-xl h-10 ${paymentType === "package" ? "bg-navy" : ""}`}
                            onClick={() => { 
                              setPaymentType("package"); 
                              setSelectedCard(""); 
                            }}
                            disabled={memberPackages.length === 0}
                          >
                            {t('packages:packageLabel')} {memberPackages.length === 0 && "❌"}
                          </Button>
                          <Button
                            type="button"
                            variant={paymentType === "card" ? "default" : "outline"}
                            className={`flex-1 min-w-[80px] rounded-xl h-10 ${paymentType === "card" ? "bg-navy" : ""}`}
                            onClick={() => { 
                              setPaymentType("card"); 
                              setSelectedPackageId(""); 
                              setSelectedPackageInfo(null);
                              // Otomatik olarak üyenin kartını seç
                              if (selectedMemberInfo?.member_card) {
                                setSelectedCard(selectedMemberInfo.member_card);
                              }
                            }}
                            disabled={!selectedMemberInfo?.member_card}
                          >
                            {t('common:cardLabel')} {!selectedMemberInfo?.member_card && "❌"}
                          </Button>
                          <Button
                            type="button"
                            variant={paymentType === "demo" ? "default" : "outline"}
                            className={`flex-1 min-w-[80px] rounded-xl h-10 ${paymentType === "demo" ? "bg-green-600" : ""}`}
                            onClick={() => { 
                              setPaymentType("demo"); 
                              setSelectedCard(""); 
                              setSelectedPackageId(""); 
                              setSelectedPackageInfo(null); 
                              setNewResBranch(""); 
                            }}
                          >
                            Demo
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
              
              {/* Paket/Kart/Demo Seçimi - Sadece iç üye için */}
              {!isExternalMember && newResMember && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  {paymentType === "package" && (
                    <div>
                      <Label className="font-medium text-sm">{t('packages:packageLabel')} *</Label>
                      <Select value={selectedPackageId} onValueChange={setSelectedPackageId}>
                        <SelectTrigger className="rounded-xl mt-1">
                          <SelectValue placeholder={memberPackages.length > 0 ? t('packages:selectPackage') : t('packages:noPackage')} />
                        </SelectTrigger>
                        <SelectContent>
                          {/* Sadece seçili antrenörün branşına uyan paketleri göster */}
                          {(() => {
                            const selectedTrainer = trainers.find(t => t.trainer_id === newResTrainer);
                            
                            return memberPackages
                              .filter((mp) => {
                                // Paket aktif ve seans kalmalı
                                // is_active sadece false ise filtrele (undefined/null geçerli)
                                const isInactive = mp.is_active === false;
                                const noSessions = (mp.remaining_sessions || 0) <= 0;
                                // Süresi dolmuş paketleri (bitiş tarihi bugünden önce) gösterme
                                const todayStr = format(new Date(), "yyyy-MM-dd");
                                const isExpired = mp.end_date && String(mp.end_date).slice(0, 10) < todayStr;
                                if (isInactive || noSessions || isExpired) {
                                  return false;
                                }
                                // Antrenör seçilmediyse tüm paketleri göster
                                if (!newResTrainer) return true;
                                // Antrenörün branşlarını al
                                if (!selectedTrainer) return true;
                                const trainerBranches = selectedTrainer.branches || [];
                                // Antrenör branş tanımlı değilse tümünü göster
                                if (trainerBranches.length === 0) return true;
                                // Paketin branşını al - birden fazla kaynaktan dene
                                let pkgBranch = mp.package_branch || mp.package_branches?.[0] || mp.branch;
                                // Eğer branş hala yoksa paket adından çıkar
                                if (!pkgBranch) {
                                  const name = (mp.package_name || "").toLowerCase();
                                  if (name.includes("ems")) pkgBranch = "EMS";
                                  else if (name.includes("pilates")) pkgBranch = "Pilates";
                                  else if (name.includes("yoga")) pkgBranch = "Yoga";
                                  else if (name.includes("fitness")) pkgBranch = "Fitness";
                                }
                                // Paket branşı hala yoksa göster
                                if (!pkgBranch) return true;
                                // Antrenör bu branşı biliyor mu? (case-insensitive)
                                return trainerBranches.some(tb => tb.toLowerCase() === pkgBranch.toLowerCase());
                              })
                              .map((mp) => {
                                let pkgBranch = mp.package_branch || mp.package_branches?.[0] || mp.branch;
                                if (!pkgBranch) {
                                  const name = (mp.package_name || "").toLowerCase();
                                  if (name.includes("ems")) pkgBranch = "EMS";
                                  else if (name.includes("pilates")) pkgBranch = "Pilates";
                                  else if (name.includes("yoga")) pkgBranch = "Yoga";
                                  else if (name.includes("fitness")) pkgBranch = "Fitness";
                                }
                                return (
                                  <SelectItem key={mp.member_package_id} value={mp.member_package_id}>
                                    {mp.package_name} ({pkgBranch || t("common:noBranch")}) - {t("packages:remainingAmount")}: {mp.remaining_sessions} ders
                                  </SelectItem>
                                );
                              });
                          })()}
                        </SelectContent>
                      </Select>
                      {/* Branch mismatch warning */}
                      {branchMismatchWarning && (
                        <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-xl">
                          <p className="text-sm text-red-600 font-medium">{branchMismatchWarning}</p>
                        </div>
                      )}
                      {/* Uygun paket yoksa uyarı */}
                      {newResTrainer && memberPackages.length > 0 && (() => {
                        const selectedTrainer = trainers.find(t => t.trainer_id === newResTrainer);
                        if (!selectedTrainer) return null;
                        const trainerBranches = selectedTrainer.branches || [];
                        if (trainerBranches.length === 0) return null;
                        
                        const hasMatchingPackage = memberPackages.some(mp => {
                          // is_active sadece false ise filtrele
                          if (mp.is_active === false || (mp.remaining_sessions || 0) <= 0) return false;
                          let pkgBranch = mp.package_branch || mp.package_branches?.[0] || mp.branch;
                          if (!pkgBranch) {
                            const name = (mp.package_name || "").toLowerCase();
                            if (name.includes("ems")) pkgBranch = "EMS";
                            else if (name.includes("pilates")) pkgBranch = "Pilates";
                            else if (name.includes("yoga")) pkgBranch = "Yoga";
                            else if (name.includes("fitness")) pkgBranch = "Fitness";
                          }
                          if (!pkgBranch) return true;
                          return trainerBranches.some(tb => tb.toLowerCase() === pkgBranch.toLowerCase());
                        });
                        
                        if (!hasMatchingPackage) {
                          return (
                            <p className="text-xs text-amber-600 mt-1">
                              ⚠️ {t("common:trainerDoesNotKnowBranches", { name: selectedTrainer.name })}
                            </p>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  )}
                  
                  {paymentType === "card" && (() => {
                    const availableCards = Array.isArray(selectedMemberInfo?.member_cards) && selectedMemberInfo.member_cards.length > 0
                      ? selectedMemberInfo.member_cards
                      : (selectedMemberInfo?.member_card ? [selectedMemberInfo.member_card] : []);

                    return (
                      <div>
                        <Label className="font-medium text-sm">{t('common:cardLabel')} *</Label>
                        {availableCards.length > 1 ? (
                          <Select value={selectedCard} onValueChange={setSelectedCard}>
                            <SelectTrigger className="rounded-xl mt-1 border-blue-300">
                              <SelectValue placeholder="Kullanılacak Kartı Seçin" />
                            </SelectTrigger>
                            <SelectContent>
                              {availableCards.map(c => (
                                <SelectItem key={c} value={c}>{c}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : availableCards.length === 1 ? (
                          <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl mt-1">
                            <span className="text-blue-700 font-medium">{availableCards[0]}</span>
                          </div>
                        ) : (
                          <div className="p-3 bg-red-50 border border-red-200 rounded-xl mt-1 text-xs text-red-600">
                            Tanımlı kart bulunamadı.
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  
                  {/* Kart veya Demo seçildiğinde branş ve antrenman tipi seç */}
                  {(paymentType === "card" || paymentType === "demo") && (
                    <>
                      <div>
                        <Label className="font-medium text-sm">{t('common:branchRequired')}</Label>
                        <Select value={newResBranch} onValueChange={setNewResBranch}>
                          <SelectTrigger className="rounded-xl mt-1">
                            <SelectValue placeholder={t("common:ph_bransSecin")} />
                          </SelectTrigger>
                          <SelectContent>
                            {/* Antrenör seçiliyse sadece o antrenörün bildiği branşları göster */}
                            {(newResTrainer ? 
                              branches.filter(b => {
                                const selectedTrainer = trainers.find(t => t.trainer_id === newResTrainer);
                                return selectedTrainer?.branches?.includes(b) || !selectedTrainer?.branches?.length;
                              })
                              : branches
                            ).map((b, idx) => (
                              <SelectItem key={idx} value={b}>
                                {b}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {newResTrainer && branches.filter(b => {
                          const selectedTrainer = trainers.find(t => t.trainer_id === newResTrainer);
                          return selectedTrainer?.branches?.includes(b);
                        }).length === 0 && (
                          <p className="text-xs text-amber-600 mt-1">{t("reservations:noDefinedBranch")}</p>
                        )}
                      </div>
                      <div>
                        <Label className="font-medium text-sm">{t('common:trainingType')} *</Label>
                        <Select value={newResType} onValueChange={setNewResType}>
                          <SelectTrigger className="rounded-xl mt-1">
                            <SelectValue placeholder={t("common:ph_antrenmanTipiSecin_76e4")} />
                          </SelectTrigger>
                          <SelectContent>
                            {(businessSettings?.session_types || [
                              { id: "one_on_one", name: t("common:privateLesson") },
                              { id: "duet", name: t('reservations:duet') },
                              { id: "group", name: t("common:groupLesson") }
                            ]).map((st) => (
                              <SelectItem key={st.id || st} value={st.id || st}>
                                {st.name || st}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}
                  
                  {/* Branş ve Antrenman Tipi - Paketten */}
                  {paymentType === "package" && selectedPackageInfo && (
                    <>
                      <div>
                        <Label className="font-medium text-grey-600 text-sm">
                          {t('packages:branchFromPackage')}
                          {(selectedPackageInfo?.branches?.length || 0) >= 2 && (
                            <span className="ml-2 inline-flex items-center gap-1 text-[10px] bg-purple-100 text-purple-700 rounded px-1.5 py-0.5 font-medium">🔗 Combo</span>
                          )}
                        </Label>
                        {(selectedPackageInfo?.branches?.length || 0) >= 2 ? (
                          // Combo package: let user pick
                          <Select value={newResBranch} onValueChange={setNewResBranch}>
                            <SelectTrigger className="rounded-xl mt-1" data-testid="combo-branch-select">
                              <SelectValue placeholder="Bu derste hangi branş?" />
                            </SelectTrigger>
                            <SelectContent>
                              {selectedPackageInfo.branches.map((b) => (
                                <SelectItem key={b} value={b}>
                                  {getLocalizedBranch ? getLocalizedBranch(b) : b}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <div className="p-3 bg-slate-100 rounded-xl text-sm mt-1">
                            {newResBranch || selectedPackageInfo?.branches?.[0] || t("common:notSpecified")}
                          </div>
                        )}
                      </div>
                      <div>
                        <Label className="font-medium text-grey-600 text-sm">{t('packages:trainingTypeFromPkg')}</Label>
                        <div className="p-3 bg-slate-100 rounded-xl text-sm mt-1">
                          {newResType === "one_on_one" ? t("reservations:privateLesson") : newResType === "duet" ? t('reservations:duet') : t("reservations:groupLesson")}
                        </div>
                      </div>
                    </>
                  )}
                  
                  {/* Demo bilgisi */}
                  {paymentType === "demo" && newResBranch && (
                    <div className="col-span-2 p-3 bg-green-50 border border-green-200 rounded-xl">
                      <p className="text-sm text-green-700">
                        <strong>{newResBranch}</strong> {t("common:demoLessonCreated")}.
                        <br />
                        <span className="text-xs">
                          {t("common:demoDuration")}: {businessSettings?.branch_demo_durations?.[newResBranch] || 30} dakika
                        </span>
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Section 2: {t('common:roomSelection')} */}
            {/* Internal member: package/card/demo seçilmiş olmalı */}
            {/* External member: external package seçilmiş olmalı */}
            {(
              (!isExternalMember && newResMember && newResTrainer && (paymentType === "package" ? (selectedPackageId && newResBranch) : (paymentType === "card" ? selectedCard : (paymentType === "demo" ? newResBranch : false)))) ||
              (isExternalMember && selectedExternalMember && selectedExternalPackageId && newResTrainer)
            ) && (
              <div className="border-b pb-4">
                <h3 className="font-semibold text-navy mb-3 flex items-center gap-2">
                  <span className="w-6 h-6 bg-navy text-white rounded-full flex items-center justify-center text-sm">2</span>
                  {t('common:roomSelection')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="font-medium text-sm">{t("admin:roomRequired2")}</Label>
                    <Select value={newResRoom} onValueChange={(v) => v && setNewResRoom(v)}>
                      <SelectTrigger className="rounded-xl mt-1">
                        <SelectValue placeholder={t("common:ph_odaSecin")} />
                      </SelectTrigger>
                      <SelectContent>
                        {filteredRooms.map((r) => {
                          // Branşa özel kapasite varsa onu göster, yoksa genel kapasiteyi
                          const branchCapacity = (newResBranch && r.branch_capacities && r.branch_capacities[newResBranch] !== undefined)
                            ? r.branch_capacities[newResBranch]
                            : r.capacity;
                          return (
                            <SelectItem key={r.room_id} value={r.room_id}>
                              {r.name} ({t('common:capacity')}: {branchCapacity})
                            </SelectItem>
                          );
                        })}
                        {filteredRooms.length === 0 && (
                          <div className="p-2 text-sm text-gray-500 text-center">
                            {t("common:noSuitableRoomFound")}
                          </div>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Seçilen antrenör gösterimi (readonly) */}
                  <div>
                    <Label className="font-medium text-grey-600 text-sm">{t("reservations:selectedTrainer")}</Label>
                    <div className="p-3 bg-slate-100 rounded-xl text-sm mt-1">
                      {trainers.find(t => t.trainer_id === newResTrainer)?.name || t("common:notSelected")} {trainers.find(t => t.trainer_id === newResTrainer)?.surname || ""}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Section 3: Tarih ve Saat */}
            {/* Hem internal hem external member için çalışmalı */}
            {(
              (!isExternalMember && newResMember && newResTrainer && newResRoom) ||
              (isExternalMember && selectedExternalMember && newResTrainer && newResRoom)
            ) && (
              <div className="border-b pb-4">
                <h3 className="font-semibold text-navy mb-3 flex items-center gap-2">
                  <span className="w-6 h-6 bg-navy text-white rounded-full flex items-center justify-center text-sm">3</span>
                  {t('common:dateAndTime')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="font-medium text-sm">{t("common:dateRequired")}</Label>
                    <Popover open={datePopoverOpen} onOpenChange={setDatePopoverOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal rounded-xl mt-1 h-11"
                          data-testid="new-res-date-picker"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {newResDate ? format(newResDate, "dd MMMM yyyy", { locale: getDateLocale() }) : t('common:selectDate')}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={newResDate}
                          onSelect={(date) => {
                            if (date) {
                              setNewResDate(date);
                              setDatePopoverOpen(false); // Close popover after selection
                            }
                          }}
                          locale={tr}
                          initialFocus={false}
                        />
                      </PopoverContent>
                    </Popover>
                    {/* Hızlı Tarih Butonları */}
                    <div className="flex gap-1 mt-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className={`text-xs rounded-lg flex-1 ${newResDate && isSameDay(newResDate, new Date()) ? 'bg-navy text-white hover:bg-navy/90' : ''}`}
                        onClick={() => setNewResDate(new Date())}
                        data-testid="quick-date-today"
                      >
                        {t('common:today')}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className={`text-xs rounded-lg flex-1 ${newResDate && isSameDay(newResDate, addDays(new Date(), 1)) ? 'bg-navy text-white hover:bg-navy/90' : ''}`}
                        onClick={() => setNewResDate(addDays(new Date(), 1))}
                        data-testid="quick-date-tomorrow"
                      >
                        {t('common:tomorrow')}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className={`text-xs rounded-lg flex-1 ${newResDate && isSameDay(newResDate, addDays(new Date(), 7)) ? 'bg-navy text-white hover:bg-navy/90' : ''}`}
                        onClick={() => setNewResDate(addDays(new Date(), 7))}
                        data-testid="quick-date-week"
                      >
                        {t('common:oneWeek')}
                      </Button>
                    </div>
                  </div>
                  <div>
                    <Label className="font-medium text-sm">{t('common:startRequired')}</Label>
                    <div className="mt-1">
                      <TimePicker
                        value={newResStartTime}
                        onChange={setNewResStartTime}
                        placeholder={t("common:ph_saatSecin")}
                        minHour={8}
                        maxHour={22}
                        minuteStep={5}
                        minTime={newResDate ? getMinTime(format(newResDate, "yyyy-MM-dd")) : undefined}
                        dataTestId="new-res-start-time"
                      />
                    </div>
                    {newResDate && newResStartTime && !isTimeSlotValid(newResDate, newResStartTime) && (
                      <p className="text-xs text-red-500 mt-1">
                        ⚠️ En az {getMinAdvanceText()} önceden rezervasyon yapılmalı
                      </p>
                    )}
                  </div>
                  <div>
                    <Label className="font-medium text-sm">
                      {t('common:endRequired')} {selectedPackageInfo?.session_duration && <span className="text-xs text-grey">({t('common:recommended')}: {selectedPackageInfo.session_duration}dk)</span>}
                    </Label>
                    <div className="mt-1">
                      <TimePicker
                        value={newResEndTime}
                        onChange={setNewResEndTime}
                        placeholder={t("common:ph_bitisSaati")}
                        minHour={8}
                        maxHour={23}
                        minuteStep={5}
                        dataTestId="new-res-end-time"
                      />
                    </div>
                    {newResStartTime && newResEndTime && newResEndTime <= newResStartTime && (
                      <p className="text-xs text-red-500 mt-1">
                        ⚠️ {t("common:endTimeAfterStart")}
                      </p>
                    )}
                    {newResDate && newResEndTime && !isEndTimeWithinBusinessHours(newResDate, newResEndTime) && (
                      <p className="text-xs text-red-500 mt-1">
                        ⚠️ {t("common:endTimeExceedsClosingShort")} ({getBusinessClosingTime(newResDate)})
                      </p>
                    )}
                  </div>
                </div>
                
                {/* Müsaitlik Durumu */}
                {newResStartTime && (
                  <div className="p-3 bg-slate-50 rounded-xl mt-4">
                    <p className="font-medium text-navy text-sm mb-2">{t("common:availabilityStatus")}</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {/* ODA MÜSAİTLİĞİ */}
                      {roomAvailability && (
                        <div className={`flex items-center gap-2 text-sm ${
                          roomAvailability.is_locked 
                            ? 'text-red-600' 
                            : roomAvailability.mk_available > 0 
                              ? 'text-green-600' 
                              : 'text-red-600'
                        }`}>
                          {roomAvailability.is_locked || roomAvailability.mk_available === 0 
                            ? <AlertCircle className="w-4 h-4" /> 
                            : <CheckCircle2 className="w-4 h-4" />}
                          <span>
                            {t("common:roomLabel")} {roomAvailability.is_locked 
                              ? t("common:roomLocked") 
                              : roomAvailability.mk_available > 0 
                                ? `${t("common:trainerAvailable")} (${roomAvailability.mk_available} ${t("common:slotsAvailable")})`
                                : t("common:roomNotAvailable")}
                          </span>
                        </div>
                      )}
                      
                      {/* ANTRENÖR MÜSAİTLİĞİ */}
                      {trainerAvailability && (
                        <div className={`flex items-center gap-2 text-sm ${
                          branchMismatchWarning 
                            ? 'text-red-600' 
                            : trainerAvailability.is_available ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {(branchMismatchWarning || !trainerAvailability.is_available) 
                            ? <AlertCircle className="w-4 h-4" /> 
                            : <CheckCircle2 className="w-4 h-4" />}
                          <span>
                            {branchMismatchWarning 
                              ? `${t("common:trainerLabel")}: ${t("reservations:trainerCannotDoThisBranch")}`
                              : `${t("common:trainerLabel")} ${trainerAvailability.is_available 
                              ? (() => {
                                  const info = trainerAvailability.training_type_info;
                                  if (info && info.max_participants > 1) {
                                    return `${t("common:trainerAvailable")} (${info.type} - ${info.remaining} ${t("common:slotsAvailable")})`;
                                  }
                                  return t("common:trainerAvailable");
                                })()
                              : (trainerAvailability.reason || t('reservations:notAvailable'))}`}
                          </span>
                        </div>
                      )}
                      {!trainerAvailability && branchMismatchWarning && (
                        <div className="flex items-center gap-2 text-sm text-red-600">
                          <AlertCircle className="w-4 h-4" />
                          <span>{t("common:trainerLabel")}: {t("reservations:trainerCannotDoThisBranch")}</span>
                        </div>
                      )}
                      
                      {/* Müsaitlik bilgisi yoksa kontrol etme mesajı */}
                      {!roomAvailability && newResRoom && (
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <CheckCircle2 className="w-4 h-4" />
                          <span>{t("common:roomLabel")} {t('qr:checking')}</span>
                        </div>
                      )}
                      {!trainerAvailability && newResTrainer && (
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <CheckCircle2 className="w-4 h-4" />
                          <span>{t("common:trainerLabel")} {t('qr:checking')}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Section 4: {t('common:optionsAndSummary')} */}
            {/* Hem internal hem external member için çalışmalı */}
            {(
              (!isExternalMember && newResMember && newResTrainer && newResRoom && newResStartTime) ||
              (isExternalMember && selectedExternalMember && selectedExternalPackageId && newResTrainer && newResRoom && newResStartTime)
            ) && (
              <div>
                <h3 className="font-semibold text-navy mb-3 flex items-center gap-2">
                  <span className="w-6 h-6 bg-navy text-white rounded-full flex items-center justify-center text-sm">4</span>
                  {t('common:optionsAndSummary')}
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Sol: Durum ve Tekrar */}
                  <div className="space-y-3">
                    {/* ÇAKIŞMA UYARILARI */}
                    {reservationConflicts.length > 0 && (
                      <div className="space-y-2">
                        {reservationConflicts.map((conflict, idx) => (
                          <div 
                            key={idx}
                            className={`p-3 rounded-xl border flex items-start gap-2 ${
                              conflict.severity === "error" 
                                ? "bg-red-50 border-red-300 text-red-800" 
                                : "bg-orange-50 border-orange-300 text-orange-800"
                            }`}
                          >
                            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                              <p className="text-sm font-medium">{conflict.i18n_key ? t(conflict.i18n_key, conflict.message) : conflict.message}</p>
                              {conflict.severity === "warning" && (
                                <p className="text-xs mt-1 opacity-80">
                                  {t("reservations:cancelExistingRequestToContinue", "Devam etmek için bu talebi iptal etmeniz gerekebilir")}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* DURUM SEÇİMİ - Yöneticiler ve izni olanlar için */}
                    {/* Online Rezervasyon kapalıysa direkt onaylı olmalı */}
                    {businessSettings?.display_settings?.allow_online_booking === false ? (
                      <div className="p-3 bg-green-50 border border-green-200 rounded-xl">
                        <p className="text-sm text-green-800 flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" />
                          {t('admin:statusAutoApproved')}
                        </p>
                        <p className="text-xs text-green-600 mt-1">
                          {t('admin:onlineReservationClosedAutoApprove')}
                        </p>
                      </div>
                    ) : (isExternalMember ? externalMembers.find(m => m.member_id === selectedExternalMember)?.is_app_registered === false : selectedMemberInfo?.is_app_registered === false) ? (
                      /* Kayıt olmayan üye (normal veya dış) - otomatik onay, radio butonlar gösterilmez */
                      <div className="p-3 bg-green-50 border border-green-200 rounded-xl">
                        <div className="flex items-center gap-2 text-green-800">
                          <CheckCircle className="w-4 h-4" />
                          <span className="font-medium text-sm">{t("reservations:autoApprovedReservation")}</span>
                        </div>
                        <p className="text-xs text-green-700 mt-1">
                          {t("admin:memberNotRegisteredAutoApprove")}
                        </p>
                      </div>
                    ) : (userPermissions?.is_manager || userPermissions?.is_owner || userPermissions?.can_approve_reservations) ? (
                      <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl">
                        <Label className="font-medium text-sm mb-2 block">{t("common:reservationStatus")}</Label>
                        <div className="flex gap-4">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input 
                              type="radio" 
                              name="resStatus" 
                              value="pending_member_approval" 
                              checked={newResStatus === "pending_member_approval"} 
                              onChange={() => setNewResStatus("pending_member_approval")} 
                              className="w-4 h-4 accent-amber-500" 
                            />
                            <span className="text-sm">{t("common:sendToMember")}</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input 
                              type="radio" 
                              name="resStatus" 
                              value="approved" 
                              checked={newResStatus === "approved"} 
                              onChange={() => setNewResStatus("approved")} 
                              className="w-4 h-4 accent-green-500" 
                            />
                            <span className="text-sm">{t('admin:confirm')}</span>
                          </label>
                        </div>
                        <p className="text-xs text-grey mt-2">
                          {newResStatus === "approved" 
                            ? t("admin:approvedReservationActive") 
                            : t("admin:willBeSentToMemberForApproval")}
                        </p>
                      </div>
                    ) : (
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
                        <p className="text-sm text-amber-800">
                          {t('admin:statusPendingMemberApproval')}
                        </p>
                        <p className="text-xs text-amber-600 mt-1">
                          {t("admin:reservationWillBeSentToMember")}
                        </p>
                      </div>
                    )}
                    
                    {/* Oda Kilitleme */}
                    {roomAvailability && roomAvailability.total_capacity > 1 && newResType === "one_on_one" && (
                      <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                        <Checkbox id="allowShared" checked={!allowShared} onCheckedChange={(checked) => setAllowShared(!checked)} />
                        <Label htmlFor="allowShared" className="text-sm cursor-pointer">{t('common:lockRoomCompletely')}</Label>
                      </div>
                    )}
                    
                    <div className="flex items-center gap-3 p-3 bg-sky-50 border border-sky-200 rounded-xl">
                      <Checkbox id="addToOnline" checked={addToOnline} onCheckedChange={(c) => setAddToOnline(!!c)} data-testid="new-res-add-to-online" />
                      <Label htmlFor="addToOnline" className="text-sm cursor-pointer">Online Ders Programına ekle</Label>
                    </div>

                    {/* {t('common:recurringReservation')} */}
                    <div className="p-3 bg-purple-50 border border-purple-200 rounded-xl space-y-3">
                      <div className="flex items-center justify-between">
                        <Label className="font-medium flex items-center gap-2 text-purple-800">
                          <RotateCcw className="w-4 h-4" />
                          {t('common:recurringReservation')}
                        </Label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={repeatEnabled} onChange={(e) => { setRepeatEnabled(e.target.checked); setRepeatType("weekly"); setRepeatDays([]); setRepeatEndMode("count"); setRepeatUntil(""); setRepeatCount(4); }} className="w-4 h-4" data-testid="recurring-toggle" />
                          <span className="text-sm">{t('common:repeat')}</span>
                        </label>
                      </div>
                      {!isExternalMember && (
                        <RecurringTemplateBar
                          member={tplMember}
                          current={{ days: repeatEnabled && repeatType === "weekdays" && newResDate ? [...new Set([...repeatDays, newResDate.getDay()])] : [], start_time: newResStartTime, end_time: newResEndTime, room_id: newResRoom, trainer_id: newResTrainer, branch: newResBranch }}
                          onApply={applyRecurringTemplate}
                          onSaved={(m) => setSavedTemplates((s) => ({ ...s, [m.member_id]: m.recurring_template }))}
                        />
                      )}
                      
                      {repeatEnabled && (
                        <div className="space-y-3 pt-2 border-t border-purple-200">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label className="text-xs">{t("common:type")}</Label>
                              <Select value={repeatType} onValueChange={setRepeatType}>
                                <SelectTrigger className="rounded-lg h-9 mt-1" data-testid="recurring-type-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="daily">{t("common:herGun")}</SelectItem>
                                  <SelectItem value="weekly">{t('common:everyWeek')}</SelectItem>
                                  <SelectItem value="weekdays">Haftanın belirli günleri</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-xs">Bitiş</Label>
                              <Select value={repeatEndMode} onValueChange={setRepeatEndMode}>
                                <SelectTrigger className="rounded-lg h-9 mt-1" data-testid="recurring-end-mode"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="count">Ders sayısına göre</SelectItem>
                                  <SelectItem value="date">Bitiş tarihine göre</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          {repeatType === "weekdays" && <WeekdayPicker days={repeatDays} fixedDay={newResDate?.getDay()} onChange={setRepeatDays} />}
                          {repeatEndMode === "count" ? (
                            <div>
                              <Label className="text-xs">Toplam ders sayısı</Label>
                              <Select value={String(repeatCount)} onValueChange={(v) => setRepeatCount(Number(v))}>
                                <SelectTrigger className="rounded-lg h-9 mt-1" data-testid="recurring-count-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {[2, 3, 4, 5, 6, 7, 8, 10, 12, 16, 20, 24].filter(n => paymentType !== "package" || isExternalMember || selectedPackageInfo?.remaining_sessions == null || n <= selectedPackageInfo.remaining_sessions).map(n => (
                                    <SelectItem key={n} value={String(n)}>{n} ders</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          ) : (
                            <div>
                              <Label className="text-xs">Son tarih</Label>
                              <DatePickerTR value={repeatUntil} onChange={setRepeatUntil} returnDateString minDate={newResDate || undefined} className="rounded-lg w-full mt-1" data-testid="recurring-until-date" />
                              {!repeatUntil && <p className="text-xs text-red-600 mt-1" data-testid="recurring-until-required">Tekrarın biteceği son tarihi seçin</p>}
                            </div>
                          )}
                          <RecurringPreview
                            dates={repeatDates}
                            slot={{ startTime: newResStartTime, endTime: newResEndTime, trainerId: newResTrainer, roomId: newResRoom, branch: newResBranch, trainingType: newResType, memberId: isExternalMember ? null : newResMember, businessId }}
                            maxAllowed={paymentType === "package" && !isExternalMember ? selectedPackageInfo?.remaining_sessions : null}
                            onStatus={setRepeatStatus}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Sağ: Özet */}
                  <div className="space-y-3">
                    <div className={`p-4 rounded-xl space-y-2 ${isExternalMember ? "bg-amber-50 border border-amber-200" : "bg-blue-50"}`}>
                      <p className={`font-semibold ${isExternalMember ? "text-amber-700" : "text-navy"}`}>
                        {isExternalMember ? t("common:externalMemberReservationSummary") : t("reservations:reservationSummary")}
                      </p>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-grey-600">{isExternalMember ? t("common:externalMemberLabel2") : t("common:memberLabel")}</span>
                          <span className="font-medium">
                            {isExternalMember 
                              ? (externalMembers.find(m => m.member_id === selectedExternalMember)?.name || "-") + " " + (externalMembers.find(m => m.member_id === selectedExternalMember)?.surname || "")
                              : (members.find(m => m.member_id === newResMember)?.name || "-") + " " + (members.find(m => m.member_id === newResMember)?.surname || "")
                            }
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-grey-600">{t("common:paymentLabel")}</span>
                          <span className="font-medium">
                            {isExternalMember 
                              ? `${t("packages:externalMemberPkg")}: ${selectedExternalPackageInfo?.package_name || "-"}`
                              : (paymentType === "package" ? `${t("packages:packageLabel")}: ${selectedPackageInfo?.package_name || "-"}` : 
                                 paymentType === "card" ? `${t("common:cardLabel")}: ${selectedCard || "-"}` : 
                                 t('reservations:demo'))
                            }
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-grey-600">{t("common:branchLabel")}</span>
                          <span className="font-medium">{newResBranch || "-"}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-grey-600">{t("common:trainerLabel")}</span>
                          <span className="font-medium">{trainers.find(t => t.trainer_id === newResTrainer)?.name || "-"} {trainers.find(t => t.trainer_id === newResTrainer)?.surname || ""}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-grey-600">{t("common:roomLabel")}</span>
                          <span className="font-medium">{rooms.find(r => r.room_id === newResRoom)?.name || "-"}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-grey-600">{t("common:dateLabel2")}</span>
                          <span className="font-medium">{newResDate ? format(newResDate, "d MMMM yyyy, EEEE", { locale: getDateLocale() }) : "-"}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-grey-600">{t("common:timeLabel")}</span>
                          <span className="font-medium">{newResStartTime || "-"} - {newResEndTime || "-"}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-grey-600">{t("common:statusLabel")}</span>
                          <span className={`font-medium ${newResStatus === "approved" ? "text-green-600" : "text-amber-600"}`}>
                            {newResStatus === "approved" ? t("admin:approved") : t("admin:pendingMemberApproval")}
                          </span>
                        </div>
                        {!allowShared && (
                          <div className="text-xs text-purple-600 pt-2 border-t border-blue-200">
                            🔒 {t('common:roomLockedPrivateLesson')}
                          </div>
                        )}
                      </div>
                      {repeatEnabled && (
                        <p className="text-xs text-purple-700 mt-2 pt-2 border-t border-blue-200">
                          📅 {repeatType === "daily" ? t("common:everyDay") : repeatType === "weekdays" ? "Seçili günlerde" : "Her hafta"} toplam <strong>{repeatDates.length}</strong> rezervasyon oluşturulacak
                        </p>
                      )}
                    </div>
                    
                    {/* ÇAKIŞMA VARSA UYARI */}
                    {reservationConflicts.some(c => c.severity === "error") && (
                      <div className="p-3 bg-red-100 border border-red-300 rounded-xl">
                        <p className="text-sm text-red-800 font-medium">
                          ⚠️ {t("qr:conflictExistsCancelFirst")}
                        </p>
                      </div>
                    )}
                    
                    <Button
                      type="submit"
                      className={`w-full rounded-xl h-12 text-base ${
                        reservationConflicts.some(c => c.severity === "error")
                          ? "bg-grey-400 cursor-not-allowed"
                          : reservationConflicts.length > 0
                            ? "bg-orange-500 hover:bg-orange-600"
                            : newResStatus === "approved"
                              ? "bg-green-600 hover:bg-green-700"
                              : "bg-amber-500 hover:bg-amber-600"
                      }`}
                      disabled={
                        submittingReservation ||
                        !!branchMismatchWarning ||
                        reservationConflicts.some(c => c.severity === "error") ||
                        (roomAvailability && roomAvailability.is_locked) ||
                        (isExternalMember 
                          ? (!selectedExternalMember || !selectedExternalPackageId)
                          : ((paymentType === "package" && (!selectedPackageId || !newResBranch)) || (paymentType === "card" && !selectedCard) || (paymentType === "demo" && !newResBranch)))
                      }
                    >
                      {submittingReservation && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      {submittingReservation ? (
                        t("common:creating")
                      ) : checkingConflicts ? (
                        t('qr:checking')
                      ) : (roomAvailability && roomAvailability.is_locked) ? (
                        t("common:roomLockedCannotSend")
                      ) : reservationConflicts.some(c => c.severity === "error") ? (
                        t("common:conflictCannotSend")
                      ) : repeatEnabled ? (
                        newResStatus === "approved" 
                          ? `${repeatDates.length} ${t("admin:createApprovedReservation")}`
                          : `${repeatDates.length} ${t("common:createRequest")}`
                      ) : reservationConflicts.length > 0 ? (
                        t("common:sendDespiteWarning")
                      ) : newResStatus === "approved" ? (
                        t("admin:createApprovedReservation")
                      ) : (
                        t("admin:sendToMemberApproval")
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </form>
        </DialogContent>
      </Dialog>

      {/* Change Reservation Modal - YENİ TASARIM */}
      <Dialog open={showChangeModal} onOpenChange={(open) => {
        setShowChangeModal(open);
        if (!open) {
          setSelectedReservationForChange(null);
          setChangeDate("");
          setChangeStartTime("");
          setChangeEndTime("");
          setChangeMember("");
          setChangeTrainer("");
          setChangeRoom("");
          setChangeBranch("");
          setChangeType("one_on_one");
          setChangeAllowShared(true);
          setChangePackageId("");
          setChangeMemberPackages([]);
          setChangeRoomAvailability(null);
          setChangeTrainerAvailability(null);
        }
      }}>
        <DialogContent className="md:w-[95vw] md:max-w-4xl md:max-h-[90vh] overflow-y-auto p-4 md:p-6">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-navy" />
              {t("common:editReservation")}
            </DialogTitle>
          </DialogHeader>
          
          {selectedReservationForChange && (
            <div className="mt-4 space-y-6">
              {/* Section 1: {t("common:memberAndPaymentInfo")} */}
              <div className="border-b pb-4">
                <h3 className="font-semibold text-navy mb-3 flex items-center gap-2">
                  <span className="w-6 h-6 bg-navy text-white rounded-full flex items-center justify-center text-sm">1</span>
                  {t("common:memberAndPaymentInfo")}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Üye - Değiştirilemez */}
                  <div>
                    <Label className="font-medium text-grey-600 text-sm">
                      {selectedReservationForChange?.payment_type === "external_member" ? "Dış Üye (değiştirilemez)" : "Üye (değiştirilemez)"}
                    </Label>
                    <div className={`p-3 rounded-xl text-sm mt-1 ${selectedReservationForChange?.payment_type === "external_member" ? "bg-amber-100 border border-amber-200" : "bg-slate-100"}`}>
                      {selectedReservationForChange?.payment_type === "external_member" 
                        ? (selectedReservationForChange?.member_name || `Dış Üye #${changeMember}`)
                        : (selectedReservationForChange?.member_name || `${members.find(m => m.member_id === changeMember)?.name || ""} ${members.find(m => m.member_id === changeMember)?.surname || ""}`.trim() || "Üye bilgisi yükleniyor...")
                      }
                    </div>
                  </div>

                  {/* Ödeme Tipi - Dış Üye değilse değiştirilebilir */}
                  {selectedReservationForChange?.payment_type !== "external_member" ? (
                    <div>
                      <Label className="font-medium text-sm">Ödeme Tipi *</Label>
                      <div className="flex gap-2 mt-1">
                        <Button
                          type="button"
                          variant={changePaymentType === "package" ? "default" : "outline"}
                          className={`flex-1 rounded-xl h-10 ${changePaymentType === "package" ? "bg-navy" : ""}`}
                          onClick={() => {
                            setChangePaymentType("package");
                            setChangePackageId("");
                          }}
                        >
                          Paket
                        </Button>
                        <Button
                          type="button"
                          variant={changePaymentType === "card" ? "default" : "outline"}
                          className={`flex-1 rounded-xl h-10 ${changePaymentType === "card" ? "bg-navy" : ""}`}
                          onClick={() => {
                            setChangePaymentType("card");
                            setChangePackageId("");
                          }}
                          disabled={!members.find(m => m.member_id === changeMember)?.member_card}
                        >
                          Kart {!members.find(m => m.member_id === changeMember)?.member_card && "❌"}
                        </Button>
                        <Button
                          type="button"
                          variant={changePaymentType === "demo" ? "default" : "outline"}
                          className={`flex-1 rounded-xl h-10 ${changePaymentType === "demo" ? "bg-green-600" : ""}`}
                          onClick={() => {
                            setChangePaymentType("demo");
                            setChangePackageId("");
                          }}
                        >
                          Demo
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <Label className="font-medium text-grey-600 text-sm">Ödeme Tipi (değiştirilemez)</Label>
                      <div className="p-3 bg-amber-100 border border-amber-200 rounded-xl text-sm mt-1">
                        <span className="text-amber-700 font-medium">{t("reservations:externalMemberPackage")}</span>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Paket veya Kart Bilgisi */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  {/* Paket seçimi - Antrenör branşına göre filtrelenmiş */}
                  {changePaymentType === "package" && (
                    <div>
                      <Label className="font-medium text-sm">Paket *</Label>
                      <Select value={changePackageId} onValueChange={setChangePackageId}>
                        <SelectTrigger className="rounded-xl mt-1">
                          <SelectValue placeholder={changeMemberPackages.length > 0 ? "Paket seçin" : "Üyenin paketi yok"}>
                            {changePackageId && changeMemberPackages.find(p => p.member_package_id === changePackageId) && (
                              `${changeMemberPackages.find(p => p.member_package_id === changePackageId)?.package_name} - ${t("packages:remainingAmount")}: ${changeMemberPackages.find(p => p.member_package_id === changePackageId)?.remaining_sessions} ders`
                            )}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {/* Antrenör seçiliyse, sadece o antrenörün branşına uyan paketleri göster */}
                          {changeMemberPackages
                            .filter((mp) => {
                              // Paket aktif ve seans kalmalı
                              if (!mp.is_active || mp.remaining_sessions <= 0) return false;
                              // Antrenör seçilmediyse tüm paketleri göster
                              if (!changeTrainer) return true;
                              // Antrenörün branşlarını al
                              const selectedTrainer = trainers.find(t => t.trainer_id === changeTrainer);
                              if (!selectedTrainer) return true;
                              const trainerBranches = selectedTrainer.branches || [];
                              if (trainerBranches.length === 0) return true;
                              // Paketin branşını al
                              const pkgBranch = mp.package_branch || mp.package_branches?.[0] || mp.branch;
                              if (!pkgBranch) return true;
                              // Antrenör bu branşı biliyor mu?
                              return trainerBranches.includes(pkgBranch);
                            })
                            .map((mp) => (
                              <SelectItem key={mp.member_package_id} value={mp.member_package_id}>
                                {mp.package_name || t('packages:package')} ({getLocalizedBranch(mp.package_branch || mp.branch) || t("common:noBranch")}) - {t("packages:remainingAmount")}: {mp.remaining_sessions} ders
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      {/* Uygun paket yoksa uyarı */}
                      {changeTrainer && changeMemberPackages.filter(mp => {
                        if (!mp.is_active || mp.remaining_sessions <= 0) return false;
                        const selectedTrainer = trainers.find(t => t.trainer_id === changeTrainer);
                        if (!selectedTrainer) return true;
                        const trainerBranches = selectedTrainer.branches || [];
                        if (trainerBranches.length === 0) return true;
                        const pkgBranch = mp.package_branch || mp.package_branches?.[0] || mp.branch;
                        if (!pkgBranch) return true;
                        return trainerBranches.includes(pkgBranch);
                      }).length === 0 && changeMemberPackages.some(mp => mp.is_active && mp.remaining_sessions > 0) && (
                        <p className="text-xs text-amber-600 mt-1">
                          Seçili antrenör ({trainers.find(t => t.trainer_id === changeTrainer)?.name}) bu üyenin paketlerindeki branşları bilmiyor.
                        </p>
                      )}
                    </div>
                  )}
                  
                  {/* Kart bilgisi - Çoklu Kart Uyumlu */}
                  {changePaymentType === "card" && (() => {
                    const currentMember = members.find(m => m.member_id === changeMember);
                    const memberCardsList = Array.isArray(currentMember?.member_cards) && currentMember.member_cards.length > 0
                      ? currentMember.member_cards
                      : (currentMember?.member_card ? [currentMember.member_card] : []);

                    return (
                      <div>
                        <Label className="font-medium text-sm">Üyelik Kartı *</Label>
                        {memberCardsList.length > 1 ? (
                          <Select 
                              value={changeCard || memberCardsList[0]} 
                              onValueChange={(val) => {
                                setChangeCard(val);
                                setSelectedReservationForChange(prev => ({ ...prev, card_type: val }));
                              }}
                            >
                            <SelectTrigger className="rounded-xl mt-1 border-blue-300">
                              <SelectValue placeholder="Kullanılacak Kartı Seçin" />
                            </SelectTrigger>
                            <SelectContent>
                              {memberCardsList.map(c => (
                                <SelectItem key={c} value={c}>{c}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : memberCardsList.length === 1 ? (
                          <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl mt-1">
                            <span className="text-blue-700 font-medium">{memberCardsList[0]}</span>
                          </div>
                        ) : (
                          <div className="p-3 bg-red-50 border border-red-200 rounded-xl mt-1 text-xs text-red-600">
                            Üyenin tanımlı kartı bulunmuyor.
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  
                  {/* Demo bilgisi */}
                  {changePaymentType === "demo" && (
                    <div className="col-span-2 p-3 bg-green-50 border border-green-200 rounded-xl">
                      <p className="text-sm text-green-700">
                        <strong>{selectedReservationForChange?.branch || changeBranch || "Seçili branş"}</strong> branşı için demo dersi
                      </p>
                    </div>
                  )}
                </div>
                
                {/* Branş ve Antrenman Tipi - Paketten */}
                {changeSelectedPackageInfo && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div>
                      <Label className="font-medium text-grey-600 text-sm">
                        {t('packages:branchFromPackage')}
                        {(changeSelectedPackageInfo?.package_branches?.length || 0) >= 2 && (
                          <span className="ml-2 inline-flex items-center gap-1 text-[10px] bg-purple-100 text-purple-700 rounded px-1.5 py-0.5 font-medium">🔗 Combo</span>
                        )}
                      </Label>
                      {(changeSelectedPackageInfo?.package_branches?.length || 0) >= 2 ? (
                        <Select value={changeBranch} onValueChange={setChangeBranch}>
                          <SelectTrigger className="rounded-xl mt-1" data-testid="change-combo-branch-select">
                            <SelectValue placeholder="Bu derste hangi branş?" />
                          </SelectTrigger>
                          <SelectContent>
                            {changeSelectedPackageInfo.package_branches.map((b) => (
                              <SelectItem key={b} value={b}>
                                {getLocalizedBranch ? getLocalizedBranch(b) : b}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <div className="p-3 bg-slate-100 rounded-xl text-sm mt-1">
                          {changeBranch || t("common:notSpecified")}
                        </div>
                      )}
                    </div>
                    <div>
                      <Label className="font-medium text-grey-600 text-sm">{t('packages:trainingTypeFromPkg')}</Label>
                      <div className="p-3 bg-slate-100 rounded-xl text-sm mt-1">
                        {changeType === "one_on_one" ? t("reservations:privateLesson") : changeType === "duet" ? t('reservations:duet') : t("reservations:groupLesson")}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Section 2: {t("common:trainerAndRoom")} */}
              <div className="border-b pb-4">
                <h3 className="font-semibold text-navy mb-3 flex items-center gap-2">
                  <span className="w-6 h-6 bg-navy text-white rounded-full flex items-center justify-center text-sm">2</span>
                  {t("common:trainerAndRoom")}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Antrenör */}
                  <div>
                    <Label className="font-medium text-sm">{t("common:trainerRequired")} {changeBranch && <span className="text-xs text-grey">({changeBranch})</span>}</Label>
                    <Select value={changeTrainer} onValueChange={setChangeTrainer}>
                      <SelectTrigger className="rounded-xl mt-1">
                        <SelectValue placeholder={t("common:selectTrainer")}>
                          {changeTrainer && trainers.find(t => t.trainer_id === changeTrainer) && (
                            `${trainers.find(t => t.trainer_id === changeTrainer)?.name} ${trainers.find(t => t.trainer_id === changeTrainer)?.surname || ""}`
                          )}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {(changeFilteredTrainers.length > 0 ? changeFilteredTrainers : trainers.filter(t => t.is_active !== false)).map((t) => (
                          <SelectItem key={t.trainer_id} value={t.trainer_id}>
                            {t.name} {t.surname}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Oda */}
                  <div>
                    <Label className="font-medium text-sm">{t("admin:roomRequired2")} {changeBranch && <span className="text-xs text-grey">({changeBranch})</span>}</Label>
                    <Select value={changeRoom} onValueChange={(v) => v && setChangeRoom(v)}>
                      <SelectTrigger className="rounded-xl mt-1">
                        <SelectValue placeholder={t("common:ph_odaSecin")}>
                          {changeRoom && rooms.find(r => r.room_id === changeRoom) && (
                            rooms.find(r => r.room_id === changeRoom)?.name
                          )}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {(changeFilteredRooms.length > 0 ? changeFilteredRooms : rooms).map((r) => {
                          const branchCapacity = (changeBranch && r.branch_capacities && r.branch_capacities[changeBranch] !== undefined)
                            ? r.branch_capacities[changeBranch]
                            : r.capacity;
                          return (
                            <SelectItem key={r.room_id} value={r.room_id}>
                              {r.name} ({t('common:capacity')}: {branchCapacity})
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Section 3: Tarih ve Saat */}
              <div className="border-b pb-4">
                <h3 className="font-semibold text-navy mb-3 flex items-center gap-2">
                  <span className="w-6 h-6 bg-navy text-white rounded-full flex items-center justify-center text-sm">3</span>
                  {t('common:dateAndTime')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Tarih */}
                  <div>
                    <Label className="font-medium text-sm">{t("common:dateRequired")}</Label>
                    <DatePickerTR
                      value={changeDate ? new Date(changeDate) : null}
                      onChange={(date) => setChangeDate(date ? formatDateToLocal(date) : "")}
                      placeholder={t('common:selectDate')}
                      minDate={new Date(getMinDate())}
                    />
                  </div>

                  {/* Başlangıç Saati */}
                  <div>
                    <Label className="font-medium text-sm">{t('common:startRequired')}</Label>
                    <div className="mt-1">
                      <TimePicker
                        value={changeStartTime}
                        onChange={setChangeStartTime}
                        placeholder={t('common:time')}
                        minHour={8}
                        maxHour={22}
                        minuteStep={5}
                        minTime={getMinTime(changeDate)}
                        dataTestId="change-start-time"
                      />
                    </div>
                    {changeDate && changeStartTime && !isTimeSlotValid(changeDate, changeStartTime) && (
                      <p className="text-xs text-red-500 mt-1">
                        ⚠️ En az {getMinAdvanceText()} önceden rezervasyon yapılmalı
                      </p>
                    )}
                  </div>

                  {/* Bitiş Saati */}
                  <div>
                    <Label className="font-medium text-sm">{t('common:endRequired')} {changeSelectedPackageInfo?.session_duration && <span className="text-xs text-grey">({t('common:recommended')}: {changeSelectedPackageInfo.session_duration} dk)</span>}</Label>
                    <div className="mt-1">
                      <TimePicker
                        value={changeEndTime}
                        onChange={setChangeEndTime}
                        placeholder={t("common:ph_bitisSaati")}
                        minHour={8}
                        maxHour={23}
                        minuteStep={5}
                        dataTestId="change-end-time"
                      />
                    </div>
                    {changeStartTime && changeEndTime && changeEndTime <= changeStartTime && (
                      <p className="text-xs text-red-500 mt-1">
                        ⚠️ {t("common:endTimeAfterStart")}
                      </p>
                    )}
                    {changeEndTime && !isEndTimeWithinBusinessHours(changeDate, changeEndTime) && (
                      <p className="text-xs text-red-500 mt-1">
                        ⚠️ {t("common:endTimeExceedsClosingShort")} ({getBusinessClosingTime(changeDate)})
                      </p>
                    )}
                  </div>
                </div>

                {/* Müsaitlik Kontrolü */}
                {(changeRoom || changeTrainer) && changeStartTime && changeDate && (
                  <div className="p-3 bg-slate-50 rounded-xl mt-4">
                    <p className="font-medium text-navy text-sm mb-2">{t("common:availabilityStatus")}</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {/* ODA MÜSAİTLİĞİ - MK formatı */}
                      {changeRoomAvailability && (
                        <div className={`flex items-center gap-2 text-sm ${
                          changeRoomAvailability.is_locked 
                            ? 'text-red-600' 
                            : (changeRoomAvailability.mk_available ?? changeRoomAvailability.available_capacity) > 0 
                              ? 'text-green-600' 
                              : 'text-red-600'
                        }`}>
                          {changeRoomAvailability.is_locked || (changeRoomAvailability.mk_available ?? changeRoomAvailability.available_capacity) === 0 
                            ? <AlertCircle className="w-4 h-4" /> 
                            : <CheckCircle2 className="w-4 h-4" />}
                          <span>
                            {t("common:roomLabel")} {changeRoomAvailability.is_locked 
                              ? t("common:roomLocked") 
                              : (changeRoomAvailability.mk_available ?? changeRoomAvailability.available_capacity) > 0 
                                ? `${t("common:trainerAvailable")} (${changeRoomAvailability.mk_available ?? changeRoomAvailability.available_capacity} ${t("common:slotsAvailable")})`
                                : t("common:roomNotAvailable")}
                          </span>
                        </div>
                      )}
                      
                      {/* ANTRENÖR MÜSAİTLİĞİ - ÖA formatı */}
                      {changeTrainerAvailability && (
                        <div className={`flex items-center gap-2 text-sm ${changeTrainerAvailability.is_available ? 'text-green-600' : 'text-red-600'}`}>
                          {changeTrainerAvailability.is_available ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                          <span>
                            {t("common:trainerLabel")} {changeTrainerAvailability.is_available 
                              ? (() => {
                                  const info = changeTrainerAvailability.training_type_info;
                                  if (info && info.max_participants > 1) {
                                    return `${t("common:trainerAvailable")} (${info.type} - ${info.remaining ?? (info.max_participants - info.current_count)} ${t("common:slotsAvailable")})`;
                                  }
                                  return t("common:trainerAvailable");
                                })()
                              : (changeTrainerAvailability.reason || t('reservations:notAvailable'))}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Section 4: {t('common:optionsAndSummary')} */}
              <div>
                <h3 className="font-semibold text-navy mb-3 flex items-center gap-2">
                  <span className="w-6 h-6 bg-navy text-white rounded-full flex items-center justify-center text-sm">4</span>
                  {t('common:optionsAndSummary')}
                </h3>
                
                {/* Oda Kilitleme */}
                <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl mb-4">
                  <Checkbox
                    id="changeAllowShared"
                    checked={!changeAllowShared}
                    onCheckedChange={(checked) => setChangeAllowShared(!checked)}
                  />
                  <Label htmlFor="changeAllowShared" className="text-sm cursor-pointer">
                    {t('common:lockRoomCompletely')}
                  </Label>
                </div>
                <div className="flex items-center gap-3 p-3 bg-sky-50 border border-sky-200 rounded-xl mb-4">
                  <Checkbox id="changeAddToOnline" checked={changeAddToOnline} onCheckedChange={handleChangeOnlineToggle} data-testid="change-res-add-to-online" />
                  <Label htmlFor="changeAddToOnline" className="text-sm cursor-pointer">Online Ders Programına ekle</Label>
                </div>

                {/* Düzenleme Özeti */}
                <div className="p-4 bg-blue-50 rounded-xl space-y-2">
                  <p className="font-semibold text-navy">{t("reservations:reservationSummary")}</p>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-grey-600">Üye:</span>
                      <span className="font-medium">{members.find(m => m.member_id === changeMember)?.name || "-"} {members.find(m => m.member_id === changeMember)?.surname || ""}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-grey-600">{t("common:paymentLabel")}</span>
                      <span className="font-medium">{t("packages:packageLabel")}: {changeMemberPackages.find(p => p.member_package_id === changePackageId)?.package_name || "-"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-grey-600">{t("common:branchLabel")}</span>
                      <span className="font-medium">{changeBranch || "-"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-grey-600">{t("common:trainerLabel")}</span>
                      <span className="font-medium">{trainers.find(t => t.trainer_id === changeTrainer)?.name || "-"} {trainers.find(t => t.trainer_id === changeTrainer)?.surname || ""}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-grey-600">{t("common:roomLabel")}</span>
                      <span className="font-medium">{rooms.find(r => r.room_id === changeRoom)?.name || "-"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-grey-600">{t("common:dateLabel2")}</span>
                      <span className="font-medium">{changeDate ? format(new Date(changeDate), "d MMMM yyyy, EEEE", { locale: getDateLocale() }) : "-"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-grey-600">{t("common:timeLabel")}</span>
                      <span className="font-medium">{changeStartTime || "-"} - {changeEndTime || "-"}</span>
                    </div>
                    {!changeAllowShared && (
                      <div className="text-xs text-purple-600 pt-2 border-t border-blue-200">
                        🔒 {t('common:roomLockedPrivateLesson')}
                      </div>
                    )}
                  </div>
                </div>

                {/* Section 5: Onay Durumu (Admin/Yönetici için) */}
                <div className="border-b pb-4">
                  <h3 className="font-semibold text-navy mb-3 flex items-center gap-2">
                    <span className="w-6 h-6 bg-navy text-white rounded-full flex items-center justify-center text-sm">5</span>
                    Onay Durumu
                  </h3>
                  {/* Kayıt olmayan üye kontrolü */}
                  {(() => {
                    const memberData = members.find(m => m.member_id === changeMember);
                    const isUnregisteredMember = memberData?.is_app_registered === false;
                    
                    // Kayıt olmayan üye ise otomatik onay
                    if (isUnregisteredMember) {
                      return (
                        <div className="p-3 bg-green-50 border border-green-200 rounded-xl">
                          <p className="text-sm text-green-800 flex items-center gap-2">
                            <CheckCircle className="w-4 h-4" />
                            {t('admin:autoApproved')}
                          </p>
                          <p className="text-xs text-green-600 mt-1">
                            {t('admin:unregisteredMemberAutoApprove')}
                          </p>
                        </div>
                      );
                    }
                    
                    // Online booking kapalıysa direkt onaylı
                    if (businessSettings?.display_settings?.allow_online_booking === false) {
                      return (
                        <div className="p-3 bg-green-50 border border-green-200 rounded-xl">
                          <p className="text-sm text-green-800 flex items-center gap-2">
                            <CheckCircle className="w-4 h-4" />
                            {t('admin:autoApproved')}
                          </p>
                          <p className="text-xs text-green-600 mt-1">
                            {t('admin:onlineClosedAutoApprove')}
                          </p>
                        </div>
                      );
                    }
                    
                    // Normal akış - hem üyeye gönder hem onayla seçeneği
                    return (
                      <>
                        <div className="flex gap-3">
                          <div 
                            className={`flex-1 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                              changeApprovalStatus === "change_pending_member" 
                                ? "border-amber-500 bg-amber-50" 
                                : "border-grey-200 hover:border-grey-300"
                            }`}
                            onClick={() => setChangeApprovalStatus("change_pending_member")}
                          >
                            <div className="flex items-center gap-2">
                              <input 
                                type="radio" 
                                name="changeApproval"
                                checked={changeApprovalStatus === "change_pending_member"}
                                onChange={() => setChangeApprovalStatus("change_pending_member")}
                                className="text-amber-500"
                              />
                              <span className="font-medium">{t("common:sendToMember")}</span>
                            </div>
                            <p className="text-xs text-grey mt-1">{t("reservations:memberApprovalRequired")}</p>
                          </div>
                          {(currentUser?.role === "business" || userPermissions?.is_manager || userPermissions?.is_owner || userPermissions?.can_approve_reservations) && (
                            <div 
                              className={`flex-1 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                                changeApprovalStatus === "approved" 
                                  ? "border-green-500 bg-green-50" 
                                  : "border-grey-200 hover:border-grey-300"
                              }`}
                              onClick={() => setChangeApprovalStatus("approved")}
                            >
                              <div className="flex items-center gap-2">
                                <input 
                                  type="radio" 
                                  name="changeApproval"
                                  checked={changeApprovalStatus === "approved"}
                                  onChange={() => setChangeApprovalStatus("approved")}
                                  className="text-green-500"
                                />
                                <span className="font-medium">{t('admin:confirm')}</span>
                              </div>
                              <p className="text-xs text-grey mt-1">{t("reservations:saveAsAutoApproved")}</p>
                            </div>
                          )}
                        </div>
                        {changeApprovalStatus === "change_pending_member" && (
                          <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            Değişiklik üyeye gönderilecek ve üye onaylayana kadar beklemede kalacak
                          </p>
                        )}
                      </>
                    );
                  })()}
                </div>

                <Button
                  className="w-full bg-navy hover:bg-navy/90 rounded-xl h-12 text-base mt-4"
                  disabled={
                    submittingChange ||
                    // Dış üye değilse ödeme tipi ve paket kontrolü yap
                    (selectedReservationForChange?.payment_type !== "external_member" && !changePaymentType) ||
                    (selectedReservationForChange?.payment_type !== "external_member" && changePaymentType === "package" && !changePackageId) ||
                    !changeTrainer || !changeRoom || !changeDate || !changeStartTime ||
                    (changeTrainerAvailability && !changeTrainerAvailability.is_available) ||
                    (changeRoomAvailability && changeRoomAvailability.is_locked)
                  }
                  onClick={async () => {
                    if (submittingChange) return;
                    
                    if (!changeDate || !changeStartTime) {
                      toast.error("Tarih ve saat zorunludur");
                      return;
                    }
                    
                    // Kapanış saati kontrolü
                    if (!isEndTimeWithinBusinessHours(changeDate, changeEndTime)) {
                      const closingTime = getBusinessClosingTime(changeDate);
                      toast.error(t("common:endTimeExceedsClosingShort"));
                      return;
                    }
                    
                    // Min advance booking kontrolü
                    if (!isTimeSlotValid(changeDate, changeStartTime)) {
                      toast.error(`Rezervasyon en az ${getMinAdvanceText()} önceden yapılmalıdır`);
                      return;
                    }
                    
                    setSubmittingChange(true);
                    setPageLoading(true);
                    try {
                      // Kayıt olmayan üye kontrolü
                      const memberData = members.find(m => m.member_id === changeMember);
                      const isUnregisteredMember = memberData?.is_app_registered === false;
                      
                      // Kayıt olmayan üye veya online booking kapalıysa direkt approved
                      const effectiveStatus = isUnregisteredMember || businessSettings?.display_settings?.allow_online_booking === false 
                        ? "approved" 
                        : changeApprovalStatus;
                      
                      const response = await fetch(`${API}/reservations/${selectedReservationForChange.reservation_id}`, {
                        method: "PUT",
                        headers: { "Content-Type": "application/json" },
                        credentials: "include",
                        body: JSON.stringify({
                          member_id: changeMember,
                          member_package_id: changePaymentType === "package" ? changePackageId : null,
                          trainer_id: changeTrainer,
                          room_id: changeRoom,
                          branch: changeBranch,
                          date: changeDate,
                          start_time: changeStartTime,
                          end_time: changeEndTime,
                          training_type: changeType,
                          allow_shared: changeAllowShared,
                          status: effectiveStatus,
                          payment_type: changePaymentType,
                          is_demo: changePaymentType === "demo",
                          card_type: changePaymentType === "card" ? changeCard : null,
                          add_to_online: changeAddToOnline,
                        }),
                      });

                      if (response.ok) {
                        const successMsg = effectiveStatus === "approved" 
                          ? "Rezervasyon güncellendi ve onaylandı" 
                          : "Değişiklik üyeye gönderildi, onay bekleniyor";
                        toast.success(successMsg, { duration: 2000 });
                        setShowChangeModal(false);
                        setSelectedReservationForChange(null);
                        setChangeApprovalStatus("change_pending_member");
                        fetchData();
                        fetchTabCounts();
                        fetchPendingReservations();
                      } else {
                        const error = await response.json();
                        toast.error(error.detail || t('settings:updateFailed'));
                      }
                    } catch (error) {
                      toast.error(t('common:errorOccurred'));
                    } finally {
                      setSubmittingChange(false);
                      setPageLoading(false);
                    }
                  }}
                >
                  {submittingChange && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {submittingChange ? "Kaydediliyor..." : "Değişikliği Kaydet"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Cancel Modal */}
      {/* İptal Analizi Dialog */}
      <GroupRemindersDialog open={showReminders} onOpenChange={setShowReminders} businessId={businessId} />
      <OpenClassesManager
        open={showOpenClasses}
        onOpenChange={setShowOpenClasses}
        businessId={businessId}
        trainers={trainers}
        rooms={rooms}
        branches={branches}
        prefill={openClassPrefill}
      />
      <Dialog open={showCancelAnalysis} onOpenChange={setShowCancelAnalysis}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="w-5 h-5 text-slate-600" /> İptal Analizi
            </DialogTitle>
            <p className="text-sm text-slate-500">
              {(viewMode === "day" || viewMode === "dayTime") && format(selectedDate, "d MMMM yyyy", { locale: getDateLocale() })}
              {viewMode === "week" && `${format(startOfWeek(selectedDate, { weekStartsOn: 1 }), "d MMM", { locale: getDateLocale() })} - ${format(addDays(startOfWeek(selectedDate, { weekStartsOn: 1 }), 6), "d MMM yyyy", { locale: getDateLocale() })}`}
              {viewMode === "month" && format(selectedDate, "MMMM yyyy", { locale: getDateLocale() })}
              {" "}dönemi için
            </p>
          </DialogHeader>
          {showCancelAnalysis && (
            <CancellationAnalysis
              businessId={businessId}
              startStr={
                viewMode === "day" || viewMode === "dayTime"
                  ? format(selectedDate, "yyyy-MM-dd")
                  : viewMode === "week"
                  ? format(startOfWeek(selectedDate, { weekStartsOn: 1 }), "yyyy-MM-dd")
                  : format(startOfMonth(selectedDate), "yyyy-MM-dd")
              }
              endStr={
                viewMode === "day" || viewMode === "dayTime"
                  ? format(selectedDate, "yyyy-MM-dd")
                  : viewMode === "week"
                  ? format(addDays(startOfWeek(selectedDate, { weekStartsOn: 1 }), 6), "yyyy-MM-dd")
                  : format(endOfMonth(selectedDate), "yyyy-MM-dd")
              }
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showCancelModal} onOpenChange={(open) => {
        setShowCancelModal(open);
        if (!open) {
          setSelectedReservationForCancel(null);
          setCancelReason("");
          setCancelReasonCategory("");
          setBurnSession(false);
          setAwardTrainerCommission(false);
          setAwardEmployeeCommission(false);
        }
      }}>
        <DialogContent className="md:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('reservations:cancelReservation')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {selectedReservationForCancel && (
              <>
                <div className="p-3 bg-slate-50 rounded-xl space-y-1">
                  <p className="text-sm font-medium text-navy">{t("common:reservationToCancel")}</p>
                  <p className="text-sm"><span className="text-grey">{t("common:dateLabel2")}</span> {selectedReservationForCancel.date}</p>
                  <p className="text-sm"><span className="text-grey">{t("common:timeLabel")}</span> {selectedReservationForCancel.start_time || selectedReservationForCancel.time}</p>
                  <p className="text-sm"><span className="text-grey">{t("common:memberLabel")}</span> {members.find(m => m.member_id === selectedReservationForCancel.member_id)?.name || "Üye"}</p>
                </div>

                <div>
                  <Label className="font-semibold">{t("common:cancellationReason")} *</Label>
                  <Select value={cancelReasonCategory} onValueChange={setCancelReasonCategory}>
                    <SelectTrigger className="rounded-xl mt-1" data-testid="cancel-reason-select">
                      <SelectValue placeholder="İptal nedeni seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {CANCEL_REASONS.map((r) => (
                        <SelectItem key={r.value} value={r.value} data-testid={`cancel-reason-${r.value}`}>
                          {r.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {cancelReasonCategory === "other" && (
                    <Input
                      placeholder="İptal nedenini yazın"
                      value={cancelReason}
                      onChange={(e) => setCancelReason(e.target.value)}
                      className="rounded-xl mt-2"
                      data-testid="cancel-reason-other-input"
                    />
                  )}
                </div>

                {cancelOnline?.linked && (
                  <div className="p-3 bg-sky-50 border border-sky-200 rounded-xl text-sm" data-testid="cancel-online-info">
                    {cancelOnline.others > 0 ? (
                      <p className="text-sky-800">Bu ders Online Ders Programında ve {cancelOnline.others} kişi daha kayıtlı. Online ders silinmeyecek, sadece bu rezervasyon iptal edilecek.</p>
                    ) : (
                      <div className="flex items-center gap-3">
                        <Checkbox id="deleteOnline" checked={deleteOnlineOnCancel} onCheckedChange={setDeleteOnlineOnCancel} data-testid="cancel-delete-online-checkbox" />
                        <Label htmlFor="deleteOnline" className="cursor-pointer text-sky-900">Online Ders Programındaki dersi de sil</Label>
                      </div>
                    )}
                  </div>
                )}

                {selectedReservationForCancel.payment_type === "package" && (
                <div className="flex items-center gap-3 p-3 bg-orange-50 border border-orange-200 rounded-xl">
                  <Checkbox
                    id="burnSession"
                    checked={burnSession}
                    onCheckedChange={setBurnSession}
                    data-testid="cancel-burn-session-checkbox"
                  />
                  <div>
                    <Label htmlFor="burnSession" className="text-sm font-medium cursor-pointer">
                      Seansı yak (paketten düş)
                    </Label>
                    <p className="text-xs text-grey">
                      {burnSession
                        ? "Üyenin paketinden 1 seans düşülecek (geç iptal)."
                        : "Normal iptal: iptal süresi geçmiş olsa bile paketten seans DÜŞÜLMEZ."}
                    </p>
                  </div>
                </div>
                )}

                {/* Antrenör primi — sadece primli/maaş+primli antrenör için */}
                {selectedReservationForCancel.payment_type !== "external_member" &&
                  ["commission_receiving", "salary_and_commission_receiving"].includes(
                    trainers.find(tr => tr.trainer_id === selectedReservationForCancel.trainer_id)?.work_type
                  ) && (
                  <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-xl">
                    <Checkbox
                      id="awardTrainerCommission"
                      checked={awardTrainerCommission}
                      onCheckedChange={setAwardTrainerCommission}
                      data-testid="cancel-award-trainer-commission-checkbox"
                    />
                    <div>
                      <Label htmlFor="awardTrainerCommission" className="text-sm font-medium cursor-pointer">
                        Antrenör primi hesaplansın
                      </Label>
                      <p className="text-xs text-grey">
                        Ders iptal edilse bile antrenör bu seansın primini alır.
                      </p>
                    </div>
                  </div>
                )}

                {/* Çalışan primi — sadece prim alan aktif çalışan varsa */}
                {selectedReservationForCancel.payment_type !== "external_member" &&
                  employees.some(e => (e.status === "active" || e.is_active !== false) &&
                    ["salary_and_commission", "commission_only"].includes(e.work_type)) && (
                  <div className="flex items-center gap-3 p-3 bg-purple-50 border border-purple-200 rounded-xl">
                    <Checkbox
                      id="awardEmployeeCommission"
                      checked={awardEmployeeCommission}
                      onCheckedChange={setAwardEmployeeCommission}
                      data-testid="cancel-award-employee-commission-checkbox"
                    />
                    <div>
                      <Label htmlFor="awardEmployeeCommission" className="text-sm font-medium cursor-pointer">
                        Çalışan primi hesaplansın
                      </Label>
                      <p className="text-xs text-grey">
                        Ders iptal edilse bile prim alan çalışanlara bu seansın primi yansır.
                      </p>
                    </div>
                  </div>
                )}

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className="flex-1 rounded-xl"
                    onClick={() => setShowCancelModal(false)}
                    disabled={submittingCancel}
                  >
                    Vazgeç
                  </Button>
                  <Button
                    className="flex-1 bg-red-600 hover:bg-red-700 rounded-xl"
                    onClick={handleCancelWithModal}
                    disabled={!cancelReasonCategory || (cancelReasonCategory === "other" && !cancelReason.trim()) || submittingCancel}
                  >
                    {submittingCancel && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    {submittingCancel ? "İptal Ediliyor..." : t("common:cancel")}
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* RESTORE MODAL */}
      <Dialog open={showRestoreModal} onOpenChange={setShowRestoreModal}>
        <DialogContent className="md:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <RotateCcw className="w-5 h-5" />
              {t("common:undoCancellation")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {selectedReservationForRestore && (
              <>
                <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                  <p className="font-medium text-green-900">{selectedReservationForRestore.member_name}</p>
                  <p className="text-sm text-green-700">
                    {selectedReservationForRestore.date} • {selectedReservationForRestore.time}
                  </p>
                  {selectedReservationForRestore.is_burned && (
                    <p className="text-xs text-orange-600 mt-2">
                      ⚠️ Bu seans yakılmıştı. Geri alındığında seans hakkı iade edilecek.
                    </p>
                  )}
                </div>

                <div>
                  <Label>Geri Alma Sebebi *</Label>
                  <Textarea
                    placeholder={t("common:ph_nedenGeriAliniyor")}
                    value={restoreReason}
                    onChange={(e) => setRestoreReason(e.target.value)}
                    className="rounded-xl mt-1"
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className="flex-1 rounded-xl"
                    onClick={() => setShowRestoreModal(false)}
                  >
                    Vazgeç
                  </Button>
                  <Button
                    className="flex-1 bg-green-600 hover:bg-green-700 rounded-xl"
                    onClick={handleRestoreWithModal}
                    disabled={!restoreReason.trim()}
                  >
                    Geri Al
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* WAITLIST MODAL */}
      <Dialog open={showWaitlistModal} onOpenChange={setShowWaitlistModal}>
        <DialogContent className="md:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <ListOrdered className="w-5 h-5" />
              Bekleme Listesine Ekle
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 text-sm">
              <p className="text-orange-800">
                <strong>{format(selectedDate, "d MMMM yyyy", { locale: getDateLocale() })}</strong> tarihi için bekleme listesine ekleniyor.
              </p>
            </div>

            <div>
              <Label>Üye *</Label>
              <Select value={waitlistMember} onValueChange={setWaitlistMember}>
                <SelectTrigger className="rounded-xl" data-testid="waitlist-member-select">
                  <SelectValue placeholder={t("common:ph_uyeSecin")} />
                </SelectTrigger>
                <SelectContent>
                  {members
                    .filter(member => member.status !== "inactive" && member.status !== "deleted")
                    .map((member) => (
                    <SelectItem key={member.member_id} value={member.member_id}>
                      {member.name} {member.surname}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Tercih Edilen Saat *</Label>
              <TimePicker
                value={waitlistTime}
                onChange={setWaitlistTime}
                placeholder={t("common:ph_saatSecin")}
                minHour={8}
                maxHour={22}
                minuteStep={5}
                minTime={getMinTime(format(selectedDate, "yyyy-MM-dd"))}
                dataTestId="waitlist-time"
              />
            </div>

            <div>
              <Label>Tercih Edilen Antrenör (Opsiyonel)</Label>
              <Select value={waitlistTrainer} onValueChange={setWaitlistTrainer}>
                <SelectTrigger className="rounded-xl" data-testid="waitlist-trainer-select">
                  <SelectValue placeholder={t("common:selectTrainer")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Farketmez</SelectItem>
                  {trainers.map((trainer) => (
                    <SelectItem key={trainer.trainer_id} value={trainer.trainer_id}>
                      {trainer.name} {trainer.surname}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Notlar (Opsiyonel)</Label>
              <Textarea
                placeholder="Ek notlar..."
                value={waitlistNotes}
                onChange={(e) => setWaitlistNotes(e.target.value)}
                className="rounded-xl"
                data-testid="waitlist-notes"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                className="flex-1 rounded-xl"
                onClick={() => {
                  setShowWaitlistModal(false);
                  resetWaitlistForm();
                }}
              >
                Vazgeç
              </Button>
              <Button
                className="flex-1 bg-orange-500 hover:bg-orange-600 rounded-xl"
                onClick={handleAddToWaitlist}
                disabled={!waitlistMember || !waitlistTime}
                data-testid="confirm-waitlist-btn"
              >
                Listeye Ekle
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* REQUEST MODAL - New Reservation Request */}
      <Dialog open={showRequestModal} onOpenChange={(open) => {
        setShowRequestModal(open);
        if (!open) {
          resetRequestForm();
        }
      }}>
        <DialogContent className="md:max-w-md md:max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <Clock className="w-5 h-5" />
              {t('common:newReservationRequestLabel')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 text-sm">
              <p className="text-orange-800">
                <strong>{format(selectedDate, "d MMMM yyyy", { locale: getDateLocale() })}</strong> tarihi için talep oluşturulacak.
                Talep onaylanana kadar slot geçici olarak bloke edilecektir.
              </p>
            </div>

            {/* 1. Üye Seçimi */}
            <div>
              <Label className="font-semibold">1. Üye *</Label>
              <Select value={requestMember} onValueChange={(value) => {
                setRequestMember(value);
                setRequestPackageId("");
                setRequestSelectedPackageInfo(null);
                fetchRequestMemberPackages(value);
              }}>
                <SelectTrigger className="rounded-xl" data-testid="request-member-select">
                  <SelectValue placeholder={t("common:ph_uyeSecin")} />
                </SelectTrigger>
                <SelectContent>
                  {members
                    .filter(m => m.status !== "inactive" && m.status !== "deleted")
                    .sort((a, b) => `${a.name} ${a.surname}`.localeCompare(`${b.name} ${b.surname}`, 'tr'))
                    .map((m) => (
                    <SelectItem key={m.member_id} value={m.member_id}>
                      {m.name} {m.surname} - {m.member_no}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* 2. Paket Seçimi */}
            {requestMember && (
              <div>
                <Label className="font-semibold">2. Paket *</Label>
                <Select value={requestPackageId} onValueChange={setRequestPackageId}>
                  <SelectTrigger className="rounded-xl" data-testid="request-package-select">
                    <SelectValue placeholder={requestMemberPackages.length > 0 ? "Paket seçin" : "Üyenin paketi yok"} />
                  </SelectTrigger>
                  <SelectContent>
                    {requestMemberPackages
                      .filter(mp => mp.is_active !== false && mp.remaining_sessions > 0)
                      .map((mp) => {
                        let pkgBranch = mp.package_branch || mp.package_branches?.[0] || mp.branch;
                        if (!pkgBranch) {
                          const name = (mp.package_name || "").toLowerCase();
                          if (name.includes("ems")) pkgBranch = "EMS";
                          else if (name.includes("pilates")) pkgBranch = "Pilates";
                          else if (name.includes("yoga")) pkgBranch = "Yoga";
                          else if (name.includes("fitness")) pkgBranch = "Fitness";
                        }
                        return (
                          <SelectItem key={mp.member_package_id} value={mp.member_package_id}>
                            {mp.package_name || t('packages:package')} ({pkgBranch || t("common:noBranch")}) - {t("packages:remainingAmount")}: {mp.remaining_sessions} ders
                          </SelectItem>
                        );
                      })}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* 3. Branş - Paketten otomatik */}
            {requestSelectedPackageInfo && (
              <div>
                <Label className="font-semibold text-grey">{t('packages:branchFromPackage')}</Label>
                <div className="p-3 bg-slate-100 rounded-xl text-sm">
                  {requestBranch || t("common:notSpecified")}
                </div>
              </div>
            )}

            {/* 4. Antrenman Tipi - Paketten otomatik */}
            {requestSelectedPackageInfo && (
              <div>
                <Label className="font-semibold text-grey">{t('packages:trainingTypeFromPkg')}</Label>
                <div className="p-3 bg-slate-100 rounded-xl text-sm">
                  {requestType === "one_on_one" ? t("reservations:privateLesson") : requestType === "duet" ? t('reservations:duet') : t("reservations:groupLesson")}
                </div>
              </div>
            )}

            {/* 5. Antrenör */}
            {requestPackageId && (
              <div>
                <Label className="font-semibold">3. {t("common:trainerRequired")}</Label>
                <Select value={requestTrainer} onValueChange={setRequestTrainer}>
                  <SelectTrigger className="rounded-xl" data-testid="request-trainer-select">
                    <SelectValue placeholder={t("common:selectTrainer")} />
                  </SelectTrigger>
                  <SelectContent>
                    {requestFilteredTrainers.map((t) => (
                      <SelectItem key={t.trainer_id} value={t.trainer_id}>
                        {t.name} {t.surname}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* 6. Oda */}
            {requestTrainer && (
              <div>
                <Label className="font-semibold">4. {t("admin:roomRequired2")} {requestBranch && <span className="text-xs text-grey">({requestBranch})</span>}</Label>
                <Select value={requestRoom} onValueChange={setRequestRoom}>
                  <SelectTrigger className="rounded-xl" data-testid="request-room-select">
                    <SelectValue placeholder={t("common:ph_odaSecin")} />
                  </SelectTrigger>
                  <SelectContent>
                    {requestFilteredRooms.map((r) => {
                      const branchCapacity = (requestBranch && r.branch_capacities && r.branch_capacities[requestBranch] !== undefined)
                        ? r.branch_capacities[requestBranch]
                        : r.capacity;
                      return (
                        <SelectItem key={r.room_id} value={r.room_id}>
                          {r.name} ({t('common:capacity')}: {branchCapacity})
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* 7. Saat */}
            {requestRoom && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="font-semibold">5. Başlangıç Saati *</Label>
                  <TimePicker
                    value={requestStartTime}
                    onChange={setRequestStartTime}
                    placeholder={t('common:time')}
                    minHour={8}
                    maxHour={22}
                    minuteStep={5}
                    minTime={getMinTime(format(selectedDate, "yyyy-MM-dd"))}
                    dataTestId="request-time"
                  />
                </div>
                <div>
                  <Label className="font-semibold">{t("common:endRequired")} {requestSelectedPackageInfo && <span className="text-xs text-grey">({t("common:recommended")}: {requestSelectedPackageInfo.session_duration || 55} dk)</span>}</Label>
                  <div className="mt-1">
                    <TimePicker
                      value={requestEndTime}
                      onChange={setRequestEndTime}
                      placeholder={t("common:ph_bitisSaati")}
                      minHour={8}
                      maxHour={23}
                      minuteStep={5}
                      dataTestId="request-end-time"
                    />
                  </div>
                  {requestStartTime && requestEndTime && requestEndTime <= requestStartTime && (
                    <p className="text-xs text-red-500 mt-1">
                      ⚠️ {t("common:endTimeAfterStart")}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Müsaitlik Kontrolü */}
            {(requestRoom || requestTrainer) && requestStartTime && (
              <div className="p-3 bg-slate-50 rounded-xl space-y-2">
                <p className="font-medium text-navy text-sm">{t("common:availabilityStatus")}</p>
                
                {/* Oda Durumu */}
                {requestRoomAvailability && (
                  <div className={`flex items-center gap-2 text-sm ${requestRoomAvailability.is_locked ? 'text-red-600' : requestRoomAvailability.available_capacity > 0 ? 'text-green-600' : 'text-orange-600'}`}>
                    {requestRoomAvailability.is_locked ? (
                      <AlertCircle className="w-4 h-4" />
                    ) : requestRoomAvailability.available_capacity > 0 ? (
                      <CheckCircle2 className="w-4 h-4" />
                    ) : (
                      <AlertCircle className="w-4 h-4" />
                    )}
                    <span>
                      {t("common:roomLabel")} {requestRoomAvailability.is_locked 
                        ? t("common:lockedPrivateLesson") 
                        : `${requestRoomAvailability.available_capacity}/${requestRoomAvailability.total_capacity} müsait`}
                    </span>
                  </div>
                )}
                
                {/* Antrenör Durumu */}
                {requestTrainerAvailability && (
                  <div className={`flex items-center gap-2 text-sm ${requestTrainerAvailability.is_available ? 'text-green-600' : 'text-red-600'}`}>
                    {requestTrainerAvailability.is_available ? (
                      <CheckCircle2 className="w-4 h-4" />
                    ) : (
                      <AlertCircle className="w-4 h-4" />
                    )}
                    <span>
                      {t("common:trainerLabel")} {requestTrainerAvailability.is_available ? t("common:trainerAvailable") : t("common:busy")}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Not */}
            <div>
              <Label>Not (opsiyonel)</Label>
              <Textarea
                placeholder="Ek notlar..."
                value={requestNotes}
                onChange={(e) => setRequestNotes(e.target.value)}
                className="rounded-xl"
                data-testid="request-notes"
              />
            </div>

            {/* Talep Özeti */}
            {requestMember && requestPackageId && requestTrainer && requestRoom && requestStartTime && (
              <div className="p-3 bg-orange-50 rounded-xl text-sm space-y-1">
                <p className="font-medium text-orange-800">{t("reservations:requestSummary")}</p>
                <p><span className="text-grey">{t("common:memberLabel")}</span> {members.find(m => m.member_id === requestMember)?.name || "-"} {members.find(m => m.member_id === requestMember)?.surname || ""}</p>
                <p><span className="text-grey">{t("packages:packageLabel")}:</span> {requestMemberPackages.find(p => p.member_package_id === requestPackageId)?.package_name || "-"}</p>
                <p><span className="text-grey">{t("common:branchLabel")}</span> {requestBranch || "-"}</p>
                <p><span className="text-grey">{t("common:trainerLabel")}</span> {trainers.find(t => t.trainer_id === requestTrainer)?.name || "-"} {trainers.find(t => t.trainer_id === requestTrainer)?.surname || ""}</p>
                <p><span className="text-grey">{t("common:roomLabel")}</span> {rooms.find(r => r.room_id === requestRoom)?.name || "-"}</p>
                <p><span className="text-grey">Tarih/Saat:</span> {format(selectedDate, "dd/MM/yyyy", { locale: getDateLocale() })} {requestStartTime} - {requestEndTime} {requestSelectedPackageInfo?.session_duration && `(${requestSelectedPackageInfo.session_duration} dk)`}</p>
                <p><span className="text-grey">Antrenman Tipi:</span> {requestType === "one_on_one" ? t("reservations:privateLesson") : requestType === "duet" ? t("common:duet") : t("reservations:groupLesson")}</p>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                className="flex-1 rounded-xl"
                onClick={() => {
                  setShowRequestModal(false);
                  resetRequestForm();
                }}
                disabled={submittingRequest}
              >
                Vazgeç
              </Button>
              <Button
                className="flex-1 bg-orange-500 hover:bg-orange-600 rounded-xl"
                onClick={handleCreateRequest}
                disabled={
                  submittingRequest ||
                  !requestMember || !requestPackageId || !requestTrainer || !requestRoom || !requestStartTime ||
                  (requestTrainerAvailability && !requestTrainerAvailability.is_available) ||
                  (requestRoomAvailability && requestRoomAvailability.is_locked)
                }
                data-testid="confirm-request-btn"
              >
                {submittingRequest && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {submittingRequest ? t("common:creating") : t("common:createRequest")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* EDIT REQUEST MODAL - İşletme tarafından talep düzenleme */}
      <Dialog open={showEditRequestModal} onOpenChange={(open) => {
        setShowEditRequestModal(open);
        if (!open) {
          setEditingRequest(null);
          setEditReqMember("");
          setEditReqTrainer("");
          setEditReqRoom("");
          setEditReqBranch("");
          setEditReqDate("");
          setEditReqStartTime("");
          setEditReqEndTime("");
          setEditReqNotes("");
          setEditReqRoomAvailability(null);
          setEditReqTrainerAvailability(null);
          setSubmittingEditRequest(false);
        }
      }}>
        <DialogContent className="md:w-[95vw] md:max-w-4xl md:max-h-[90vh] overflow-y-auto p-4 md:p-6">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-navy" />
              Talebi Düzenle
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {editingRequest && (
              <>
                {/* Üye Bilgisi - Sadece gösterim */}
                <div className="p-3 bg-slate-50 rounded-xl">
                  <p className="text-xs text-grey">{t("common:member")}</p>
                  <p className="font-semibold text-navy">
                    {editingRequest.member_name || `${members.find(m => m.member_id === editingRequest.member_id)?.name || ""} ${members.find(m => m.member_id === editingRequest.member_id)?.surname || ""}`.trim() || t('common:member')}
                  </p>
                </div>

                {/* Branş */}
                <div>
                  <Label>{t("common:branch")}</Label>
                  <Select value={editReqBranch} onValueChange={(val) => {
                    setEditReqBranch(val);
                    setEditReqTrainer(""); // Branş değişince antrenörü sıfırla
                    setEditReqRoom(""); // Branş değişince odayı sıfırla
                  }}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder={t("common:ph_bransSecin")} />
                    </SelectTrigger>
                    <SelectContent>
                      {branches.map((b) => (
                        <SelectItem key={b} value={b}>{b}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Antrenör - Branşa göre filtrelenmiş */}
                <div>
                  <Label>Antrenör {editReqBranch && editReqFilteredTrainers.length === 0 && <span className="text-red-500 text-xs">(Bu branşta antrenör yok)</span>}</Label>
                  <Select value={editReqTrainer} onValueChange={setEditReqTrainer} disabled={!editReqBranch}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder={editReqBranch ? "Antrenör seçin" : "Önce branş seçin"} />
                    </SelectTrigger>
                    <SelectContent>
                      {editReqFilteredTrainers.map((t) => (
                        <SelectItem key={t.trainer_id} value={t.trainer_id}>
                          {t.name} {t.surname}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Oda - Branşa göre filtrelenmiş */}
                <div>
                  <Label>Oda {editReqBranch && editReqFilteredRooms.length === 0 && <span className="text-red-500 text-xs">(Bu branşta oda yok)</span>}</Label>
                  <Select value={editReqRoom} onValueChange={(v) => v && setEditReqRoom(v)} disabled={!editReqBranch}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder={editReqBranch ? "Oda seçin" : "Önce branş seçin"} />
                    </SelectTrigger>
                    <SelectContent>
                      {editReqFilteredRooms.map((r) => {
                        const branchCapacity = (editReqBranch && r.branch_capacities && r.branch_capacities[editReqBranch] !== undefined)
                          ? r.branch_capacities[editReqBranch]
                          : r.capacity;
                        return (
                          <SelectItem key={r.room_id} value={r.room_id}>
                            {r.name} ({t('common:capacity')}: {branchCapacity})
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>

                {/* Tarih */}
                <div>
                  <Label>{t('common:date')}</Label>
                  <DatePickerTR
                    value={editReqDate ? new Date(editReqDate) : null}
                    onChange={(date) => setEditReqDate(date ? formatDateToLocal(date) : "")}
                    placeholder={t('common:selectDate')}
                  />
                </div>

                {/* Saat */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{t('settings:startTime')}</Label>
                    <TimePicker
                      value={editReqStartTime}
                      onChange={setEditReqStartTime}
                    />
                  </div>
                  <div>
                    <Label>Bitiş <span className="text-xs text-grey">(Otomatik)</span></Label>
                    <div className="h-10 px-3 py-2 bg-slate-100 border border-input rounded-xl flex items-center text-sm">
                      {editReqEndTime || "--:--"}
                    </div>
                  </div>
                </div>
                
                {/* Bitiş saati validasyon uyarısı */}
                {editReqStartTime && editReqEndTime && editReqEndTime <= editReqStartTime && (
                  <p className="text-xs text-red-500 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    Bitiş saati başlangıçtan sonra olmalı
                  </p>
                )}

                {/* Müsaitlik Durumu */}
                {editReqStartTime && editReqDate && (editReqRoom || editReqTrainer) && (
                  <div className="p-3 bg-slate-50 rounded-xl">
                    <p className="font-medium text-navy text-sm mb-2">{t("common:availabilityStatus")}</p>
                    <div className="grid grid-cols-1 gap-2">
                      {/* ODA MÜSAİTLİĞİ */}
                      {editReqRoom && editReqRoomAvailability && (
                        <div className={`flex items-center gap-2 text-sm ${
                          editReqRoomAvailability.is_locked 
                            ? 'text-red-600' 
                            : (editReqRoomAvailability.mk_available ?? editReqRoomAvailability.available_capacity) > 0 
                              ? 'text-green-600' 
                              : 'text-red-600'
                        }`}>
                          {editReqRoomAvailability.is_locked || (editReqRoomAvailability.mk_available ?? editReqRoomAvailability.available_capacity) === 0 
                            ? <AlertCircle className="w-4 h-4" /> 
                            : <CheckCircle2 className="w-4 h-4" />}
                          <span>
                            Oda: {editReqRoomAvailability.is_locked 
                              ? t("common:roomLocked") 
                              : (editReqRoomAvailability.mk_available ?? editReqRoomAvailability.available_capacity) > 0 
                                ? `${t("common:trainerAvailable")} (${editReqRoomAvailability.mk_available ?? editReqRoomAvailability.available_capacity} ${t("common:slotsAvailable")})`
                                : t("common:roomNotAvailable")}
                          </span>
                        </div>
                      )}
                      {editReqRoom && !editReqRoomAvailability && (
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Clock className="w-4 h-4 animate-spin" />
                          <span>{t("common:roomLabel")} {t('qr:checking')}</span>
                        </div>
                      )}
                      
                      {/* ANTRENÖR MÜSAİTLİĞİ */}
                      {editReqTrainer && editReqTrainerAvailability && (
                        <div className={`flex items-center gap-2 text-sm ${editReqTrainerAvailability.is_available ? 'text-green-600' : 'text-red-600'}`}>
                          {editReqTrainerAvailability.is_available ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                          <span>
                            Antrenör: {editReqTrainerAvailability.is_available 
                              ? (() => {
                                  const info = editReqTrainerAvailability.training_type_info;
                                  if (info && info.max_participants > 1) {
                                    return `${t("common:trainerAvailable")} (${info.type} - ${info.remaining ?? (info.max_participants - info.current_count)} ${t("common:slotsAvailable")})`;
                                  }
                                  return t("common:trainerAvailable");
                                })()
                              : (editReqTrainerAvailability.reason || t('reservations:notAvailable'))}
                          </span>
                        </div>
                      )}
                      {editReqTrainer && !editReqTrainerAvailability && (
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Clock className="w-4 h-4 animate-spin" />
                          <span>{t("common:trainerLabel")} {t('qr:checking')}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Notlar */}
                <div>
                  <Label>Notlar</Label>
                  <Textarea
                    value={editReqNotes}
                    onChange={(e) => setEditReqNotes(e.target.value)}
                    placeholder="Not ekleyin..."
                    className="rounded-xl"
                    rows={2}
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    variant="outline"
                    className="flex-1 rounded-xl"
                    onClick={() => {
                      setShowEditRequestModal(false);
                      setEditingRequest(null);
                    }}
                    disabled={submittingEditRequest}
                  >
                    Vazgeç
                  </Button>
                  <Button
                    className="flex-1 bg-navy hover:bg-navy/90 rounded-xl"
                    onClick={async () => {
                      if (!editingRequest || submittingEditRequest) return;
                      
                      setSubmittingEditRequest(true);
                      setPageLoading(true);
                      try {
                        const response = await fetch(`${API}/reservation-requests/${editingRequest.request_id}`, {
                          method: "PUT",
                          headers: { "Content-Type": "application/json" },
                          credentials: "include",
                          body: JSON.stringify({
                            trainer_id: editReqTrainer,
                            room_id: editReqRoom,
                            branch: editReqBranch,
                            date: editReqDate,
                            start_time: editReqStartTime,
                            end_time: editReqEndTime,
                            notes: editReqNotes
                          })
                        });
                        
                        if (response.ok) {
                          toast.success(t('common:requestUpdated'));
                          setShowEditRequestModal(false);
                          setEditingRequest(null);
                          fetchReservationRequests();
                        } else {
                          const error = await response.json();
                          toast.error(error.detail || t('settings:updateFailed'));
                        }
                      } catch (error) {
                        toast.error(t('common:errorOccurred'));
                      } finally {
                        setSubmittingEditRequest(false);
                        setPageLoading(false);
                      }
                    }}
                    disabled={
                      submittingEditRequest ||
                      !editReqDate || !editReqStartTime || !editReqTrainer ||
                      (editReqTrainerAvailability && !editReqTrainerAvailability.is_available) ||
                      (editReqRoomAvailability && editReqRoomAvailability.is_locked)
                    }
                  >
                    {submittingEditRequest && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    {submittingEditRequest ? "Kaydediliyor..." : t('common:save')}
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* REJECT REQUEST MODAL */}
      <Dialog open={showRejectModal} onOpenChange={(open) => {
        setShowRejectModal(open);
        if (!open) {
          setSelectedRequestForReject(null);
          setRejectReason("");
        }
      }}>
        <DialogContent className="md:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <X className="w-5 h-5" />
              {t("reservations:rejectRequest")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {selectedRequestForReject && (
              <>
                <div className="p-3 bg-red-50 rounded-xl space-y-1">
                  <p className="text-sm font-medium text-red-800">{t("reservations:requestToReject")}</p>
                  <p className="text-sm"><span className="text-grey">{t("common:memberLabel")}</span> {selectedRequestForReject.member_name}</p>
                  <p className="text-sm"><span className="text-grey">{t("common:dateLabel2")}</span> {selectedRequestForReject.date} {selectedRequestForReject.start_time}</p>
                  {selectedRequestForReject.trainer_name && (
                    <p className="text-sm"><span className="text-grey">{t("common:trainerLabel")}</span> {selectedRequestForReject.trainer_name}</p>
                  )}
                </div>

                <div>
                  <Label className="font-semibold">{t("reservations:rejectionReasonLabel")}</Label>
                  <Textarea
                    placeholder={t("admin:ph_nedenReddediyorsunuzBuMesajUyeye")}
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    className="rounded-xl mt-1"
                    rows={3}
                    data-testid="reject-reason-input"
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className="flex-1 rounded-xl"
                    onClick={() => {
                      setShowRejectModal(false);
                      setSelectedRequestForReject(null);
                      setRejectReason("");
                    }}
                    disabled={processingReservationId === selectedRequestForReject?.reservation_id}
                  >
                    {t("common:giveUp")}
                  </Button>
                  <Button
                    className="flex-1 bg-red-600 hover:bg-red-700 rounded-xl"
                    onClick={handleRejectRequest}
                    disabled={!rejectReason.trim() || processingReservationId === selectedRequestForReject?.reservation_id}
                    data-testid="confirm-reject-btn"
                  >
                    {processingReservationId === selectedRequestForReject?.reservation_id && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    {processingReservationId === selectedRequestForReject?.reservation_id ? t("reservations:rejecting") : t('admin:reject')}
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* REJECT CHANGE REQUEST MODAL - Değişiklik Talebi Reddi */}
      <Dialog open={showRejectChangeModal} onOpenChange={(open) => {
        setShowRejectChangeModal(open);
        if (!open) {
          setSelectedChangeForReject(null);
          setRejectChangeReason("");
        }
      }}>
        <DialogContent className="md:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <X className="w-5 h-5" />
              {t("reservations:rejectChangeRequest")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {selectedChangeForReject && (
              <>
                {/* Talep Bilgileri */}
                <div className="p-3 bg-slate-50 rounded-xl space-y-1">
                  <p className="text-sm"><span className="text-grey">{t("common:memberLabel")}</span> {selectedChangeForReject.member_name || t("common:member")}</p>
                  <p className="text-sm"><span className="text-grey">{t("reservations:current")}</span> {selectedChangeForReject.original_date} {selectedChangeForReject.original_start_time}</p>
                  <p className="text-sm"><span className="text-grey">{t("reservations:requested")}</span> {selectedChangeForReject.new_date} {selectedChangeForReject.new_start_time}</p>
                  {selectedChangeForReject.reason && (
                    <p className="text-sm italic text-grey">&ldquo;{selectedChangeForReject.reason}&rdquo;</p>
                  )}
                </div>

                {/* Red Nedeni */}
                <div>
                  <Label className="text-red-600">Red Nedeni *</Label>
                  <Textarea
                    value={rejectChangeReason}
                    onChange={(e) => setRejectChangeReason(e.target.value)}
                    placeholder={t("admin:ph_lutfenRedNedeniniAciklayin")}
                    className="rounded-xl mt-1"
                    rows={3}
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className="flex-1 rounded-xl"
                    onClick={() => {
                      setShowRejectChangeModal(false);
                      setSelectedChangeForReject(null);
                      setRejectChangeReason("");
                    }}
                    disabled={processingReservationId === selectedChangeForReject?.change_id}
                  >
                    Vazgeç
                  </Button>
                  <Button
                    className="flex-1 bg-red-600 hover:bg-red-700 rounded-xl"
                    onClick={handleRejectChangeRequest}
                    disabled={!rejectChangeReason.trim() || processingReservationId === selectedChangeForReject?.change_id}
                  >
                    {processingReservationId === selectedChangeForReject?.change_id && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    {processingReservationId === selectedChangeForReject?.change_id ? "Reddediliyor..." : t('admin:reject')}
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* EDIT CHANGE REQUEST MODAL - Değişiklik Talebini Düzenle */}
      <Dialog open={showEditChangeModal} onOpenChange={(open) => {
        setShowEditChangeModal(open);
        if (!open) {
          setEditingChangeRequest(null);
          setEditChangeForm({ new_date: "", new_start_time: "", new_end_time: "", trainer_id: "", room_id: "", reason: "" });
          setEditChangeRoomAvailability(null);
          setEditChangeTrainerAvailability(null);
        }
      }}>
        <DialogContent className="md:w-[95vw] md:max-w-4xl md:max-h-[90vh] overflow-y-auto p-4 md:p-6">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-navy">
              <Edit className="w-5 h-5" />
              Değişiklik Talebini Düzenle
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {editingChangeRequest && (
              <>
                {/* Mevcut Bilgiler */}
                <div className="p-3 bg-slate-50 rounded-xl space-y-1 text-sm">
                  <p><span className="text-grey">{t("common:memberLabel")}</span> {editingChangeRequest.member_name || "Üye"}</p>
                  <p><span className="text-grey">Mevcut:</span> {editingChangeRequest.original_date} {editingChangeRequest.original_start_time}</p>
                  <p><span className="text-grey">Talep Edilen:</span> {editingChangeRequest.new_date} {editingChangeRequest.new_start_time}</p>
                </div>

                {/* Yeni Tarih */}
                <div>
                  <Label>Yeni Tarih</Label>
                  <DatePickerTR
                    value={editChangeForm.new_date}
                    onChange={(date) => setEditChangeForm(prev => ({ ...prev, new_date: date }))}
                    returnDateString={true}
                    minDate={new Date()}
                    placeholder={t('common:selectDate')}
                  />
                </div>

                {/* Yeni Saat */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{t('settings:startTime')}</Label>
                    <TimePicker
                      value={editChangeForm.new_start_time}
                      onChange={(time) => {
                        const start = time;
                        // Otomatik bitiş hesapla (branşa göre)
                        const branchDurations = { "EMS": 30, "Pilates": 60, "Yoga": 60, "Fitness": 60 };
                        const branch = editingChangeRequest?.branch || "";
                        const duration = branchDurations[branch] || 60;
                        const [h, m] = start.split(':').map(Number);
                        const endMins = h * 60 + m + duration;
                        const endH = Math.floor(endMins / 60);
                        const endM = endMins % 60;
                        const endTime = `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;
                        setEditChangeForm(prev => ({ ...prev, new_start_time: start, new_end_time: endTime }));
                      }}
                      minHour={8}
                      maxHour={22}
                      minuteStep={5}
                    />
                  </div>
                  <div>
                    <Label>Bitiş (Otomatik)</Label>
                    <div className="h-10 px-3 py-2 bg-slate-100 border border-input rounded-xl flex items-center text-sm">
                      {editChangeForm.new_end_time || "--:--"}
                    </div>
                  </div>
                </div>

                {/* Antrenör */}
                <div>
                  <Label>{t('common:trainer')}</Label>
                  <Select value={editChangeForm.trainer_id} onValueChange={(v) => setEditChangeForm(prev => ({ ...prev, trainer_id: v }))}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder={t("common:selectTrainer")} />
                    </SelectTrigger>
                    <SelectContent>
                      {trainers.filter(t => t.is_active || t.status === "active").map((t) => (
                        <SelectItem key={t.trainer_id} value={t.trainer_id}>
                          {t.name} {t.surname}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Oda */}
                <div>
                  <Label>{t('common:room')}</Label>
                  <Select value={editChangeForm.room_id} onValueChange={(v) => setEditChangeForm(prev => ({ ...prev, room_id: v }))}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder={t("common:ph_odaSecin")} />
                    </SelectTrigger>
                    <SelectContent>
                      {rooms.filter(r => r.is_active).map((r) => {
                        const editBranch = editingChangeRequest?.branch;
                        const branchCapacity = (editBranch && r.branch_capacities && r.branch_capacities[editBranch] !== undefined)
                          ? r.branch_capacities[editBranch]
                          : r.capacity;
                        return (
                          <SelectItem key={r.room_id} value={r.room_id}>
                            {r.name} ({t('common:capacity')}: {branchCapacity})
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>

                {/* Neden */}
                <div>
                  <Label>{t("reservations:changeReasonLabel")}</Label>
                  <Textarea
                    value={editChangeForm.reason}
                    onChange={(e) => setEditChangeForm(prev => ({ ...prev, reason: e.target.value }))}
                    placeholder={t("common:ph_nedenDegisiklikYaptiginiziAciklayin")}
                    className="rounded-xl"
                    rows={2}
                  />
                </div>

                {/* Müsaitlik Durumu */}
                {editChangeForm.new_start_time && editChangeForm.new_date && (editChangeForm.room_id || editChangeForm.trainer_id) && (
                  <div className="p-3 bg-slate-50 rounded-xl">
                    <p className="font-medium text-navy text-sm mb-2">{t("common:availabilityStatus")}</p>
                    <div className="grid grid-cols-1 gap-2">
                      {/* ODA MÜSAİTLİĞİ - MK formatı */}
                      {editChangeForm.room_id && editChangeRoomAvailability && (
                        <div className={`flex items-center gap-2 text-sm ${
                          editChangeRoomAvailability.is_locked 
                            ? 'text-red-600' 
                            : (editChangeRoomAvailability.mk_available ?? editChangeRoomAvailability.available_capacity) > 0 
                              ? 'text-green-600' 
                              : 'text-red-600'
                        }`}>
                          {editChangeRoomAvailability.is_locked || (editChangeRoomAvailability.mk_available ?? editChangeRoomAvailability.available_capacity) === 0 
                            ? <AlertCircle className="w-4 h-4" /> 
                            : <CheckCircle2 className="w-4 h-4" />}
                          <span>
                            Oda: {editChangeRoomAvailability.is_locked 
                              ? t("common:roomLocked") 
                              : (editChangeRoomAvailability.mk_available ?? editChangeRoomAvailability.available_capacity) > 0 
                                ? `${t("common:trainerAvailable")} (${editChangeRoomAvailability.mk_available ?? editChangeRoomAvailability.available_capacity} ${t("common:slotsAvailable")})`
                                : t("common:roomNotAvailable")}
                          </span>
                        </div>
                      )}
                      {editChangeForm.room_id && !editChangeRoomAvailability && (
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Clock className="w-4 h-4 animate-spin" />
                          <span>{t("common:roomLabel")} {t('qr:checking')}</span>
                        </div>
                      )}
                      
                      {/* ANTRENÖR MÜSAİTLİĞİ - ÖA formatı */}
                      {editChangeForm.trainer_id && editChangeTrainerAvailability && (
                        <div className={`flex items-center gap-2 text-sm ${editChangeTrainerAvailability.is_available ? 'text-green-600' : 'text-red-600'}`}>
                          {editChangeTrainerAvailability.is_available ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                          <span>
                            Antrenör: {editChangeTrainerAvailability.is_available 
                              ? (() => {
                                  const info = editChangeTrainerAvailability.training_type_info;
                                  if (info && info.max_participants > 1) {
                                    return `${t("common:trainerAvailable")} (${info.type} - ${info.remaining ?? (info.max_participants - info.current_count)} ${t("common:slotsAvailable")})`;
                                  }
                                  return t("common:trainerAvailable");
                                })()
                              : (editChangeTrainerAvailability.reason || t('reservations:notAvailable'))}
                          </span>
                        </div>
                      )}
                      {editChangeForm.trainer_id && !editChangeTrainerAvailability && (
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Clock className="w-4 h-4 animate-spin" />
                          <span>{t("common:trainerLabel")} {t('qr:checking')}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className="flex-1 rounded-xl"
                    onClick={() => {
                      setShowEditChangeModal(false);
                      setEditingChangeRequest(null);
                      setEditChangeRoomAvailability(null);
                      setEditChangeTrainerAvailability(null);
                    }}
                    disabled={submittingEditChange}
                  >
                    Vazgeç
                  </Button>
                  <Button
                    className="flex-1 bg-navy hover:bg-navy/90 rounded-xl"
                    onClick={handleEditChangeSubmit}
                    disabled={
                      submittingEditChange ||
                      !editChangeForm.new_date || 
                      !editChangeForm.new_start_time ||
                      (editChangeTrainerAvailability && !editChangeTrainerAvailability.is_available) ||
                      (editChangeRoomAvailability && editChangeRoomAvailability.is_locked)
                    }
                  >
                    {submittingEditChange && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    {submittingEditChange ? "Gönderiliyor..." : t("common:sendToMember")}
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* View Reservation Modal */}
      <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
        <DialogContent className="md:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-navy" />
              {t("reservations:reservationDetails")}
            </DialogTitle>
          </DialogHeader>
          {viewingReservation && (
            <div className="space-y-4 mt-4">
              <div className="p-4 bg-slate-50 rounded-xl space-y-3">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-grey">{t("common:member")}</p>
                    <p className="font-semibold text-navy">
                      {viewingReservation.member_name || `${members.find(m => m.member_id === viewingReservation.member_id)?.name || ""} ${members.find(m => m.member_id === viewingReservation.member_id)?.surname || ""}`.trim() || t('common:member')}
                    </p>
                    {viewingReservation.package_request && (
                      <Badge className="mt-1 bg-orange-100 text-orange-700 border border-orange-300 text-xs" data-testid="view-package-request-badge">
                        Paket yok – paket satışı gerekli
                      </Badge>
                    )}
                  </div>
                  {getStatusBadge(viewingReservation.status, viewingReservation)}
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-grey">{t('common:date')}</p>
                    <p className="font-medium">{viewingReservation.date ? format(new Date(viewingReservation.date), "dd.MM.yyyy EEEE", { locale: getDateLocale() }) : "-"}</p>
                  </div>
                  <div>
                    <p className="text-grey">{t('common:time')}</p>
                    <p className="font-medium">{viewingReservation.start_time || "-"} - {viewingReservation.end_time || "-"}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-grey">{t("common:branch")}</p>
                    <p className="font-medium">{getLocalizedBranch(viewingReservation.branch) || "-"}</p>
                  </div>
                  <div>
                    <p className="text-grey">{t('common:trainer')}</p>
                    <p className="font-medium">{viewingReservation.trainer_name || trainers.find(t => t.trainer_id === viewingReservation.trainer_id)?.name || "-"}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-grey">{t('common:room')}</p>
                    <p className="font-medium">{viewingReservation.room_name || rooms.find(r => r.room_id === viewingReservation.room_id)?.name || "-"}</p>
                  </div>
                  <div>
                    <p className="text-grey">{t("reservations:requestType")}</p>
                    <p className="font-medium">
                      {viewingReservation.is_demo || viewingReservation.payment_type === "demo" 
                        ? "Demo" 
                        : viewingReservation.payment_type === "card" 
                          ? `${t("memberDashboard:card")}${viewingReservation.card_name ? ` (${viewingReservation.card_name})` : ""}`
                          : `${t("reservations:package")}${viewingReservation.package_name ? ` (${viewingReservation.package_name})` : ""}`}
                    </p>
                  </div>
                </div>

                {viewingReservation.notes && (
                  <div className="text-sm pt-2 border-t border-slate-200">
                    <p className="text-grey">{t('common:note')}</p>
                    <p className="text-navy italic">&ldquo;{viewingReservation.notes}&rdquo;</p>
                  </div>
                )}

                {viewingReservation.previous_values && (
                  <div className="text-sm pt-2 border-t border-slate-200">
                    <p className="text-grey mb-1">{t("reservations:previousValues")}</p>
                    <p className="text-grey-600 line-through">
                      {viewingReservation.previous_values.date ? format(new Date(viewingReservation.previous_values.date), "dd.MM.yyyy", { locale: getDateLocale() }) : ""} {viewingReservation.previous_values.start_time || ""}
                      {viewingReservation.previous_values.trainer_name && ` • ${viewingReservation.previous_values.trainer_name}`}
                    </p>
                  </div>
                )}

                {viewingReservation.change_reason && (
                  <div className="text-sm pt-2 border-t border-slate-200">
                    <p className="text-grey">{t("reservations:changeReason")}</p>
                    <p className="text-purple-700 italic">&ldquo;{viewingReservation.change_reason}&rdquo;</p>
                  </div>
                )}

                {["cancelled", "cancelled_late", "burned"].includes(viewingReservation.status) && (
                  <div className="text-sm pt-2 border-t border-slate-200" data-testid="view-cancel-reason">
                    <p className="text-grey">{t("members:cancellationReason", "İptal Nedeni")}</p>
                    <p className="text-red-600 italic">
                      {viewingReservation.cancel_reason
                        ? `“${viewingReservation.cancel_reason}”`
                        : t("common:notSpecified", "Belirtilmedi")}
                    </p>
                  </div>
                )}
              </div>
              
              {canEdit && ["approved", "confirmed", "pending", "pending_member_approval", "pending_business_approval"].includes(viewingReservation.status) && viewingReservation.member_id && (
                <Button variant="outline" className="w-full rounded-xl text-indigo-700 border-indigo-200 hover:bg-indigo-50" onClick={() => { setShowViewModal(false); setSeriesSource(viewingReservation); }} data-testid="view-series-btn">
                  <Layers className="w-4 h-4 mr-2" /> Seriyi Yönet
                </Button>
              )}
              {canRepeat(viewingReservation) && (
                <Button variant="outline" className="w-full rounded-xl text-purple-700 border-purple-200 hover:bg-purple-50" onClick={() => { setShowViewModal(false); setRepeatSource(viewingReservation); }} data-testid="view-repeat-btn">
                  <RefreshCw className="w-4 h-4 mr-2" /> Tekrarla
                </Button>
              )}
              <Button
                variant="outline"
                className="w-full rounded-xl"
                onClick={() => setShowViewModal(false)}
              >
                {t("common:close")}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {repeatSource && (
        <RepeatReservationDialog
          reservation={repeatSource}
          member={(() => { const m = members.find((x) => x.member_id === repeatSource.member_id); return m && { ...m, recurring_template: savedTemplates[m.member_id] !== undefined ? savedTemplates[m.member_id] : m.recurring_template }; })()}
          businessId={businessId}
          defaultStatus={statusForMember(members.find((x) => x.member_id === repeatSource.member_id))}
          onClose={() => setRepeatSource(null)}
          onDone={() => { setRepeatSource(null); refreshAfterBulk(); }}
        />
      )}
      {seriesSource && (
        <SeriesManageDialog
          reservation={seriesSource}
          businessId={businessId}
          trainers={trainers.filter((t) => t.is_active !== false && (!seriesSource.branch || (t.branches || []).includes(seriesSource.branch)))}
          rooms={rooms.filter((rm) => rm.is_active !== false && (!seriesSource.branch || (rm.branches || []).includes(seriesSource.branch)))}
          onClose={() => setSeriesSource(null)}
          onDone={() => { setSeriesSource(null); refreshAfterBulk(); }}
        />
      )}
      {showBulkPlan && (
        <BulkPlanDialog
          businessId={businessId}
          members={members}
          statusFor={statusForMember}
          onClose={() => setShowBulkPlan(false)}
          onDone={() => { setShowBulkPlan(false); refreshAfterBulk(); }}
        />
      )}

      {/* Lesson Note Modal - Tamamlarken ve sonradan düzenleme için */}
      <Dialog open={showLessonNoteModal} onOpenChange={(o) => { if (!o) setShowLessonNoteModal(false); }}>
        <DialogContent className="max-w-lg" data-testid="lesson-note-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-amber-600" />
              {lessonNoteMode === "complete"
                ? t("reservations:completeLessonWithNote", "Dersi Tamamla ve Açıklama Ekle")
                : (lessonNoteTarget?.lesson_note
                    ? t("reservations:editLessonNote", "Ders Açıklamasını Düzenle")
                    : t("reservations:addLessonNote", "Ders Açıklaması Ekle"))}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {lessonNoteTarget && (
              <div className="text-xs text-grey bg-slate-50 rounded-lg p-3 space-y-1">
                <div><strong>{t("common:member")}:</strong> {lessonNoteTarget.member_name || "-"}</div>
                <div><strong>{t("common:date")}:</strong> {lessonNoteTarget.date} • {lessonNoteTarget.start_time}{lessonNoteTarget.end_time ? `-${lessonNoteTarget.end_time}` : ""}</div>
              </div>
            )}
            <div>
              <Label className="text-sm font-medium">
                {t("reservations:lessonNote", "Ders Açıklaması")}
                <span className="text-grey ml-1 font-normal">({t("common:optional", "isteğe bağlı")})</span>
              </Label>
              <Textarea
                value={lessonNoteText}
                onChange={(e) => setLessonNoteText(e.target.value.slice(0, 1000))}
                placeholder={t("reservations:lessonNotePlaceholder", "Bu derste yapılanlar, üye performansı, dikkat edilmesi gerekenler... (örn. 'Sırt ağrısı oldu, dinlendirildi')")}
                rows={5}
                className="rounded-xl mt-1"
                data-testid="lesson-note-textarea"
              />
              <p className="text-[11px] text-grey text-right mt-1">{(lessonNoteText || "").length}/1000</p>
            </div>
            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                className="flex-1 rounded-xl"
                onClick={() => setShowLessonNoteModal(false)}
                disabled={savingLessonNote}
                data-testid="lesson-note-cancel"
              >
                {lessonNoteMode === "complete" ? t("common:cancel") : t("common:close")}
              </Button>
              <Button
                className="flex-1 rounded-xl bg-amber-600 hover:bg-amber-700 text-white"
                onClick={submitLessonNoteModal}
                disabled={savingLessonNote}
                data-testid="lesson-note-save"
              >
                {savingLessonNote ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  lessonNoteMode === "complete"
                    ? t("reservations:completeAndSave", "Tamamla ve Kaydet")
                    : t("common:save")
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
